import React, { memo, useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from '@rebass/grid';
import { getThemeProp, MatchMedia, ZINDEX, media, isMatchedMaxWidth, darken } from '@amtrav/themes';
import { Heading2, skeletonLoadingStyles, AirportCode, Tooltip, MetricBox, Tabs as Tabs$1, Button, Modal, Notice, ActivityIndicator, Carousel, ButtonTextStyled, ScrollArea, Badge, PopoverMenu, AirlineLogo, EmptyState, FullPageLoader, List, Card, Dialog, Heading4, PreferredMark, HorizontalScroll, reshapeCustomLocation, DropdownSelect, DatePicker, TextInput, DateRangePicker, PassengersFilterChip, ErrorMessage, CityAutocomplete } from '@amtrav/view-components';
import { randomString, formatDate } from '@amtrav/utils';
import { parseDate, differenceInMinutes, dayOfWeekShortFormatter, monthShortFormatter, dayOfMonthFormatter, weekdayMonthDayYear, hourMinuteMeridianCondensed, isoExtendedFormat } from '@amtrav/dates';
import styled, { css, ThemeContext, createGlobalStyle } from 'styled-components';
import { getTokenValue, THEME_TOKENS, SPACE } from '@amtrav/theme-system';
import { IconClock, IconTrain, IconPlane, IconLiveTV, IconPersonalVideo, IconStreamingVideo, IconUSB, IconPower, IconAngledFlatSeat, IconLieFlatSeat, IconLieFlatSuite, IconLieFlatPod, IconMiddleSeatFree, IconWiFi, IconWiFiFast, IconPrevious, IconNext, IconCamera, IconLuggage, IconWarningTriangle, IconWarning, IconCheckCircleLine, IconBolt, IconEco, IconCoronavirus, IconEarth, IconCaretUp, IconCaretDown, IconLine, IconCheckCircle, IconOpenNew } from '@amtrav/icons';
import tinycolor from 'tinycolor2';
import { FlightLegDisplay, SelectedLegInfo, SeatMap } from '@amtrav/seatmaps';
import { animated, useSpring } from 'react-spring';
import { getJsr, encodeObjToJsrString } from '@amtrav/jsr';
import { cancellableFindLocations } from '@amtrav/webservice';

const TRIP_TYPE = {
  /**
   * One Way.
   */
  oneWay: 'OW',
  /**
   * Round trip.
   */
  roundTrip: 'RT',
  /**
   * Multi-city.
   */
  multiCity: 'MC'
};
const SEAT_ASSIGNMENT_CODES = {
  0: 'No Info Available',
  1: 'Not Offered',
  2: 'Fee Applies',
  3: 'FREE',
  4: 'FREE (Fee for Premium)',
  5: 'FREE (Premium)',
  6: 'Open Seating'
};
const SEAT_SIZE = {
  suite: 'S',
  fullFlat: 'F',
  angledFlat: 'A',
  flatPod: 'P'
};
const FRIENDLY_WIFI_LABEL = {
  Yes: 'WiFi Available',
  No: 'No WiFi',
  Fast: 'Fast Wifi Available',
  Maybe: 'Wifi (maybe)'
};
const FRIENDLY_IFE_LABEL = {
  PersonalVideoOnDemand: 'Personal On Demand Video',
  PersonalVideoOnDemandOrStreaming: 'Personal Video or Streaming Entertainment',
  MaybePersonalVideoOnDemand: 'Personal On Demand Video (maybe)',
  Streaming: 'Streaming Video',
  MaybeStreaming: 'Streaming Video (maybe)',
  PersonalVideo: 'Personal Video',
  PersonalVideoOrStreaming: 'Personal Video or Streaming Entertainment',
  MaybePersonalVideo: 'Personal Video (maybe)',
  Tablet: 'Tablet Video',
  MaybeTablet: 'Tablet Video (maybe)',
  Overhead: 'Overhead Screen',
  MaybeOverhead: 'Overhead Screen (maybe)',
  None: 'No In-flight Entertainment'
};
const FRIENDLY_LIVE_TV_LABEL = {
  Yes: 'Live TV Available',
  Maybe: 'Live TV (maybe)',
  No: 'No Live TV'
};
const FRIENDLY_POWER_LABEL = {
  Yes: 'Power Available',
  YesWithUSB: 'Power and USB',
  WithAdapter: 'Power with Adapter',
  USBOnly: 'USB Only',
  MaybeUSB: 'USB (maybe)',
  Maybe: 'Power (maybe)',
  No: 'No Power'
};

/* eslint-disable no-param-reassign */

/**
 * Maps extraColumns to a multidimensional array. This is required to sort multiple fares that belong
 * to the same column to the proper array.
 *
 * @param {Array} columnBrands An columnBrands array of objects that have an `extraColumns` property
 */
const getBrandColumnMap = columnBrands => {
  const brandMap = {};
  let i = 0;
  columnBrands.forEach((cb, idx) => {
    brandMap[i] = idx;
    i += 1;
    let {
      extraColumns
    } = cb;
    while (extraColumns > 0) {
      brandMap[i] = idx;
      i += 1;
      extraColumns -= 1;
    }
  });
  return brandMap;
};
const stopDetails = (segments, segIdx, totalTripDuration) => {
  if (segments.length === 1 || segIdx === 0) {
    return null;
  }
  const currSegment = segments[segIdx];
  const prevSegment = segments[segIdx - 1];
  const stopsDuration = differenceInMinutes(parseDate(currSegment.departs), parseDate(prevSegment.arrives));
  return {
    duration: stopsDuration,
    relativeDuration: stopsDuration / totalTripDuration * 100
  };
};
const relativeDuration = (segments, segIdx, totalTripDuration) => {
  return segments[segIdx].duration / totalTripDuration * 100;
};
const generateFareKey = (fare, legNum) => {
  return fare.itineraryFlights ? `${fare.itineraryFlights[legNum - 1]}|${fare.flightBrand.brandName}|${fare.totalFare}` : null;
};
const generateResultId = flights => flights.reduce((acc, f) => `${acc}${f.tag}`, '');
const getLowestFare = flights => {
  if (flights !== null && flights !== void 0 && flights.length) {
    const lowFare = flights.map(flight => flight.fares.filter(f => !f.soldOut).reduce((prev, curr) => curr.totalItinerary < prev.totalItinerary ? curr : prev)).reduce((prev, curr) => curr.totalItinerary < prev.totalItinerary ? curr : prev);
    return lowFare.totalItinerary;
  }
  return null;
};
const processFlightsData = _ref => {
  let {
    flights,
    columnBrands,
    flightBrands,
    previouslySelectedFlight,
    additionalOutOfPolicyText
  } = _ref;
  const brandColumnMap = getBrandColumnMap(columnBrands);
  const fastestDuration = flights.reduce((prev, current) => prev.duration <= current.duration ? prev : current, 0).duration;
  const fastestDurationPlus15 = fastestDuration + 15;
  return flights.map((flight, initialIndex) => {
    const structuredFares = Array.from({
      length: columnBrands.length
    }, () => []);
    const legPrices = [];
    const totalPrices = [];

    // takes into account totalItinerary and residualCredit (needed to sort fares for change trips)
    const netPrices = [];
    const durationOfLongestSegment = flight.segments.reduce((prev, current) => prev.duration >= current.duration ? prev : current, 0).duration;
    const segments = flight.segments.map((segment, segIdx) => {
      return {
        ...segment,
        departsDate: parseDate(segment.departs),
        arrivesDate: parseDate(segment.arrives),
        relativeDuration: relativeDuration(flight.segments, segIdx, flight.duration),
        stopDetails: stopDetails(flight.segments, segIdx, flight.duration),
        isLongestSegment: segment.duration === durationOfLongestSegment,
        isDepartingArrivalMismatch: !!(segIdx === 0 && previouslySelectedFlight && (previouslySelectedFlight === null || previouslySelectedFlight === void 0 ? void 0 : previouslySelectedFlight.toCode) !== segment.fromCode)
      };
    });
    flight.fareIds = [];
    // eslint-disable-next-line max-len
    flight.isFastest = flight.duration === fastestDuration || flight.duration <= fastestDurationPlus15 && flight.numStops === 0;
    flight.fares.forEach((fare, i) => {
      const nextFare = i + 1 in flight.fares ? flight.fares[i + 1] : null;
      const fareId = `fa-${flight.tag}_${randomString()}`;
      flight.fareIds.push(fareId);
      fare.nextFare = nextFare;
      fare.flightBrand = flightBrands[fare.brandIndex];
      fare.flightTag = flight.tag;
      fare.fareId = fareId;
      fare.additionalOutOfPolicyText = additionalOutOfPolicyText;
      structuredFares[brandColumnMap[fare.brandColumn]].push(fare);
      // We use this to sort by available price. 0 means sold out, so we don't include it.
      if (fare.totalFare > 0) {
        legPrices.push(fare.totalFare);
      }
      totalPrices.push(fare.totalItinerary);
      netPrices.push(fare.totalItinerary - fare.residualCredit);
    });
    flight.uniqueId = `fl-${flight.tag}_${randomString()}`;
    return {
      ...flight,
      segments,
      structuredFares,
      initialOrder: initialIndex,
      lowestItineraryPrice: Math.min(...totalPrices),
      lowestLegPrice: Math.min(...legPrices),
      lowestNetCost: Math.min(...netPrices)
    };
  });
};

/**
 * Determine trip type based on legs.
 * @param {Array} legsArr
 * @returns TRIP_TYPE
 */
const getTripType = legsArr => {
  if (legsArr.length === 1) {
    return TRIP_TYPE.oneWay;
  }
  if (legsArr.length === 2 && legsArr[0].fromLocId === legsArr[1].toLocId && legsArr[0].toLocId === legsArr[1].fromLocId) {
    return TRIP_TYPE.roundTrip;
  }
  return TRIP_TYPE.multiCity;
};

/**
 * Returns location summary based on legs.
 * @param {Array} legsArr
 * @param {Array} locations
 */
const getTripRouteSummary = (legsArr, locations) => {
  const tripType = getTripType(legsArr);
  if (tripType !== TRIP_TYPE.multiCity) {
    var _locations$filter, _locations$filter2;
    const fromCity = (locations === null || locations === void 0 || (_locations$filter = locations.filter(e => {
      var _legsArr$;
      return e.locId === (legsArr === null || legsArr === void 0 || (_legsArr$ = legsArr[0]) === null || _legsArr$ === void 0 ? void 0 : _legsArr$.fromLocId);
    })) === null || _locations$filter === void 0 || (_locations$filter = _locations$filter[0]) === null || _locations$filter === void 0 ? void 0 : _locations$filter.shortDisplay) || '';
    const toCity = (locations === null || locations === void 0 || (_locations$filter2 = locations.filter(e => {
      var _legsArr$2;
      return e.locId === (legsArr === null || legsArr === void 0 || (_legsArr$2 = legsArr[0]) === null || _legsArr$2 === void 0 ? void 0 : _legsArr$2.toLocId);
    })) === null || _locations$filter2 === void 0 || (_locations$filter2 = _locations$filter2[0]) === null || _locations$filter2 === void 0 ? void 0 : _locations$filter2.shortDisplay) || '';
    return `${fromCity} to ${toCity}`;
  }
  return 'Multi City';
};

/**
 * Convert a duration (in minutes) to a friendly format 132 --> '2H 12M'
 * @param {number} duration
 */
const convertDurationToFriendlyString = duration => {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  return `${hours > 0 ? `${hours}h ` : ''}${minutes}m`;
};

/**
 * Returns date summary based on legs.
 * @param {*} legsArr
 */
const getTripDateSummary = legsArr => {
  var _legsArr$4, _legsArr;
  const tripType = getTripType(legsArr);
  const formatDate = dateStr => {
    const date = parseDate(dateStr);
    return `${dayOfWeekShortFormatter(date)} ${monthShortFormatter(date)} ${dayOfMonthFormatter(date)} `;
  };
  if (tripType === TRIP_TYPE.oneWay) {
    var _legsArr$3;
    const date = formatDate((legsArr === null || legsArr === void 0 || (_legsArr$3 = legsArr[0]) === null || _legsArr$3 === void 0 ? void 0 : _legsArr$3.date) || '');
    return `${date}`;
  }
  const fromDate = formatDate((legsArr === null || legsArr === void 0 || (_legsArr$4 = legsArr[0]) === null || _legsArr$4 === void 0 ? void 0 : _legsArr$4.date) || '');
  const toDate = formatDate((legsArr === null || legsArr === void 0 || (_legsArr = legsArr[legsArr.length - 1]) === null || _legsArr === void 0 ? void 0 : _legsArr.date) || '');
  return `${fromDate} — ${toDate}`;
};

/**
 * Returns date summary object based on legs.
 * @param {*} legsArr
 */
const getTripDateSummaryObj = legsArr => {
  var _legsArr$6, _legsArr2;
  const tripType = getTripType(legsArr);
  const formatDate = dateStr => {
    const date = parseDate(dateStr);
    return `${dayOfWeekShortFormatter(date)} ${monthShortFormatter(date)} ${dayOfMonthFormatter(date)} `;
  };
  if (tripType === TRIP_TYPE.oneWay) {
    var _legsArr$5;
    const date = formatDate((legsArr === null || legsArr === void 0 || (_legsArr$5 = legsArr[0]) === null || _legsArr$5 === void 0 ? void 0 : _legsArr$5.date) || '');
    return {
      fromDate: date,
      toDate: null
    };
  }
  const fromDate = formatDate((legsArr === null || legsArr === void 0 || (_legsArr$6 = legsArr[0]) === null || _legsArr$6 === void 0 ? void 0 : _legsArr$6.date) || '');
  const toDate = formatDate((legsArr === null || legsArr === void 0 || (_legsArr2 = legsArr[legsArr.length - 1]) === null || _legsArr2 === void 0 ? void 0 : _legsArr2.date) || '');
  return {
    fromDate,
    toDate
  };
};
const formatFare = function (price) {
  let showSignDisplay = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    signDisplay: showSignDisplay ? 'always' : 'auto',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
  return currencyFormatter.format(parseInt(price, 10));
};

/**
 * @param {string} Airport/Rail Code
 */
const isTrainStation = code => code && code === code.toLowerCase();

/**
 *
 * @param {*} fee
 * @returns {boolean}
 */
const hasCarryOnBagFee = fee => {
  const feeLowercase = fee === null || fee === void 0 ? void 0 : fee.toLowerCase();
  return fee && feeLowercase !== 'free' && feeLowercase !== 'see airline';
};

const DurationRegion$2 = styled(Flex)`
  font-size: 15px;
  white-space: nowrap;
`;
const DurationAmount = styled.div`
  font-weight: bold;
`;
const Duration$1 = _ref => {
  let {
    duration
  } = _ref;
  return /*#__PURE__*/React.createElement(DurationRegion$2, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Flex, {
    pr: 1
  }, /*#__PURE__*/React.createElement(IconClock, null)), /*#__PURE__*/React.createElement(DurationAmount, null, convertDurationToFriendlyString(duration)));
};
Duration$1.propTypes = {
  duration: PropTypes.number.isRequired
};

// TODO use the new dates package.

const FlightTime = styled(Heading2)`
  color: inherit;
`;
const FlightDateMeridiem = styled.span`
  font-size: 14px;
  padding-left: 2px;
`;
const DayChange = styled(Box)`
  font-size: 0.6875rem;
  display: inline-block;
  position: absolute;
  right: -2px;
  bottom: 1px;
  font-weight: bold;
`;
const DayChangeLong = styled(Box)`
  font-size: 0.6875rem;
  position: absolute;
`;
const RouteTimeWrapper$1 = styled.div`
  position: relative;
  ${_ref => {
  let {
    isLoading
  } = _ref;
  return skeletonLoadingStyles(isLoading);
}};
`;
const Time = styled.span`
  color: ${_ref2 => {
  let {
    primary
  } = _ref2;
  return primary ? getTokenValue(THEME_TOKENS.base.color.primaryText) : 'inherit';
}};
`;
const RouteTime = _ref3 => {
  let {
    date,
    showDay,
    isLoading,
    dayChange,
    showDayShort,
    primary
  } = _ref3;
  return /*#__PURE__*/React.createElement(RouteTimeWrapper$1, {
    isLoading: isLoading,
    showDay: showDay
  }, /*#__PURE__*/React.createElement(FlightTime, null, /*#__PURE__*/React.createElement(Time, {
    primary: primary
  }, formatDate(date, 'h:mm')), /*#__PURE__*/React.createElement(FlightDateMeridiem, null, formatDate(date, 'aaa')), showDayShort && /*#__PURE__*/React.createElement(DayChange, {
    pl: 1
  }, dayChange > 0 ? `+${dayChange}` : dayChange)), showDay && /*#__PURE__*/React.createElement(DayChangeLong, {
    pt: 1
  }, formatDate(date, 'EEEE')));
};
RouteTime.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  showDay: PropTypes.bool,
  isLoading: PropTypes.bool,
  showDayShort: PropTypes.bool,
  dayChange: PropTypes.number,
  primary: PropTypes.bool
};
RouteTime.defaultProps = {
  showDay: false,
  showDayShort: false,
  isLoading: false,
  dayChange: 0,
  primary: false
};
var RouteTime$1 = /*#__PURE__*/memo(RouteTime);

/* eslint-disable react/require-default-props */
const BulletSpacer = styled(Box)`
  display: inline;
`;
const TextBullet = _ref => {
  let {
    smallSpacing = false
  } = _ref;
  return /*#__PURE__*/React.createElement(BulletSpacer, {
    px: smallSpacing ? 1 : '4px'
  }, "\xB7");
};
TextBullet.propTypes = {
  smallSpacing: PropTypes.bool
};

const FlightMicroDetails = _ref => {
  let {
    aircraftTypeName,
    miles,
    onTimePercent,
    numStops
  } = _ref;
  const details = [];
  if (numStops > 0) {
    details.push(`${numStops} ${numStops > 1 ? 'stops' : 'stop'}`);
  }
  if (aircraftTypeName) {
    details.push(aircraftTypeName);
  }
  if (miles > 0) {
    details.push(`${miles} miles`);
  }
  if (onTimePercent > 0) {
    details.push(`${onTimePercent}% on time`);
  }
  if (details.length) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, details.map((det, idx) => {
      return (
        /*#__PURE__*/
        // eslint-disable-next-line react/no-array-index-key
        React.createElement(React.Fragment, {
          key: idx
        }, det, idx < details.length - 1 && /*#__PURE__*/React.createElement(TextBullet, null))
      );
    }));
  }
  return null;
};
FlightMicroDetails.propTypes = {
  aircraftTypeName: PropTypes.string,
  miles: PropTypes.number,
  onTimePercent: PropTypes.number,
  numStops: PropTypes.number
};
FlightMicroDetails.defaultProps = {
  aircraftTypeName: '',
  miles: -1,
  onTimePercent: -1,
  numStops: 0
};

const DetailsRegion = styled.div`
  line-height: 1;
`;
const DurationBadge$2 = styled.span`
  display: inline-block;
  background: rgba(0, 0, 0, 0.25);
  padding: 3px 5px;
`;
const DurationRegion$1 = styled(Flex)`
  border-top: 1px solid rgba(255, 255, 255, 0.4);
`;
const Terminal = styled(Box)`
  font-size: 10px;
  text-transform: uppercase;
  color: white;
`;
const AirportName$1 = styled(Box)`
  font-size: 12px;
  opacity: 0.8;
  /* white-space: nowrap; */
  font-weight: 600;
`;
const IconPlaneWrapper = styled(Box)`
  font-size: 30px;
  align-self: flex-start;
  padding-top: 23px;
`;
const RouteTimeWrapper = styled.div`
  height: 43px;
`;
const Details = styled(Box)`
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.15), rgba(255, 255, 255, 0));
  font-size: 12px;
  opacity: 0.8;
  white-space: nowrap;
  margin: 0 -16px;
`;
const BookingCodes = styled(Box)`
  border-top: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(0, 0, 0, 0.15);
  line-height: 1.3;
`;
const BookingCodeTitle = styled.b`
  opacity: 0.75;
  font-size: 11px;
  text-transform: uppercase;
`;
const BookingCode = styled.div`
  max-width: 280px;
  padding-top: 2px;
  font-family: monospace;
  white-space: normal;
  font-weight: bold;
  font-size: 13px;
`;
const SegmentRoute = _ref => {
  let {
    fromCode,
    fromName,
    toCode,
    toName,
    fromTerminal = '',
    toTerminal = '',
    departsDate,
    arrivesDate,
    dayChange
  } = _ref;
  return /*#__PURE__*/React.createElement(Flex, {
    pt: 3,
    alignItems: "center",
    justifyContent: "flex-start"
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(AirportCode, {
    code: fromCode
  }), /*#__PURE__*/React.createElement(AirportName$1, {
    mt: 1
  }, fromName), fromTerminal ? /*#__PURE__*/React.createElement(Terminal, {
    pt: 1,
    pb: 1
  }, "Terminal ", fromTerminal) : /*#__PURE__*/React.createElement(Terminal, {
    pt: 1,
    pb: 1
  }, "\u2014"), /*#__PURE__*/React.createElement(RouteTimeWrapper, null, /*#__PURE__*/React.createElement(RouteTime$1, {
    date: departsDate
  }))), /*#__PURE__*/React.createElement(IconPlaneWrapper, {
    px: 3
  }, isTrainStation(fromCode) ? /*#__PURE__*/React.createElement(IconTrain, null) : /*#__PURE__*/React.createElement(IconPlane, null)), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(AirportCode, {
    code: toCode
  }), /*#__PURE__*/React.createElement(AirportName$1, {
    mt: 1
  }, toName), toTerminal ? /*#__PURE__*/React.createElement(Terminal, {
    pt: 1,
    pb: 1
  }, "Terminal ", toTerminal) : /*#__PURE__*/React.createElement(Terminal, {
    pt: 1,
    pb: 1
  }, "\u2014"), /*#__PURE__*/React.createElement(RouteTimeWrapper, null, /*#__PURE__*/React.createElement(RouteTime$1, {
    date: arrivesDate,
    showDay: dayChange !== 0
  }))));
};
SegmentRoute.propTypes = {
  arrivesDate: PropTypes.instanceOf(Date).isRequired,
  departsDate: PropTypes.instanceOf(Date).isRequired,
  dayChange: PropTypes.number.isRequired,
  fromCode: PropTypes.string.isRequired,
  fromName: PropTypes.string.isRequired,
  toCode: PropTypes.string.isRequired,
  toName: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  fromTerminal: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  toTerminal: PropTypes.string
};
const SegmentDetails = _ref2 => {
  let {
    segment
  } = _ref2;
  const {
    airlineCode,
    aircraftTypeName,
    arrivesDate,
    departsDate,
    dayChange,
    duration,
    fromCode,
    fromName,
    toCode,
    toName,
    fromTerminal,
    toTerminal,
    numStops,
    onTimePercent,
    miles,
    flightNumber,
    bookingCodes,
    airlineName
  } = segment;
  return /*#__PURE__*/React.createElement(DetailsRegion, null, /*#__PURE__*/React.createElement(Details, {
    pb: 2,
    pt: 0,
    px: 3
  }, /*#__PURE__*/React.createElement(FlightMicroDetails, {
    aircraftTypeName: aircraftTypeName,
    miles: miles,
    onTimePercent: onTimePercent,
    numStops: numStops
  })), /*#__PURE__*/React.createElement(SegmentRoute, {
    fromCode: fromCode,
    fromName: fromName,
    fromTerminal: fromTerminal,
    toCode: toCode,
    toName: toName,
    toTerminal: toTerminal,
    departsDate: departsDate,
    arrivesDate: arrivesDate,
    dayChange: dayChange
  }), bookingCodes && /*#__PURE__*/React.createElement(BookingCodes, {
    p: "2"
  }, /*#__PURE__*/React.createElement(BookingCodeTitle, null, "Booking Codes"), /*#__PURE__*/React.createElement(BookingCode, null, bookingCodes)), /*#__PURE__*/React.createElement(DurationRegion$1, {
    pb: 1,
    pt: 2,
    justifyContent: "space-between",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Box, null, isTrainStation(fromCode) ? airlineName : airlineCode, "# ", flightNumber), /*#__PURE__*/React.createElement(DurationBadge$2, null, /*#__PURE__*/React.createElement(Duration$1, {
    duration: duration
  }))));
};
SegmentDetails.propTypes = {
  segment: PropTypes.shape({
    airlineCode: PropTypes.string,
    airlineName: PropTypes.string,
    aircraftTypeName: PropTypes.string,
    arrivesDate: PropTypes.instanceOf(Date),
    departsDate: PropTypes.instanceOf(Date),
    dayChange: PropTypes.number,
    duration: PropTypes.number,
    fromCode: PropTypes.string,
    fromName: PropTypes.string,
    toCode: PropTypes.string,
    toName: PropTypes.string,
    fromTerminal: PropTypes.string,
    toTerminal: PropTypes.string,
    numStops: PropTypes.number,
    onTimePercent: PropTypes.number,
    miles: PropTypes.number,
    flightNumber: PropTypes.number,
    bookingCodes: PropTypes.string
  }).isRequired
};
SegmentDetails.defaultProps = {};

/* eslint-disable react/no-array-index-key */
const TimelineTotalTrip = styled.div`
  background: ${_ref => {
  let {
    isLoading
  } = _ref;
  return isLoading ? 'unset' : 'currentColor';
}};
  width: 100%;
  position: relative;
  display: flex;
  ${_ref2 => {
  let {
    isLoading
  } = _ref2;
  return skeletonLoadingStyles(isLoading);
}};
  box-shadow: ${_ref3 => {
  let {
    isLoading
  } = _ref3;
  return isLoading ? '0px 4px 0px 0px white inset, 0px -4px 0px 0px white inset' : 'unset';
}};
`;
const StopsTooltip = styled.div`
  line-height: 1;
`;
const DurationRegion = styled(Flex)`
  border-top: 1px solid rgba(255, 255, 255, 0.4);
`;
const DurationBadge$1 = styled.span`
  display: inline-block;
  background: rgba(0, 0, 0, 0.25);
  padding: 3px 5px;
`;
const SegWrapper = styled.div``;
const Seg = styled.div`
  height: 17px;
  position: relative;

  ::before {
    position: absolute;
    content: '';
    width: 100%;
    height: 19px;
    top: -2px;
    box-shadow: 0px 7px 0px 0px white inset;
    transition: box-shadow 0.2s linear;
  }

  ::after {
    content: '';
    width: 100%;
    height: 19px;
    position: absolute;
    bottom: -2px;
    box-shadow: 0px -7px 0px 0px white inset;
    transition: box-shadow 0.2s linear;
  }

  &:hover::after {
    box-shadow: 0px -3px 0px 0px white inset;
  }

  &:hover::before {
    box-shadow: 0px 3px 0px 0px white inset;
  }
`;
const Stop = styled(Seg)`
  background: rgba(255, 255, 255, 0.6);

  &:hover {
    background: ${getThemeProp('global.warning--color')};
  }
`;
const Flight = styled(Seg)`
  background: ${getThemeProp('global.textBody--color')};

  &:hover {
    background: ${getThemeProp('global.info--color')};
  }
`;
const AirportName = styled(Box)`
  font-weight: 600;
  font-size: 12px;
  opacity: 0.8;
  white-space: nowrap;
`;
const StopsText$1 = styled.div`
  font-size: 14px;
  position: absolute;
  top: 19px;
  left: 0;
  width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
const Timeline = _ref4 => {
  let {
    segments,
    isLoading
  } = _ref4;
  return /*#__PURE__*/React.createElement(TimelineTotalTrip, {
    isLoading: isLoading
  }, segments.map((seg, idx) => {
    if (!seg.stopDetails) {
      return /*#__PURE__*/React.createElement(SegWrapper, {
        key: idx,
        style: {
          width: `${seg.relativeDuration}%`
        }
      }, /*#__PURE__*/React.createElement(Tooltip, {
        maxWidth: "unset",
        kind: "info",
        placement: "top",
        triggerDisplayProperty: "block",
        content: /*#__PURE__*/React.createElement(SegmentDetails, {
          segment: seg
        })
      }, /*#__PURE__*/React.createElement(Flight, null)));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: idx
    }, /*#__PURE__*/React.createElement(SegWrapper, {
      style: {
        width: `${seg.stopDetails.relativeDuration}%`
      }
    }, /*#__PURE__*/React.createElement(Tooltip, {
      maxWidth: "unset",
      kind: "warning",
      placement: "top",
      triggerDisplayProperty: "block",
      content: /*#__PURE__*/React.createElement(StopsTooltip, null, /*#__PURE__*/React.createElement(Box, {
        py: 2
      }, /*#__PURE__*/React.createElement(AirportCode, {
        code: seg.fromCode
      }), /*#__PURE__*/React.createElement(AirportName, {
        mt: 1
      }, seg.fromName)), /*#__PURE__*/React.createElement(DurationRegion, {
        pb: 1,
        pt: 2,
        justifyContent: "space-between",
        alignItems: "center"
      }, /*#__PURE__*/React.createElement(Box, {
        pr: 3
      }, "Layover"), /*#__PURE__*/React.createElement(DurationBadge$1, null, /*#__PURE__*/React.createElement(Duration$1, {
        duration: seg.stopDetails.duration
      }))))
    }, /*#__PURE__*/React.createElement(Stop
    // eslint-disable-next-line react/no-array-index-key
    , {
      key: idx
    }))), /*#__PURE__*/React.createElement(SegWrapper, {
      style: {
        width: `${seg.relativeDuration}%`
      }
    }, /*#__PURE__*/React.createElement(Tooltip, {
      maxWidth: "unset",
      kind: "info",
      placement: "top",
      triggerDisplayProperty: "block",
      content: /*#__PURE__*/React.createElement(SegmentDetails, {
        segment: seg
      })
    }, /*#__PURE__*/React.createElement(Flight, null))));
  }), /*#__PURE__*/React.createElement(StopsText$1, null, /*#__PURE__*/React.createElement(React.Fragment, null, segments.map((seg, idx) => {
    if (seg.stopDetails) {
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: seg.fromCode
      }, idx !== 1 ? /*#__PURE__*/React.createElement(TextBullet, {
        smallSpacing: true
      }) : null, convertDurationToFriendlyString(seg.stopDetails.duration), " in ", seg.fromCode);
    }
    return null;
  }))));
};
Timeline.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool.isRequired
};
Timeline.defaultProps = {
  segments: [{}]
};

const DurationContainer = styled(Flex)`
  gap: 4px;
  flex-direction: column;

  @media (min-width: 1320px) {
    align-items: flex-end;
    justify-content: space-between;
    flex-direction: row;
  }
`;
const Duration = styled.span`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  white-space: nowrap;
  ${_ref => {
  let {
    isLoading
  } = _ref;
  return skeletonLoadingStyles(isLoading);
}};
`;
const Stops = styled.span`
  font-size: 14px;
  color: ${_ref2 => {
  let {
    isNonstop
  } = _ref2;
  return isNonstop ? getTokenValue(THEME_TOKENS.base.color.success) : getTokenValue(THEME_TOKENS.base.color.primaryText);
}};
  ${_ref3 => {
  let {
    isLoading
  } = _ref3;
  return skeletonLoadingStyles(isLoading);
}};
`;
const StopsTextRegion = styled(Box)`
  position: relative;
`;
const StopsText = styled.div`
  font-size: 0.6875rem;
  position: absolute;
  top: 23px;
  left: 0;
  width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-weight: normal;
`;
const convertToStopsFriendlyString = stops => {
  if (stops === 0) return 'Nonstop';
  return stops + (stops > 1 ? ' Stops' : ' Stop');
};
const DurationStops = _ref4 => {
  let {
    segments,
    duration,
    stops,
    isLoading
  } = _ref4;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.LargeAndLess, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Duration, {
    isLoading: isLoading
  }, /*#__PURE__*/React.createElement("span", null, convertDurationToFriendlyString(duration))), /*#__PURE__*/React.createElement(StopsTextRegion, {
    pt: 1
  }, /*#__PURE__*/React.createElement(Stops, {
    isNonstop: stops === 0,
    isLoading: isLoading,
    stops: stops
  }, /*#__PURE__*/React.createElement("span", null, convertToStopsFriendlyString(stops)), stops === 1 && /*#__PURE__*/React.createElement(StopsText, null, segments.map((seg, idx) => {
    if (seg.stopDetails) {
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: seg.fromCode
      }, idx !== 1 ? /*#__PURE__*/React.createElement(TextBullet, {
        smallSpacing: true
      }) : null, convertDurationToFriendlyString(seg.stopDetails.duration), " in ", seg.fromCode);
    }
    return null;
  })), stops >= 2 && /*#__PURE__*/React.createElement(StopsText, null, segments.map((seg, idx) => {
    if (seg.stopDetails) {
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: seg.fromCode
      }, idx !== 1 ? /*#__PURE__*/React.createElement(TextBullet, {
        smallSpacing: true
      }) : null, seg.fromCode);
    }
    return null;
  })))))), /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, /*#__PURE__*/React.createElement(Box, {
    width: "99.99%"
  }, /*#__PURE__*/React.createElement(DurationContainer, {
    pb: "2px"
  }, /*#__PURE__*/React.createElement(Duration, {
    isLoading: isLoading
  }, /*#__PURE__*/React.createElement("span", null, convertDurationToFriendlyString(duration))), /*#__PURE__*/React.createElement(Stops, {
    isNonstop: stops === 0,
    isLoading: isLoading,
    stops: stops
  }, /*#__PURE__*/React.createElement("span", null, convertToStopsFriendlyString(stops)))), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Timeline, {
    isLoading: isLoading,
    segments: segments
  })))));
};
DurationStops.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({})),
  duration: PropTypes.number,
  stops: PropTypes.number,
  isLoading: PropTypes.bool
};
DurationStops.defaultProps = {
  segments: [{}],
  duration: 0,
  stops: 0,
  isLoading: false
};
var DurationStops$1 = /*#__PURE__*/memo(DurationStops);

const AmenityColor = styled.span`
  padding: 0 1px;
  display: inline-block;
`;
const Size12 = styled(AmenityColor)`
  font-size: 12px;
`;
const PowerSize = styled(Size12)`
  margin-top: 1px;
`;
const Size14 = styled(AmenityColor)`
  font-size: 14px;
`;
const Size16$1 = styled(AmenityColor)`
  padding-top: 1px;
  font-size: 16px;
`;
const StreamingSize = styled(Size16$1)`
  margin-top: 1px;
`;
const PersonalVideo = styled(Size12)`
  margin-top: 1px;
`;
const MaybeContainer = styled.span`
  position: relative;
`;
const MaybeDotIndicator = styled.span`
  background: ${getThemeProp('global.warning--color')};
  position: absolute;
  height: 8px;
  width: 8px;
  border-radius: 50%;
  border: 1px solid white;
  bottom: 0px;
  right: 0px;
  z-index: 1;
`;
const MaybeTitle = styled.b`
  color: ${getThemeProp('global.warning--color')};
`;

// eslint-disable-next-line react/prop-types
const Maybe = _ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(MaybeContainer, null, /*#__PURE__*/React.createElement(MaybeDotIndicator, null), children);
};
var amenityIconByKey = attributeKey => {
  const map = [];
  map.LiveTV = {
    description: 'Live TV',
    icon: /*#__PURE__*/React.createElement(Size14, null, /*#__PURE__*/React.createElement(IconLiveTV, null))
  };
  map.MaybeLiveTV = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Live TV ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(Size14, null, /*#__PURE__*/React.createElement(IconLiveTV, null)))
  };
  map.PersonalVideoOnDemand = {
    description: 'In Seat Video On Demand',
    icon: /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null))
  };
  map.PersonalVideoOnDemandOrStreaming = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Personal Video or Streaming Entertainment"),
    icon: /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null))
  };
  map.MaybePersonalVideoOnDemand = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "In Seat Video On Demand ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null)))
  };
  map.Streaming = {
    description: 'Streaming Video',
    icon: /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null))
  };
  map.MaybeStreaming = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Streaming Video ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null)))
  };
  map.PersonalVideo = {
    description: 'In Seat Video',
    icon: /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null))
  };
  map.PersonalVideoOrStreaming = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Personal Video or Streaming Entertainment"),
    icon: /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null))
  };
  map.MaybePersonalVideo = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "In Seat Video ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(PersonalVideo, null, /*#__PURE__*/React.createElement(IconPersonalVideo, null)))
  };
  map.Tablet = {
    description: 'Handheld Video Player ,',
    icon: /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null))
  };
  map.MaybeTablet = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Handheld Video Player ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null)))
  };
  map.Overhead = {
    description: 'Overhead Movies',
    icon: /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null))
  };
  map.MaybeOverhead = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Overhead Movies ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(StreamingSize, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null))
  };
  map.USB = {
    description: 'USB',
    icon: /*#__PURE__*/React.createElement(Size14, null, /*#__PURE__*/React.createElement(IconUSB, null))
  };
  map.MaybeUSB = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "USB ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(Size14, null, /*#__PURE__*/React.createElement(IconUSB, null)))
  };
  map.PowerAndUSB = {
    description: 'Power & USB',
    icon: /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null))
  };
  map.MaybePowerAndUSB = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Power & USB ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null)))
  };
  map.Power = {
    description: 'Power Port',
    icon: /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null))
  };
  map.MaybePower = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Power Port ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null)))
  };
  map.PowerWithAdapter = {
    description: 'Power with Adapter',
    icon: /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null))
  };
  map.MaybePowerWithAdapter = {
    description: /*#__PURE__*/React.createElement(React.Fragment, null, "Power with Adapter ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)")),
    icon: /*#__PURE__*/React.createElement(Maybe, null, /*#__PURE__*/React.createElement(PowerSize, null, /*#__PURE__*/React.createElement(IconPower, null)))
  };
  return map[attributeKey];
};

// import { Flex, Box } from '@rebass/grid';

const TooltipText$1 = styled.div``;
const IconContainter = styled.span``;
const IconWrapper$5 = styled(IconContainter)`
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
    transition: opacity 0.3s;
  }
`;
const BoxTooltipDescription$3 = styled(Box)`
  font-size: 13px;
  opacity: 0.7;
`;
const Amenity = _ref => {
  let {
    attributeCode,
    text,
    suppressTooltip
  } = _ref;
  const amenityObj = amenityIconByKey(attributeCode);
  if (!amenityObj) return null;
  if (suppressTooltip) {
    return /*#__PURE__*/React.createElement(IconContainter, null, amenityObj.icon);
  }
  const tooltipContent = /*#__PURE__*/React.createElement(TooltipText$1, null, /*#__PURE__*/React.createElement("b", null, amenityObj.description), text && /*#__PURE__*/React.createElement(BoxTooltipDescription$3, {
    pt: 2
  }, text));
  return /*#__PURE__*/React.createElement(Tooltip, {
    placement: "top",
    content: tooltipContent
  }, /*#__PURE__*/React.createElement(IconWrapper$5, null, amenityObj.icon));
};
Amenity.propTypes = {
  attributeCode: PropTypes.string.isRequired,
  text: PropTypes.string,
  suppressTooltip: PropTypes.bool
};
Amenity.defaultProps = {
  text: '',
  suppressTooltip: false
};

const Amenities = _ref => {
  let {
    amenities,
    suppressTooltip,
    maxNumberToShow = -1
  } = _ref;
  if (amenities.attributes) {
    /**
     * we only want fare level amenities, as opposed to flight level amenities (i.e. Wi-Fi),
     * amenityIconByKey allows us to filter out the flight level amenities
     */
    let fareLevelAmenities = amenities.attributes.filter(amenity => {
      return amenityIconByKey(amenity.attribute);
    });
    if (maxNumberToShow !== -1) {
      fareLevelAmenities = fareLevelAmenities.splice(0, maxNumberToShow);
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, fareLevelAmenities.map(amenity => {
      return /*#__PURE__*/React.createElement(Amenity, {
        suppressTooltip: suppressTooltip,
        key: amenity.attribute,
        text: amenity.text,
        attributeCode: amenity.attribute
      });
    }));
  }
  return null;
};
Amenities.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  amenities: PropTypes.shape({
    attributes: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  suppressTooltip: PropTypes.bool,
  /**
   * A value of -1 indicates no max
   */
  maxNumberToShow: PropTypes.number
};
Amenities.defaultProps = {
  suppressTooltip: false
};

const Perk$4 = styled.i`
  color: ${getThemeProp('global.success--color')};
  font-weight: bold;
`;
const Description$4 = styled.div`
  opacity: 0.7;
  font-size: 13px;
  line-height: 1.4;
`;
const SeatDetailsStandard = _ref => {
  let {
    allAisleAccess,
    adjacentSeatBlocked,
    seatText = '',
    seatPitch,
    seatWidth,
    seatConfig,
    compact = false
  } = _ref;
  const SeatDetail = () => /*#__PURE__*/React.createElement(React.Fragment, null, allAisleAccess && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Perk$4, null, "All aisle access.")), adjacentSeatBlocked && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Perk$4, null, "Middle seats blocked.")), seatText && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Description$4, null, seatText)));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    pb: compact ? 2 : 0
  }, /*#__PURE__*/React.createElement("b", null, "Seat Details")), !compact && /*#__PURE__*/React.createElement(SeatDetail, null), /*#__PURE__*/React.createElement(Flex, {
    flex: "0 0 auto"
  }, /*#__PURE__*/React.createElement(MetricBox, {
    mr: 1,
    title: "Legroom",
    value: seatPitch
  }), seatWidth && /*#__PURE__*/React.createElement(MetricBox, {
    ml: 1,
    mr: 1,
    title: "Width",
    value: seatWidth
  }), seatConfig && /*#__PURE__*/React.createElement(MetricBox, {
    ml: 1,
    title: "Layout",
    value: seatConfig
  })));
};
SeatDetailsStandard.propTypes = {
  allAisleAccess: PropTypes.bool.isRequired,
  adjacentSeatBlocked: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/require-default-props
  seatText: PropTypes.string,
  seatPitch: PropTypes.string.isRequired,
  seatWidth: PropTypes.string.isRequired,
  seatConfig: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  compact: PropTypes.bool
};

const Perk$3 = styled.i`
  color: ${getThemeProp('global.success--color')};
  font-weight: bold;
`;
const Description$3 = styled.div`
  opacity: 0.7;
  font-size: 13px;
  line-height: 1.4;
`;
const IconWrapper$4 = styled.div`
  font-size: 48px;
  opacity: 0.5;
  position: relative;

  svg {
    position: absolute;
    bottom: -5px;
  }
`;
const SeatDetailsAngledFlat = _ref => {
  let {
    allAisleAccess,
    adjacentSeatBlocked,
    seatConfig = '',
    seatText = '',
    compact = false
  } = _ref;
  const SeatDetail = () => /*#__PURE__*/React.createElement(React.Fragment, null, allAisleAccess && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Perk$3, null, "All aisle access.")), adjacentSeatBlocked && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Perk$3, null, "Middle seats blocked.")), seatText && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Description$3, null, seatText)));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    pb: compact ? 2 : 0
  }, /*#__PURE__*/React.createElement("b", null, "Angled Flat Seat")), !compact && /*#__PURE__*/React.createElement(SeatDetail, null), /*#__PURE__*/React.createElement(Flex, {
    flex: "0 0 auto"
  }, /*#__PURE__*/React.createElement(MetricBox, {
    mr: 1,
    title: "Angled",
    value: /*#__PURE__*/React.createElement(IconWrapper$4, null, /*#__PURE__*/React.createElement(IconAngledFlatSeat, null))
  }), seatConfig && /*#__PURE__*/React.createElement(MetricBox, {
    ml: 1,
    title: "Layout",
    value: seatConfig
  })));
};
SeatDetailsAngledFlat.propTypes = {
  allAisleAccess: PropTypes.bool.isRequired,
  adjacentSeatBlocked: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/require-default-props
  seatText: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  seatConfig: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  compact: PropTypes.bool
};

const Perk$2 = styled.i`
  color: ${getThemeProp('global.success--color')};
  font-weight: bold;
`;
const Description$2 = styled.div`
  opacity: 0.7;
  font-size: 13px;
  line-height: 1.4;
`;
const IconWrapper$3 = styled.div`
  font-size: 48px;
  opacity: 0.5;
  position: relative;

  svg {
    position: absolute;
    bottom: -5px;
  }
`;
const SeatDetailsFlat = _ref => {
  let {
    allAisleAccess,
    adjacentSeatBlocked,
    seatConfig = '',
    seatText = '',
    compact = false
  } = _ref;
  const SeatDetail = () => /*#__PURE__*/React.createElement(React.Fragment, null, allAisleAccess && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Perk$2, null, "All aisle access.")), adjacentSeatBlocked && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Perk$2, null, "Middle seats blocked.")), seatText && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Description$2, null, seatText)));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    pb: compact ? 2 : 0
  }, /*#__PURE__*/React.createElement("b", null, "Lie Flat Seat")), !compact && /*#__PURE__*/React.createElement(SeatDetail, null), /*#__PURE__*/React.createElement(Flex, {
    flex: "0 0 auto"
  }, /*#__PURE__*/React.createElement(MetricBox, {
    mr: 1,
    title: "Lie Flat",
    value: /*#__PURE__*/React.createElement(IconWrapper$3, null, /*#__PURE__*/React.createElement(IconLieFlatSeat, null))
  }), seatConfig && /*#__PURE__*/React.createElement(MetricBox, {
    ml: 1,
    title: "Layout",
    value: seatConfig
  })));
};
SeatDetailsFlat.propTypes = {
  allAisleAccess: PropTypes.bool.isRequired,
  adjacentSeatBlocked: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/require-default-props
  seatText: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  seatConfig: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  compact: PropTypes.bool
};

const Perk$1 = styled.i`
  color: ${getThemeProp('global.success--color')};
  font-weight: bold;
`;
const Description$1 = styled.div`
  opacity: 0.7;
  font-size: 13px;
  line-height: 1.4;
`;
const IconWrapper$2 = styled.div`
  font-size: 44px;
  opacity: 0.5;
  position: relative;

  svg {
    position: absolute;
    bottom: -5px;
  }
`;
const SeatDetailsSuite = _ref => {
  let {
    allAisleAccess,
    adjacentSeatBlocked,
    seatConfig = '',
    seatText = '',
    compact = false
  } = _ref;
  const SeatDetail = () => /*#__PURE__*/React.createElement(React.Fragment, null, allAisleAccess && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Perk$1, null, "All aisle access.")), adjacentSeatBlocked && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Perk$1, null, "Middle seats blocked.")), seatText && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Description$1, null, seatText)));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    pb: compact ? 2 : 0
  }, /*#__PURE__*/React.createElement("b", null, "Lie Flat Suite")), !compact && /*#__PURE__*/React.createElement(SeatDetail, null), /*#__PURE__*/React.createElement(Flex, {
    flex: "0 0 auto"
  }, /*#__PURE__*/React.createElement(MetricBox, {
    mr: 1,
    title: "Suite",
    value: /*#__PURE__*/React.createElement(IconWrapper$2, null, /*#__PURE__*/React.createElement(IconLieFlatSuite, null))
  }), seatConfig && /*#__PURE__*/React.createElement(MetricBox, {
    ml: 1,
    title: "Layout",
    value: seatConfig
  })));
};
SeatDetailsSuite.propTypes = {
  allAisleAccess: PropTypes.bool.isRequired,
  adjacentSeatBlocked: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/require-default-props
  seatText: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  seatConfig: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  compact: PropTypes.bool
};

const Perk = styled.i`
  color: ${getThemeProp('global.success--color')};
  font-weight: bold;
`;
const Description = styled.div`
  opacity: 0.7;
  font-size: 13px;
  line-height: 1.4;
`;
const IconWrapper$1 = styled.div`
  font-size: 48px;
  opacity: 0.5;
  position: relative;

  svg {
    position: absolute;
    bottom: -5px;
  }
`;
const SeatDetailsPod = _ref => {
  let {
    allAisleAccess,
    adjacentSeatBlocked,
    seatConfig = '',
    seatText = '',
    compact = false
  } = _ref;
  const SeatDetail = () => /*#__PURE__*/React.createElement(React.Fragment, null, allAisleAccess && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Perk, null, "All aisle access.")), adjacentSeatBlocked && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Perk, null, "Middle seats blocked.")), seatText && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Description, null, seatText)));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    pb: compact ? 2 : 0
  }, /*#__PURE__*/React.createElement("b", null, "Lie Flat Pod")), !compact && /*#__PURE__*/React.createElement(SeatDetail, null), /*#__PURE__*/React.createElement(Flex, {
    flex: "0 0 auto"
  }, /*#__PURE__*/React.createElement(MetricBox, {
    mr: 1,
    title: "Flat Pod",
    value: /*#__PURE__*/React.createElement(IconWrapper$1, null, /*#__PURE__*/React.createElement(IconLieFlatPod, null))
  }), seatConfig && /*#__PURE__*/React.createElement(MetricBox, {
    ml: 1,
    title: "Layout",
    value: seatConfig
  })));
};
SeatDetailsPod.propTypes = {
  allAisleAccess: PropTypes.bool.isRequired,
  adjacentSeatBlocked: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/require-default-props
  seatText: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  seatConfig: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  compact: PropTypes.bool
};

/* eslint-disable react/forbid-prop-types */
const SeatPitch = styled.span``;
const SeatIconWrapper = styled.span``;
const BoxSeat = styled(Box)`
  background: rgba(255, 255, 255, 0.65);
  box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 2px;
  font-size: 11px;
  padding: 3px 3px 1px 2px;
  cursor: default;

  ${SeatPitch},
  ${SeatIconWrapper} {
    transition: opacity 0.2s;
  }

  :hover ${SeatPitch}, :hover ${SeatIconWrapper} {
    opacity: 1;
    transition: opacity 0.3s;
  }
`;
const Size16 = styled.span`
  padding: 0 1px;
  display: inline-block;
  font-size: 16px;
`;
const AmenitySeat = _ref => {
  let {
    amenities
  } = _ref;
  const {
    seatType,
    seatText,
    seatWidth,
    seatPitch,
    seatConfig,
    allAisleAccess,
    adjacentSeatBlocked
  } = amenities;
  if (seatType === SEAT_SIZE.suite) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: /*#__PURE__*/React.createElement(SeatDetailsSuite, {
        allAisleAccess: allAisleAccess,
        adjacentSeatBlocked: adjacentSeatBlocked,
        seatText: seatText,
        seatConfig: seatConfig
      })
    }, /*#__PURE__*/React.createElement(BoxSeat, {
      mr: 1
    }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(SeatIconWrapper, null, /*#__PURE__*/React.createElement(Size16, null, /*#__PURE__*/React.createElement(IconLieFlatSuite, null))))));
  }
  if (seatType === SEAT_SIZE.flatPod) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: /*#__PURE__*/React.createElement(SeatDetailsPod, {
        allAisleAccess: allAisleAccess,
        adjacentSeatBlocked: adjacentSeatBlocked,
        seatText: seatText,
        seatConfig: seatConfig
      })
    }, /*#__PURE__*/React.createElement(BoxSeat, {
      mr: 1
    }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(SeatIconWrapper, null, /*#__PURE__*/React.createElement(Size16, null, /*#__PURE__*/React.createElement(IconLieFlatPod, null))))));
  }
  if (seatType === SEAT_SIZE.angledFlat) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: /*#__PURE__*/React.createElement(SeatDetailsAngledFlat, {
        allAisleAccess: allAisleAccess,
        adjacentSeatBlocked: adjacentSeatBlocked,
        seatText: seatText,
        seatConfig: seatConfig
      })
    }, /*#__PURE__*/React.createElement(BoxSeat, {
      mr: 1
    }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(SeatIconWrapper, null, /*#__PURE__*/React.createElement(Size16, null, /*#__PURE__*/React.createElement(IconAngledFlatSeat, null))))));
  }
  if (seatType === SEAT_SIZE.fullFlat) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: /*#__PURE__*/React.createElement(SeatDetailsFlat, {
        allAisleAccess: allAisleAccess,
        adjacentSeatBlocked: adjacentSeatBlocked,
        seatText: seatText,
        seatConfig: seatConfig
      })
    }, /*#__PURE__*/React.createElement(BoxSeat, {
      mr: 1
    }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(SeatIconWrapper, null, /*#__PURE__*/React.createElement(Size16, null, /*#__PURE__*/React.createElement(IconLieFlatSeat, null))))));
  }
  if (seatPitch) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: /*#__PURE__*/React.createElement(SeatDetailsStandard, {
        allAisleAccess: allAisleAccess,
        adjacentSeatBlocked: adjacentSeatBlocked,
        seatText: seatText,
        seatPitch: seatPitch,
        seatWidth: seatWidth,
        seatConfig: seatConfig
      })
    }, /*#__PURE__*/React.createElement(BoxSeat, {
      mr: 1
    }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(SeatIconWrapper, null, /*#__PURE__*/React.createElement(IconMiddleSeatFree, null)), /*#__PURE__*/React.createElement(SeatPitch, null, seatPitch.replace(' in.', '"')))));
  }
  return null;
};
AmenitySeat.propTypes = {
  amenities: PropTypes.shape({
    seatType: PropTypes.string,
    seatText: PropTypes.string,
    seatWidth: PropTypes.string,
    seatPitch: PropTypes.string,
    seatConfig: PropTypes.string,
    allAisleAccess: PropTypes.bool,
    adjacentSeatBlocked: PropTypes.bool
  }).isRequired
};

/* eslint-disable react/forbid-prop-types */
const TooltipText = styled.div``;
const BoxTooltipDescription$2 = styled.div`
  opacity: 0.7;
  font-size: 13px;
`;
const FlexWifiContainer$1 = styled(Flex)`
  font-size: 20px;
  color: ${_ref => {
  let {
    notIncluded
  } = _ref;
  return notIncluded ? getTokenValue(THEME_TOKENS.base.color.secondaryText) : 'inherit';
}};
  position: relative;
`;
const FlexWifi = styled(FlexWifiContainer$1)`
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
    transition: opacity 0.3s;
  }
`;
const WifiMaybeDotIndicator = styled(MaybeDotIndicator)`
  bottom: 2px;
  right: 1px;
`;
const SlashedLine$1 = styled.span`
  position: absolute;
  background: ${getTokenValue(THEME_TOKENS.base.color.danger)};
  height: 2px;
  margin-left: 1px;
  top: calc(50% - 1px);
  width: 90%;
  transform: rotate(-50deg);
  box-shadow: 0 1px 0 0 ${getTokenValue(THEME_TOKENS.base.color.infoBackground)},
    0 -1px 0 0 ${getTokenValue(THEME_TOKENS.base.color.infoBackground)};
  z-index: ${ZINDEX.DROPDOWN};
`;
const NO_WIFI_CODE = 'No';
const YES_WIFI_CODE = 'Yes';
const FAST_WIFI_CODE = 'Fast';
const MAYBE_WIFI_CODE = 'Maybe';
const AmenityWifi = _ref2 => {
  let {
    wifiStatus,
    wifiText,
    suppressTooltip
  } = _ref2;
  if (wifiStatus === NO_WIFI_CODE) {
    if (suppressTooltip) {
      return /*#__PURE__*/React.createElement(FlexWifiContainer$1, {
        notIncluded: true
      }, /*#__PURE__*/React.createElement(SlashedLine$1, null), " ", /*#__PURE__*/React.createElement(IconWiFi, null));
    }
    return /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: wifiText
    }, /*#__PURE__*/React.createElement(FlexWifi, {
      notIncluded: true
    }, /*#__PURE__*/React.createElement(SlashedLine$1, null), " ", /*#__PURE__*/React.createElement(IconWiFi, null)));
  }
  if (wifiStatus === YES_WIFI_CODE) {
    if (suppressTooltip) {
      return /*#__PURE__*/React.createElement(FlexWifiContainer$1, null, /*#__PURE__*/React.createElement(IconWiFi, null));
    }
    return /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: /*#__PURE__*/React.createElement(TooltipText, null, /*#__PURE__*/React.createElement("b", null, "WiFi"), /*#__PURE__*/React.createElement(Box, {
        pt: 2
      }, /*#__PURE__*/React.createElement(BoxTooltipDescription$2, null, wifiText)))
    }, /*#__PURE__*/React.createElement(FlexWifi, null, /*#__PURE__*/React.createElement(IconWiFi, null)));
  }
  if (wifiStatus === FAST_WIFI_CODE) {
    if (suppressTooltip) {
      return /*#__PURE__*/React.createElement(FlexWifiContainer$1, null, /*#__PURE__*/React.createElement(IconWiFiFast, null));
    }
    return /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: /*#__PURE__*/React.createElement(TooltipText, null, /*#__PURE__*/React.createElement("b", null, "Really Great Wifi"), /*#__PURE__*/React.createElement(Box, {
        pt: 2
      }, /*#__PURE__*/React.createElement(BoxTooltipDescription$2, null, wifiText)))
    }, /*#__PURE__*/React.createElement(FlexWifi, null, /*#__PURE__*/React.createElement(IconWiFiFast, null)));
  }
  if (wifiStatus === MAYBE_WIFI_CODE) {
    if (suppressTooltip) {
      return /*#__PURE__*/React.createElement(FlexWifiContainer$1, null, /*#__PURE__*/React.createElement(MaybeContainer, null, /*#__PURE__*/React.createElement(WifiMaybeDotIndicator, null), /*#__PURE__*/React.createElement(IconWiFi, null)));
    }
    return /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: /*#__PURE__*/React.createElement(TooltipText, null, /*#__PURE__*/React.createElement("b", null, "WiFi"), " ", /*#__PURE__*/React.createElement(MaybeTitle, null, "(maybe)"), /*#__PURE__*/React.createElement(Box, {
        pt: 2
      }, /*#__PURE__*/React.createElement(BoxTooltipDescription$2, null, wifiText)))
    }, /*#__PURE__*/React.createElement(FlexWifi, null, /*#__PURE__*/React.createElement(MaybeContainer, null, /*#__PURE__*/React.createElement(WifiMaybeDotIndicator, null), /*#__PURE__*/React.createElement(IconWiFi, null))));
  }
  return null;
};
AmenityWifi.propTypes = {
  wifiStatus: PropTypes.string.isRequired,
  wifiText: PropTypes.string.isRequired,
  suppressTooltip: PropTypes.bool
};
AmenityWifi.defaultProps = {
  suppressTooltip: false
};
var AmenityWifi$1 = /*#__PURE__*/memo(AmenityWifi);

/* eslint-disable consistent-return */
const breakpointXXL$1 = '1520px';
const breakpointXXXL = '1640px';
const fourColumnStyles = props => {
  if (props.columnCount >= 4) {
    return css`
      width: 140px;

      @media (min-width: ${breakpointXXXL}) {
        width: 160px;
      }
    `;
  }
};
const fiveColumnStyles = props => {
  if (props.columnCount === 5) {
    return css`
      width: 116px;

      @media (min-width: ${getThemeProp('global.breakpointLG--size')}px) {
        width: 126px;
      }
      @media (min-width: ${breakpointXXL$1}) {
        width: 150px;
      }
      @media (min-width: ${breakpointXXXL}) {
        width: 160px;
      }
    `;
  }
};
const sixColumnStyles = props => {
  if (props.columnCount >= 6) {
    return css`
      width: 106px;

      @media (min-width: ${breakpointXXL$1}) {
        width: 130px;
      }
    `;
  }
};
const BoxFareSpacer = styled(Box)`
  width: 160px;

  ${fourColumnStyles};
  ${fiveColumnStyles};
  ${sixColumnStyles};

  /* see note in @amtrav/themes/MatchMedia why we are using 0.02 in the media query */
  @media (max-width: ${props => {
  return getThemeProp('global.breakpointXL--size')(props) - 0.02;
}}px) {
    display: none;
  }
`;
const BoxFlightDetailsSpacer = styled(Box)`
  flex: 0 0 auto;
  -ms-flex: 0 0 100%; // IE FIX

  ${media.Large`
    flex: 0 0 320px;
  `}

  ${media.XLargeOnly`
    flex: 1 0 auto;
    -ms-flex: 1 0 100%; // IE FIX
    max-width: 640px;
  `}
`;

const PreviewFlightLegSelector = _ref => {
  let {
    legList,
    selectedLeg,
    setSelectedLeg,
    setSelectedLegIndex
  } = _ref;
  const [disableBackButton, setDisableBackButton] = useState(false);
  const [disableNextButton, setDisableNextButton] = useState(false);
  const handleTabClicked = leg => {
    setSelectedLeg(leg);
  };
  useEffect(() => {
    const currentLegFlightNumber = selectedLeg.flightNumber;
    const currentIndex = legList.findIndex(leg => leg.flightNumber === currentLegFlightNumber);
    setSelectedLegIndex(currentIndex);
    if (currentIndex === -1) {
      return;
    }
    if (currentIndex === 0) {
      setDisableBackButton(true);
      setDisableNextButton(false);
      return;
    }
    if (currentIndex === legList.length - 1) {
      setDisableBackButton(false);
      setDisableNextButton(true);
      return;
    }
    setDisableBackButton(false);
    setDisableNextButton(false);
  }, [selectedLeg]);
  const setLegToNext = () => {
    const flightLegs = legList;
    const currentLegId = selectedLeg.flightNumber;
    const currentIndex = flightLegs.findIndex(leg => leg.flightNumber === currentLegId);
    if (currentIndex === -1) {
      return;
    }
    const nextIndex = (currentIndex + 1) % flightLegs.length;
    const nextLeg = flightLegs[nextIndex];
    setSelectedLeg(nextLeg);
  };
  const setLegToPrevious = () => {
    const flightLegs = legList;
    const currentLegId = selectedLeg.flightNumber;
    const currentIndex = flightLegs.findIndex(leg => leg.flightNumber === currentLegId);
    if (currentIndex === -1) {
      return;
    }
    const previousIndex = (currentIndex - 1) % flightLegs.length;
    const previousLeg = flightLegs[previousIndex];
    setSelectedLeg(previousLeg);
  };
  const themeContext = useContext(ThemeContext);
  const isMobileOrSmaller = isMatchedMaxWidth(`${themeContext.global['breakpointSM--size']}px`);
  // console.log('selectedLeg: ', selectedLeg);
  return /*#__PURE__*/React.createElement(Box, {
    pt: isMobileOrSmaller ? 0 : 3
  }, /*#__PURE__*/React.createElement(Tabs$1.List, {
    customTabSwitchingControls: isMobileOrSmaller ? /*#__PURE__*/React.createElement(Flex, {
      justifyContent: "space-around",
      backgroundColor: "white",
      py: 3
    }, /*#__PURE__*/React.createElement(Flex, {
      justifyContent: "center",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: setLegToPrevious,
      icon: /*#__PURE__*/React.createElement(IconPrevious, null),
      disabled: disableBackButton,
      round: true
    })), /*#__PURE__*/React.createElement(Box, {
      pt: "6px"
    }, /*#__PURE__*/React.createElement(FlightLegDisplay, {
      departureAirport: selectedLeg.fromCode,
      arrivalAirport: selectedLeg.toCode,
      airlineCode: selectedLeg.airlineCode,
      isSelected: true
    })), /*#__PURE__*/React.createElement(Flex, {
      justifyContent: "center",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: setLegToNext,
      icon: /*#__PURE__*/React.createElement(IconNext, null),
      disabled: disableNextButton,
      round: true
    }))) : null
  }, legList.map(leg => {
    return /*#__PURE__*/React.createElement(Tabs$1.Trigger, {
      grow: true,
      value: `${leg.fromCode}${leg.toCode}`,
      kind: "primary",
      onClick: () => handleTabClicked(leg),
      key: leg.flightNumber
    }, /*#__PURE__*/React.createElement(FlightLegDisplay, {
      departureAirport: leg.fromCode,
      arrivalAirport: leg.toCode,
      airlineCode: leg.airlineCode,
      isSelected: selectedLeg.flightNumber === leg.flightNumber
    }));
  })));
};
const LegDataPropTypes = {
  fromCode: PropTypes.string,
  toCode: PropTypes.string,
  airlineCode: PropTypes.string,
  flightNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
PreviewFlightLegSelector.propTypes = {
  legList: PropTypes.arrayOf(PropTypes.shape(LegDataPropTypes)),
  selectedLeg: PropTypes.shape(LegDataPropTypes),
  setSelectedLeg: PropTypes.func,
  setSelectedLegIndex: PropTypes.func
};

const DEFAULT_PLANE_WIDTH = 500;
const OverrideJetSeats = createGlobalStyle`
  // hide legacy seat map tabs and metadata as React-based seat maps now handle this
  #seatTabs {
    display: none;
  }
  #seatMeta .plane-head-det-region {
    display: none;
  }
  .plane-head {
    margin-top: 32px;
  }
`;
const SeatLink = styled.span`
  color: ${getThemeProp('global.link--color')};
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
`;
const SeatMapPreview = _ref => {
  let {
    segments,
    activeLeg,
    fetchSeatAvailability,
    baseUrl
  } = _ref;
  const [isOpen, setIsOpen] = useState(false);
  // activeLegIndex is the index of the leg inside the seatmap modal
  // activeLeg is the index of the leg on the result card in the list
  const [activeLegIndex, setActiveLegIndex] = useState(activeLeg);
  const [activeLegData, setActiveLegData] = useState(segments[activeLegIndex]);
  const [deviceWidth, setDeviceWidth] = useState(DEFAULT_PLANE_WIDTH);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [cachedSeats, setCachedSeats] = useState();

  // Allow for optional overrides to the api method

  const fetchSeatData = async () => {
    setIsLoading(true);
    try {
      const requiredLegDatas = [];
      segments.forEach(_ref2 => {
        let {
          aircraftTypeCode,
          airlineCode,
          arrives,
          departs,
          flightNumber,
          fromCode,
          toCode
        } = _ref2;
        requiredLegDatas.push({
          aircraftTypeCode,
          airlineCode,
          arrives,
          departs,
          flightNumber,
          fromCode,
          toCode
        });
      });
      await Promise.all(requiredLegDatas.map(segData => {
        return fetchSeatAvailability(segData);
      })).then(segmentsSeatData => {
        const segmentsSeatDataArrayOfMaps = [];
        segmentsSeatData.forEach(data => {
          if (!data.data) return segmentsSeatDataArrayOfMaps.push(null);
          const {
            seats,
            cabinLabels,
            responseMsg
          } = data.data;
          const seatData = new Map();
          seats.forEach(seat => {
            if (['Y', 'N', 'R', 'X'].includes(seat.status)) {
              var _seat$policyStatus;
              seatData.set(seat.seatNum, {
                cabinBrandName: seat.cabinBrandName,
                seatStatus: seat.status,
                seatPrice: seat.price ? Math.round(seat.price) : 0,
                amenities: seat.amenitiesAttributes || [],
                seatPitch: seat.seatPitch,
                seatWidth: seat.seatWidth,
                outOfPolicy: seat === null || seat === void 0 ? void 0 : seat.outOfPolicy,
                outOfPolicyReason: seat === null || seat === void 0 || (_seat$policyStatus = seat.policyStatus) === null || _seat$policyStatus === void 0 ? void 0 : _seat$policyStatus.policyBrokeText,
                isEliteSeat: !seat.enhanced && seat.preferred
              });
            }
          });

          // remap object keys to use uppercase keys
          const cabinLabelsObject = {};
          Object.keys(cabinLabels).forEach(key => {
            cabinLabelsObject[key.toUpperCase()] = cabinLabels[key];
          });
          return segmentsSeatDataArrayOfMaps.push({
            seatData,
            cabinLabels: cabinLabelsObject,
            responseMessage: responseMsg || null
          });
        });
        setCachedSeats(segmentsSeatDataArrayOfMaps);
      }).catch(err => {
        setError('This seat map is unavailable.');
        console.error(err);
      });
      setIsLoading(false);
    } catch (err) {
      setError('This seat map is unavailable.');
      console.error(err);
    }
  };
  const openModal = () => {
    fetchSeatData();
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsOpen(false);
  };
  useEffect(() => {
    setActiveLegIndex(activeLeg);
    setActiveLegData(segments[activeLeg]);
  }, [activeLeg]);
  useEffect(() => {
    if (isOpen) {
      const windowInnerWidth = window.innerWidth;
      setDeviceWidth(windowInnerWidth > DEFAULT_PLANE_WIDTH ? DEFAULT_PLANE_WIDTH : windowInnerWidth - 32);
    }
  }, [isOpen]);
  const formatDuration = duration => {
    // Convert duration to hours and minutes
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;

    // Construct the formatted string
    let formattedDuration = '';
    if (hours > 0) {
      formattedDuration += `${hours}h`;
    }
    if (minutes > 0) {
      formattedDuration += `${(formattedDuration ? ' ' : '') + minutes}m`;
    }
    return formattedDuration;
  };
  const withShorterText = isMatchedMaxWidth('1350px');
  const seatLinkText = withShorterText ? 'Seats' : 'Seat Maps';
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextBullet, null), /*#__PURE__*/React.createElement(SeatLink, {
    onClick: openModal
  }, seatLinkText), isOpen && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(OverrideJetSeats, null), /*#__PURE__*/React.createElement(Modal, {
    fullscreen: deviceWidth < DEFAULT_PLANE_WIDTH,
    onClose: closeModal
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Seat Map"), /*#__PURE__*/React.createElement(Modal.Body, null, error ? /*#__PURE__*/React.createElement(Box, {
    style: {
      minHeight: '70vh',
      maxWidth: '300px'
    }
  }, /*#__PURE__*/React.createElement(Notice, {
    kind: "warning"
  }, error)) : /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(Tabs$1, {
    value: `${activeLegData.fromCode}${activeLegData.toCode}`
  }, segments.length > 1 && /*#__PURE__*/React.createElement(Box, {
    pb: 3
  }, /*#__PURE__*/React.createElement(PreviewFlightLegSelector, {
    legList: segments,
    selectedLeg: activeLegData,
    setSelectedLeg: setActiveLegData,
    setSelectedLegIndex: setActiveLegIndex
  })), segments.map((seg, i) => {
    return /*#__PURE__*/React.createElement(Tabs$1.Content, {
      key: `${seg.fromCode}${seg.toCode}`,
      value: `${seg.fromCode}${seg.toCode}`,
      forceMount: true,
      style: {
        display: seg.flightNumber === activeLegData.flightNumber ? null : 'none'
      }
    }, /*#__PURE__*/React.createElement(Box, {
      style: {
        minHeight: '100vh',
        minWidth: `${deviceWidth}px`
      }
    }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(SelectedLegInfo, {
      departureCity: seg.fromName,
      arrivalCity: seg.toName,
      departureDateDisplay: weekdayMonthDayYear(seg.departsDate),
      departureTimeDisplay: hourMinuteMeridianCondensed(seg.departsDate),
      duration: formatDuration(seg.duration),
      flightNumber: `${seg.airlineName} ${seg.flightNumber}`,
      aircraftType: seg.aircraftTypeName,
      airlineCode: seg.airlineCode,
      opAirlineCode: seg.opAirlineCode,
      opAirlineName: seg.opAirlineName
    })), isLoading && !error ? /*#__PURE__*/React.createElement(Box, {
      pt: 4
    }, /*#__PURE__*/React.createElement(ActivityIndicator, null)) : null, !isLoading && /*#__PURE__*/React.createElement(SeatMap, {
      flight: {
        // Don't think we need this, flight obj should be changing with state
        // for caching purposes
        // id: `${activeLegIndex}`,
        airlineCode: seg.airlineCode,
        flightNo: `${seg.flightNumber}`,
        departureDate: isoExtendedFormat(seg.departsDate),
        departure: seg.fromCode,
        arrival: seg.toCode
      },
      baseUrl: baseUrl,
      seatData: cachedSeats[i].seatData,
      customNoticeMessage: cachedSeats[i].responseMessage,
      cabinLabels: cachedSeats[i].cabinLabels,
      isPreview: true
    })));
  })))))));
};
SeatMapPreview.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({
    aircraftTypeCode: PropTypes.string,
    airlineCode: PropTypes.string,
    arrives: PropTypes.string,
    departs: PropTypes.string,
    toCode: PropTypes.string,
    flightNumber: PropTypes.number,
    fromCode: PropTypes.string
  })).isRequired,
  /**
   * Represents which of the legs is selected in the result, before the preview opens
   */
  activeLeg: PropTypes.number.isRequired,
  fetchSeatAvailability: PropTypes.func.isRequired,
  baseUrl: PropTypes.string
};

const TextLink = styled.span`
  color: ${getThemeProp('global.link--color')};
  text-decoration: underline;
  font-weight: 600;
  display: inline-flex;
  cursor: pointer;
`;
const Figure$1 = styled.figure`
  color: white;
  position: relative;
  display: block;
  margin: 0 auto;
`;
const FigCaptionWrapper = styled.figcaption`
  position: absolute;
  bottom: 10px;
  width: 100%;
  text-align: center;
`;
const FigCaption = styled.span`
  color: white;
  display: inline-block;
  background: rgba(0, 0, 0, 0.5);
  font-weight: 600;
  padding: 4px 16px;
  border: 1px solid rgba(255, 255, 255, 0.5);
`;
const ImageGallery = styled.div`
  max-width: 80vw;

  ${media.Medium`
    max-width: 700px;
  `}

  ${media.XLargeOnly`
    max-width: ${_ref => {
  let {
    hasVirtualTour
  } = _ref;
  return hasVirtualTour ? '1200px' : '700px';
}};
  `}

  & img {
    width: 80vw;
    margin: 0 auto;
    display: block;

    ${media.Medium`
      max-height: 400px;
      width: auto;
    `}
  }
`;
const BoxPlaneInfo = styled(Box)`
  font-size: 14px;
  font-style: italic;
  opacity: 0.8;
`;
const CameraWrapper = styled(Box)`
  height: 10px;
  overflow: hidden;
`;
const VirtualTourContainer = styled.div`
  height: 400px;
  width: 100%;
  margin: 0 auto;

  @media (min-height: 1000px) {
    height: 800px;
  }
  @media (min-width: 1400px) {
    width: 1200px;
  }
`;
const VirtualTour = styled.iframe`
  height: 400px;
  margin: 0 auto;
  width: 100%;
  border: 0;

  @media (min-height: 1000px) {
    height: 800px;
  }
  @media (min-width: 1400px) {
    width: 1200px;
  }
`;
const imagesHasVirtualTours = images => {
  return images.some(img => img.isVirtualTour && img.virtualTourUrl);
};
const FlightImages = _ref2 => {
  let {
    images,
    airlineName,
    flightNumber,
    aircraftTypeName
  } = _ref2;
  if (!images.length) {
    return null;
  }
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVirtualTourVisible, setVirtualTourVisible] = useState(false);
  let timer = null;
  const openModal = () => {
    // The virtual tour iframe causes the carousel to jump and misalign, so we wait one second to resolve this
    timer = setTimeout(() => {
      setVirtualTourVisible(true);
    }, 1000);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    clearTimeout(timer);
    setVirtualTourVisible(false);
    setIsModalOpen(false);
  };
  const hasVirtualTour = imagesHasVirtualTours(images);
  const photosTitleText = hasVirtualTour ? '360° Tour' : 'Photos';
  const imageList = hasVirtualTour ? images.filter(img => img.isVirtualTour) : images;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextBullet, null), /*#__PURE__*/React.createElement(TextLink, {
    onClick: openModal
  }, /*#__PURE__*/React.createElement(CameraWrapper, {
    pr: 1
  }, /*#__PURE__*/React.createElement(IconCamera, null)), photosTitleText), isModalOpen && /*#__PURE__*/React.createElement(Modal, {
    onClose: closeModal
  }, /*#__PURE__*/React.createElement(Modal.Header, null, photosTitleText, /*#__PURE__*/React.createElement(BoxPlaneInfo, {
    pt: 2
  }, airlineName, " ", flightNumber, " \u2013 ", aircraftTypeName)), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(ImageGallery, {
    hasVirtualTour: hasVirtualTour
  }, /*#__PURE__*/React.createElement(Carousel, {
    showCount: true
  }, imageList.map(img => /*#__PURE__*/React.createElement(Figure$1, {
    key: img.url
  }, img.isVirtualTour && img.virtualTourUrl ? /*#__PURE__*/React.createElement(VirtualTourContainer, null, /*#__PURE__*/React.createElement(VirtualTour, {
    style: {
      display: isVirtualTourVisible ? 'block' : 'none'
    },
    title: "Virtual Tour",
    src: img.virtualTourUrl,
    key: img.virtualTourUrl
  }), /*#__PURE__*/React.createElement(FigCaptionWrapper, null, /*#__PURE__*/React.createElement(FigCaption, null, img.cabin && imageList.length > 1 ? `${img.cabin} ` : '', "360\xB0 Tour"))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("img", {
    alt: img.cabin,
    src: img.url.startsWith('http') ? img.url : `https://cdn.amtrav.com/airline-interior/${img.url}`
  }), img.cabin && /*#__PURE__*/React.createElement(FigCaptionWrapper, null, /*#__PURE__*/React.createElement(FigCaption, null, img.cabin))))))))));
};
FlightImages.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({
    cabin: PropTypes.string,
    url: PropTypes.string
  })),
  airlineName: PropTypes.string,
  flightNumber: PropTypes.number,
  aircraftTypeName: PropTypes.string
};
FlightImages.defaultProps = {
  images: [],
  airlineName: '',
  flightNumber: null,
  aircraftTypeName: ''
};

const FlexNoCarryOnContainer = styled(Flex)`
  font-size: 16px;
  position: relative;
  color: ${_ref => {
  let {
    notIncluded
  } = _ref;
  return notIncluded ? getTokenValue(THEME_TOKENS.base.color.secondaryText) : 'inherit';
}};
  padding: 2px;
`;
const FlexNoCarryOn = styled(FlexNoCarryOnContainer)`
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
    transition: opacity 0.3s;
  }
`;
const SlashedLine = styled.span`
  position: absolute;
  background: ${getTokenValue(THEME_TOKENS.base.color.danger)};
  height: 2px;
  margin-left: -1px;
  top: 50%;
  width: 90%;
  transform: rotate(-50deg);
  box-shadow: 0 1px 0 0 ${getTokenValue(THEME_TOKENS.base.color.infoBackground)},
    0 -1px 0 0 ${getTokenValue(THEME_TOKENS.base.color.infoBackground)};
  z-index: ${ZINDEX.DROPDOWN};
`;
const BoxTooltipDescription$1 = styled(Box)`
  opacity: 0.7;
  font-size: 13px;
`;
const AmenityNoCarryOn = _ref2 => {
  let {
    fee,
    withMargin,
    suppressTooltip
  } = _ref2;
  if (suppressTooltip) {
    return /*#__PURE__*/React.createElement(FlexNoCarryOnContainer, {
      notIncluded: true,
      mr: withMargin ? '1px' : 0
    }, /*#__PURE__*/React.createElement(SlashedLine, null), " ", /*#__PURE__*/React.createElement(IconLuggage, null));
  }
  return /*#__PURE__*/React.createElement(Tooltip, {
    placement: "top",
    content: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Carry-On Bag"), /*#__PURE__*/React.createElement(BoxTooltipDescription$1, {
      pt: 2
    }, fee, "."))
  }, /*#__PURE__*/React.createElement(FlexNoCarryOn, {
    notIncluded: true,
    mr: withMargin ? '1px' : 0
  }, /*#__PURE__*/React.createElement(SlashedLine, null), " ", /*#__PURE__*/React.createElement(IconLuggage, null)));
};
AmenityNoCarryOn.propTypes = {
  fee: PropTypes.string,
  withMargin: PropTypes.bool,
  suppressTooltip: PropTypes.bool
};
AmenityNoCarryOn.defaultProps = {
  fee: '',
  withMargin: false,
  suppressTooltip: false
};

/* eslint-disable react/prop-types */
const breakpointXXL = '1520px';
const FlexTabs = styled(Flex)`
  height: 50px;
  top: -50px;
  gap: 4px;
  position: absolute;
  visibility: ${_ref => {
  let {
    isLoading
  } = _ref;
  return isLoading ? 'hidden' : 'unset';
}};
`;
const BoxSegment = styled(Box)`
  position: relative;
`;
const FlexSegmentDetails = styled(Flex)`
  height: 34px;
  background-color: ${getTokenValue(THEME_TOKENS.card.color.background)};
  border-top: 1px solid ${getThemeProp('global.border--color')};
  font-size: 0.75rem;
  border-bottom-right-radius: ${_ref2 => {
  let {
    hasCodeShare
  } = _ref2;
  return hasCodeShare ? 0 : getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge);
}};
  border-bottom-left-radius: ${_ref3 => {
  let {
    hasCodeShare
  } = _ref3;
  return hasCodeShare ? 0 : getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge);
}};
`;
const BoxTab = styled(Box)`
  font-size: 14px;
  font-weight: 600;
  background-color: white;
  border: 1px solid ${getThemeProp('global.border--color')};
  border-bottom: 0;
  cursor: ${_ref4 => {
  let {
    selected
  } = _ref4;
  return selected ? 'default' : 'pointer';
}};
  border-top-left-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusSmall)};
  border-top-right-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusSmall)};
  padding: ${SPACE.size8} ${SPACE.size16};

  ${_ref5 => {
  let {
    selected
  } = _ref5;
  return selected && css`
      padding-bottom: 10px;
      background-color: ${getTokenValue(THEME_TOKENS.card.color.background)};
      transform: translateY(1px);
      /* background-color: ${getThemeProp('global.border--color')}; */
      color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
    `;
}};
`;
const FlexFares$2 = styled(BoxFareSpacer)`
  height: 34px;
  border-left: ${props => props.withLeftBorder ? `1px solid ${tinycolor.mix(getThemeProp('global.border--color')(props), 'black', 10).toString()}` : 'unset'};
  border-right: ${props => props.withRightBorder ? `1px solid ${tinycolor.mix(getThemeProp('global.border--color')(props), 'black', 10).toString()}` : 'unset'};
`;
const FaresContainer = styled(Flex)`
  border-left: 1px solid
    ${props => tinycolor.mix(getThemeProp('global.border--color')(props), 'black', 10).toString()};
`;
const FlexSegmentLeg = styled(BoxFlightDetailsSpacer)`
  display: flex;
  flex: 0 0 auto;

  ${media.Large`
    flex: 0 0 auto;
  `}

  ${media.XLargeOnly`
    flex: 1 0 auto;
    max-width: 640px;
  `}
`;
const WifiBox = styled(Box)`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  display: none;

  ${media.XLargeOnly`
    display: block;
  `}
`;
const ReviewLink = styled.span`
  color: ${getThemeProp('global.link--color')};
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
`;
const AircraftName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 88px;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};

  @media (min-width: ${breakpointXXL}) {
    max-width: 110px;
  }
`;
const FlexSegmentLinks = styled(Flex)`
  min-width: 0;
  max-width: 360px;
`;
const AmenityFlex = styled(Flex)`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const Tabs = /*#__PURE__*/memo(_ref6 => {
  let {
    onTabSelection,
    activeLeg,
    segments
  } = _ref6;
  if (segments.length <= 1) return null;
  return segments.map((leg, idx) => /*#__PURE__*/React.createElement(BoxTab, {
    onClick: () => {
      onTabSelection(idx);
    },
    selected: idx === activeLeg,
    key: `${leg.fromCode}-${leg.toCode}`
  }, leg.fromCode, " \u2013 ", leg.toCode));
});
Tabs.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  segments: PropTypes.array,
  onTabSelection: PropTypes.func.isRequired,
  activeLeg: PropTypes.number.isRequired
};
Tabs.defaultProps = {
  segments: []
};
const Segments = _ref7 => {
  var _segments$activeLeg, _segments$activeLeg2, _segments$activeLeg3, _segments$activeLeg4, _segments$activeLeg5, _segments$activeLeg6, _segments$activeLeg7, _flatFares$, _flatFares$0$amenitie, _flatFares$2, _flatFares$0$amenitie2;
  let {
    columnBrandCount,
    segments,
    fares,
    isLoading,
    activeLeg,
    setActiveLeg,
    cmsPostClickHandler,
    fetchSeatAvailability,
    baseUrl,
    hasCodeShare
  } = _ref7;
  const flatFares = fares.flat();
  return /*#__PURE__*/React.createElement(BoxSegment, null, /*#__PURE__*/React.createElement(FlexTabs, {
    isLoading: isLoading,
    ml: -3,
    pl: 4,
    alignItems: "flex-end"
  }, /*#__PURE__*/React.createElement(Tabs, {
    activeLeg: activeLeg,
    onTabSelection: setActiveLeg,
    segments: segments
  })), /*#__PURE__*/React.createElement(FlexSegmentDetails, {
    hasCodeShare: hasCodeShare,
    alignItems: "center",
    justifyContent: columnBrandCount >= 4 ? 'space-between' : 'unset'
  }, /*#__PURE__*/React.createElement(FlexSegmentLeg, {
    columnCount: columnBrandCount,
    alignItems: "center",
    justifyContent: "space-between",
    pl: SPACE.size24
  }, !isLoading && /*#__PURE__*/React.createElement(FlexSegmentLinks, null, /*#__PURE__*/React.createElement(AircraftName, {
    title: segments === null || segments === void 0 || (_segments$activeLeg = segments[activeLeg]) === null || _segments$activeLeg === void 0 ? void 0 : _segments$activeLeg.aircraftTypeName
  }, segments === null || segments === void 0 || (_segments$activeLeg2 = segments[activeLeg]) === null || _segments$activeLeg2 === void 0 ? void 0 : _segments$activeLeg2.aircraftTypeName), /*#__PURE__*/React.createElement(SeatMapPreview, {
    segments: segments,
    activeLeg: activeLeg,
    fetchSeatAvailability: fetchSeatAvailability,
    baseUrl: baseUrl
  }), /*#__PURE__*/React.createElement(FlightImages, {
    airlineName: segments === null || segments === void 0 || (_segments$activeLeg3 = segments[activeLeg]) === null || _segments$activeLeg3 === void 0 ? void 0 : _segments$activeLeg3.airlineName,
    flightNumber: segments === null || segments === void 0 || (_segments$activeLeg4 = segments[activeLeg]) === null || _segments$activeLeg4 === void 0 ? void 0 : _segments$activeLeg4.flightNumber,
    aircraftTypeName: segments === null || segments === void 0 || (_segments$activeLeg5 = segments[activeLeg]) === null || _segments$activeLeg5 === void 0 ? void 0 : _segments$activeLeg5.aircraftTypeName,
    images: segments === null || segments === void 0 || (_segments$activeLeg6 = segments[activeLeg]) === null || _segments$activeLeg6 === void 0 ? void 0 : _segments$activeLeg6.images
  }), (segments === null || segments === void 0 || (_segments$activeLeg7 = segments[activeLeg]) === null || _segments$activeLeg7 === void 0 ? void 0 : _segments$activeLeg7.reviewAvailable) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextBullet, null), /*#__PURE__*/React.createElement(ReviewLink, {
    onClick: () => {
      var _segments$activeLeg8;
      return cmsPostClickHandler('aircraft', segments === null || segments === void 0 || (_segments$activeLeg8 = segments[activeLeg]) === null || _segments$activeLeg8 === void 0 ? void 0 : _segments$activeLeg8.reviewAvailable);
    }
  }, "Review"))), /*#__PURE__*/React.createElement(WifiBox, {
    mr: 3
  }, !isLoading && /*#__PURE__*/React.createElement(AmenityWifi$1, {
    wifiStatus: flatFares !== null && flatFares !== void 0 && (_flatFares$ = flatFares[0]) !== null && _flatFares$ !== void 0 && (_flatFares$ = _flatFares$.amenities) !== null && _flatFares$ !== void 0 && (_flatFares$ = _flatFares$.segmentAmenities) !== null && _flatFares$ !== void 0 && (_flatFares$ = _flatFares$[activeLeg]) !== null && _flatFares$ !== void 0 && _flatFares$.wifiStatus ? (_flatFares$0$amenitie = flatFares[0].amenities) === null || _flatFares$0$amenitie === void 0 || (_flatFares$0$amenitie = _flatFares$0$amenitie.segmentAmenities) === null || _flatFares$0$amenitie === void 0 || (_flatFares$0$amenitie = _flatFares$0$amenitie[activeLeg]) === null || _flatFares$0$amenitie === void 0 ? void 0 : _flatFares$0$amenitie.wifiStatus : '',
    wifiText: flatFares !== null && flatFares !== void 0 && (_flatFares$2 = flatFares[0]) !== null && _flatFares$2 !== void 0 && (_flatFares$2 = _flatFares$2.amenities) !== null && _flatFares$2 !== void 0 && (_flatFares$2 = _flatFares$2.segmentAmenities) !== null && _flatFares$2 !== void 0 && (_flatFares$2 = _flatFares$2[activeLeg]) !== null && _flatFares$2 !== void 0 && _flatFares$2.wifiText ? (_flatFares$0$amenitie2 = flatFares[0].amenities) === null || _flatFares$0$amenitie2 === void 0 || (_flatFares$0$amenitie2 = _flatFares$0$amenitie2.segmentAmenities) === null || _flatFares$0$amenitie2 === void 0 || (_flatFares$0$amenitie2 = _flatFares$0$amenitie2[activeLeg]) === null || _flatFares$0$amenitie2 === void 0 ? void 0 : _flatFares$0$amenitie2.wifiText : ''
  }))), /*#__PURE__*/React.createElement(FaresContainer, null, fares.map((fare, i) => {
    var _fare$, _fare$2, _fare$3;
    const brandDetails = fare === null || fare === void 0 || (_fare$ = fare[0]) === null || _fare$ === void 0 ? void 0 : _fare$.flightBrand;
    const amenities = fare !== null && fare !== void 0 && (_fare$2 = fare[0]) !== null && _fare$2 !== void 0 && (_fare$2 = _fare$2.amenities) !== null && _fare$2 !== void 0 && _fare$2.segmentAmenities[activeLeg] ? fare === null || fare === void 0 || (_fare$3 = fare[0]) === null || _fare$3 === void 0 || (_fare$3 = _fare$3.amenities) === null || _fare$3 === void 0 ? void 0 : _fare$3.segmentAmenities[activeLeg] : {};
    return /*#__PURE__*/React.createElement(FlexFares$2, {
      columnCount: columnBrandCount,
      withLeftBorder: i !== 0,
      withRightBorder: columnBrandCount <= 3 && i === fares.length - 1
      // eslint-disable-next-line react/no-array-index-key
      ,
      key: `fare-${i}`,
      height: "100%",
      p: 2,
      alignItems: "center",
      justifyContent: "center"
    }, /*#__PURE__*/React.createElement(AmenityFlex, {
      justifyContent: "center",
      alignItems: "center",
      height: "100%"
    }, !isLoading && /*#__PURE__*/React.createElement(React.Fragment, null, hasCarryOnBagFee(brandDetails === null || brandDetails === void 0 ? void 0 : brandDetails.carryOnBag) ? /*#__PURE__*/React.createElement(AmenityNoCarryOn, {
      fee: brandDetails === null || brandDetails === void 0 ? void 0 : brandDetails.carryOnBag,
      withMargin: true
    }) : null, amenities && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AmenitySeat, {
      amenities: amenities
    }), /*#__PURE__*/React.createElement(Amenities, {
      columnBrandCount: columnBrandCount,
      amenities: amenities,
      maxNumberToShow: columnBrandCount === 6 && hasCarryOnBagFee(brandDetails === null || brandDetails === void 0 ? void 0 : brandDetails.carryOnBag) ? 3 : -1
    })))));
  }))));
};
Segments.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({})),
  columnBrandCount: PropTypes.number.isRequired,
  fares: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
  isLoading: PropTypes.bool.isRequired,
  activeLeg: PropTypes.number.isRequired,
  setActiveLeg: PropTypes.func.isRequired,
  cmsPostClickHandler: PropTypes.func.isRequired,
  fetchSeatAvailability: PropTypes.func.isRequired,
  baseUrl: PropTypes.string,
  hasCodeShare: PropTypes.bool
};
Segments.defaultProps = {
  segments: [{
    aircraftTypeName: ''
  }],
  fares: [],
  hasCodeShare: false
};
var Segments$1 = /*#__PURE__*/memo(Segments);

const OutOfPolicyIconWrapper = styled(Box)`
  color: ${getThemeProp('global.warning--color')};
  font-size: 19px;
  opacity: 0.85;
  display: flex;

  &:hover {
    opacity: 1;
    transition: opacity 0.3s;
  }
`;
const AdditionalText = styled(Box)`
  font-style: italic;
  background: rgba(0, 0, 0, 0.15);
`;
const BoxTooltipDescription = styled(Box)`
  font-size: 13px;
  max-width: 240px;

  ${media.Large`
    max-width: unset;
  `}
`;
const OutOfPolicyIcon = () => /*#__PURE__*/React.createElement(OutOfPolicyIconWrapper, null, /*#__PURE__*/React.createElement(IconWarningTriangle, null));
const OutOfPolicyIndicator = _ref => {
  let {
    details,
    additionalText = ''
  } = _ref;
  return /*#__PURE__*/React.createElement(Tooltip, {
    kind: "warning",
    placement: "top",
    content: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("b", null, "Out of Policy"), details.map(det => /*#__PURE__*/React.createElement(BoxTooltipDescription, {
      py: 2,
      key: det
    }, det)), additionalText ? /*#__PURE__*/React.createElement(AdditionalText, {
      my: 2,
      p: 2
    }, additionalText) : null)
  }, /*#__PURE__*/React.createElement(OutOfPolicyIcon, null));
};
OutOfPolicyIndicator.propTypes = {
  details: PropTypes.arrayOf(PropTypes.string),
  additionalText: PropTypes.string
};
OutOfPolicyIndicator.defaultProps = {
  details: []
};

const MixedCabinWrapper = styled(Box)`
  color: ${getThemeProp('global.warning--color')};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: default;
  opacity: 0.8;

  &:hover {
    opacity: 1;
    transition: opacity 0.3s;
  }
`;
const Text$1 = styled.span`
  padding-bottom: 1px;
  border-bottom: 1px dotted;

  &:hover {
    border-bottom: 1px solid;
  }
`;
const MixedCabinIndicator = () => {
  return /*#__PURE__*/React.createElement(MixedCabinWrapper, null, /*#__PURE__*/React.createElement(Tooltip, {
    kind: "warning",
    content: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("b", null, "Mixed Cabin"), /*#__PURE__*/React.createElement(Box, {
      pt: 1
    }, "The total fare shown is based on a lower cabin or fare category for one or more of the subsequent legs.")),
    placement: "top"
  }, /*#__PURE__*/React.createElement(Text$1, null, "Mixed Cabin")));
};

const SpecialBadge = styled.span`
  padding: 0 0.7em;
  display: inline-block;
  color: ${props => tinycolor.mix(getThemeProp('global.success--color')(props), 'black', 60).toString()};
  text-align: center;
  vertical-align: baseline;
  line-height: 1.8em;
  background-color: ${props => tinycolor.mix(getThemeProp('global.success--color')(props), 'white', 70).setAlpha(0.5).toString()};
  font-size: 11px;
`;
const Text = styled.span`
  font-style: italic;
  font-weight: 600;
  opacity: 0.75;
  letter-spacing: 0;
  text-transform: none;
`;
const SpecialRateBadge = _ref => {
  let {
    text,
    popup
  } = _ref;
  if (!text) {
    return null;
  }
  return /*#__PURE__*/React.createElement(SpecialBadge, {
    link: popup
  }, /*#__PURE__*/React.createElement(Text, null, text));
};
SpecialRateBadge.propTypes = {
  text: PropTypes.string,
  popup: PropTypes.string
};
SpecialRateBadge.defaultProps = {
  text: '',
  popup: ''
};

/* eslint-disable react/prop-types */
styled(Box)`
  text-align: left;
  width: 400px;
  border-radius: 10px;
  background: ${props => tinycolor.mix(getThemeProp('global.border--color')(props), 'white', 40).toString()};
  line-height: 1.2;
`;
const UpgradeButton = styled(Button)`
  font-size: 12px;
  font-weight: 600;
  border-width: 1px;
  border-color: ${getThemeProp('global.border--color')};
  box-shadow: 0px 3px 10px -4px rgba(0, 0, 0, 0.05);
  border-radius: 0 0 4px 4px;

  &:hover {
    color: ${getThemeProp('global.link--color')};
    background-color: ${getThemeProp('global.light--color')};
  }

  ${ButtonTextStyled} {
    padding-top: 7px;
    padding-bottom: 7px;
  }
`;
styled.i`
  font-size: 13px;
`;
styled(Box)`
  color: ${getThemeProp('global.success--color')};
  font-weight: 600;
`;
({
  fare: PropTypes.shape({}).isRequired,
  legNum: PropTypes.number.isRequired,
  onSelectFare: PropTypes.func.isRequired
});
const getUpgradeButtonText = _ref2 => {
  let {
    numberOfUpgrades,
    showShortText
  } = _ref2;
  if (showShortText) {
    return `+${numberOfUpgrades} Option${numberOfUpgrades >= 2 ? 's' : ''}`;
  }
  return `+${numberOfUpgrades} Fare Option${numberOfUpgrades >= 2 ? 's' : ''}`;
};
const FareDrawer = _ref3 => {
  let {
    fares,
    onFareDetails,
    columnBrandsLength
  } = _ref3;
  return /*#__PURE__*/React.createElement(UpgradeButton, {
    size: "small",
    onClick: onFareDetails,
    bordered: true,
    fullWidth: true,
    text: getUpgradeButtonText({
      numberOfUpgrades: fares.length - 1,
      showShortText: columnBrandsLength >= 6
    })
  });
};
FareDrawer.propTypes = {
  fares: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onFareDetails: PropTypes.func.isRequired,
  columnBrandsLength: PropTypes.number.isRequired
};

const CompareButton$1 = _ref => {
  let {
    numAdded,
    onClick
  } = _ref;
  return /*#__PURE__*/React.createElement(Button, {
    onClick: onClick,
    prefixIconLineName: numAdded ? 'Minus' : 'Plus',
    kind: numAdded ? 'danger' : 'success',
    size: "small",
    text: numAdded ? 'Remove' : 'Add'
  });
};
CompareButton$1.propTypes = {
  numAdded: PropTypes.number,
  onClick: PropTypes.func.isRequired
};
CompareButton$1.defaultProps = {
  numAdded: 0
};

/**
 * The primary purpose of this component is to allow <AdditionalFareInfo /> to conditional
 * add styles if contained within <SelectFareRegion />.
 */
const SelectFareRegion$1 = styled(Box)`
  text-align: center;
`;

/* 
Change Trip only:
exchange type "M" = manual exchange
exchange type "X" = buy & cancel
exchange type "A" = automated exchange
exchange type "F" or blank = buy & forfeit (no tooltip)
exchange type "R" = ???
*/

const getResCreditMessage = (exchangeType, creditAmount) => {
  const messages = {
    M: `After the change, you'll have a $${creditAmount} credit per passenger remaining with the airline.`,
    X: `If you select this option, you'll be buying a new ticket and keeping your current one as a credit.`,
    A: `After the change, you'll have a $${creditAmount} credit per passenger remaining with the airline.`
  };
  return messages[exchangeType];
};
const CreditText = styled.div`
  color: ${props => darken(getThemeProp('global.success--color')(props), 5)};
  border-bottom: ${_ref => {
  let {
    showTooltip
  } = _ref;
  return showTooltip ? '1px dotted' : 'unset';
}};
  cursor: ${_ref2 => {
  let {
    showTooltip
  } = _ref2;
  return showTooltip ? 'default' : 'unset';
}};

  ${SelectFareRegion$1} & {
    background: white;
    box-shadow: 0 0 0 4px white;
  }
  ${SelectFareRegion$1} & br {
    display: none;
  }

  b {
    font-weight: 800;
  }
`;
const CreditDisplay = styled(Flex)`
  line-height: 1.2;
  align-items: center;
  justify-content: center;
  min-height: 22px;

  ${media.XLargeOnly`
    min-height: 40px;
  `};

  ${SelectFareRegion$1} & {
    min-height: 28px;
  }
`;
const ResidualCredit = _ref3 => {
  let {
    isLastLeg,
    creditAmount,
    showCreditTooltip = false
  } = _ref3;
  return /*#__PURE__*/React.createElement(CreditText, {
    showTooltip: showCreditTooltip
  }, !isLastLeg && /*#__PURE__*/React.createElement(React.Fragment, null, "up to", ' ', /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, /*#__PURE__*/React.createElement("br", null))), /*#__PURE__*/React.createElement("b", null, "$", creditAmount), " credit");
};
ResidualCredit.propTypes = {
  isLastLeg: PropTypes.bool,
  creditAmount: PropTypes.number,
  showCreditTooltip: PropTypes.bool
};
const AdditionalFareInfo = _ref4 => {
  let {
    isChangeTrips,
    isOneWayFare,
    residualCredit,
    totalItinerary,
    exchangeType,
    tripType,
    isLastLeg
  } = _ref4;
  if (isChangeTrips) {
    const residualCreditFloor = Math.floor(residualCredit);
    const showCredit = residualCreditFloor > 0;
    const acceptedExchangeTypes = ['M', 'X', 'A'];
    const showCreditTooltip = acceptedExchangeTypes.includes(exchangeType);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, null, totalItinerary > 0 ? 'additional' : null, totalItinerary < 0 ? 'refund' : null, totalItinerary === 0 ? 'no charge' : null), showCredit && (showCreditTooltip ? /*#__PURE__*/React.createElement(CreditDisplay, {
      pt: 2
    }, /*#__PURE__*/React.createElement(Tooltip, {
      content: getResCreditMessage(exchangeType, residualCreditFloor)
    }, /*#__PURE__*/React.createElement(ResidualCredit, {
      isLastLeg: isLastLeg,
      creditAmount: residualCreditFloor,
      showCreditTooltip: true
    }))) : /*#__PURE__*/React.createElement(CreditDisplay, {
      pt: 2
    }, /*#__PURE__*/React.createElement(ResidualCredit, {
      isLastLeg: isLastLeg,
      creditAmount: residualCreditFloor
    }))));
  }
  if (isOneWayFare) {
    return tripType === TRIP_TYPE.oneWay ? 'total' : `${formatFare(Math.ceil(totalItinerary))} total`;
  }
  if (tripType === TRIP_TYPE.roundTrip) {
    return 'roundtrip';
  }
  return 'total';
};
AdditionalFareInfo.propTypes = {
  isChangeTrips: PropTypes.bool.isRequired,
  isOneWayFare: PropTypes.bool.isRequired,
  totalItinerary: PropTypes.number.isRequired,
  tripType: PropTypes.oneOf(Object.values(TRIP_TYPE)).isRequired,
  residualCredit: PropTypes.number,
  exchangeType: PropTypes.string,
  isLastLeg: PropTypes.bool.isRequired
};

const CompareRegion = styled(animated.div)`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const CompareButtonRegion = styled(Box)`
  position: absolute;
  visibility: visible;
  bottom: 4px;

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    top: 8px;
    right: 0;
    left: 0;
  }
`;
const SpecialRateWrapper$1 = styled.div`
  position: absolute;
  top: -12px;
  left: 0;
  width: 100%;
`;
const OutOfPolicyWrapper$2 = styled.div`
  padding-top: 5px;
`;
const PriceBox = styled(Box)`
  ${_ref => {
  let {
    airportOnly,
    isLoading
  } = _ref;
  return airportOnly ? css`
          color: gray;
        ` : css`
          cursor: pointer;
          border: 1px solid ${isLoading ? 'transparent' : getThemeProp('global.border--color')};
          border-radius: 4px;
          padding: 16px 0;
          min-height: 74px;
          transition: all 0.15s;

          &:hover {
            background-color: ${getThemeProp('global.light--color')};
            border-color: ${getThemeProp('global.link--color')};
          }
        `;
}};
  ${_ref2 => {
  let {
    hasDrawerFares
  } = _ref2;
  return hasDrawerFares && css`
      border-radius: 4px 4px 0 0;
      border-bottom: 0;

      &:hover {
        + ${UpgradeButton} {
          border-top: 1px solid ${getThemeProp('global.link--color')};
        }
      }
    `;
}};
`;
const MainFare$1 = styled.span`
  font-weight: 700;
  font-size: 1.375rem;
  color: ${_ref3 => {
  let {
    showingDiff,
    airportOnly
  } = _ref3;
  if (airportOnly) {
    return 'gray';
  }
  return getThemeProp(showingDiff ? 'global.secondary--color' : 'global.link--color');
}};
  ${_ref4 => {
  let {
    isLoading
  } = _ref4;
  return skeletonLoadingStyles(isLoading);
}}
`;
const BoxSoldOut = styled(Box)`
  font-size: 14px;
  line-height: 1.4;
  opacity: 0.75;
  max-width: 100px;
  width: 100%;
  margin: 0 auto;
  padding-top: ${SPACE.size16};
`;
const BoxSummaryFare = styled(Box)`
  font-size: 12px;
  visibility: ${_ref5 => {
  let {
    isLoading
  } = _ref5;
  return isLoading ? 'hidden' : 'unset';
}};
`;
const FareBrand = styled(Flex)`
  font-size: 0.6875rem;
  font-weight: 600;
  width: 100%;
  justify-content: center;
  cursor: default;
  ${_ref6 => {
  let {
    isLoading
  } = _ref6;
  return skeletonLoadingStyles(isLoading);
}};
`;
const FareBrandText = styled.div`
  opacity: 0.75;
  max-width: 108px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const FareBrandLink = styled(FareBrandText)`
  cursor: pointer;
  color: ${getThemeProp('global.link--color')};
  opacity: 1;
  border-bottom: 1px solid ${_ref7 => {
  let {
    isLoading
  } = _ref7;
  return isLoading ? 'transparent' : getThemeProp('global.link--color');
}};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ${_ref8 => {
  let {
    isLoading
  } = _ref8;
  return isLoading ? 'clip' : 'ellipsis';
}};
  max-width: ${_ref9 => {
  let {
    isOutOfPolicy
  } = _ref9;
  return isOutOfPolicy ? '80px' : '100px';
}};
  margin-left: ${_ref10 => {
  let {
    isOutOfPolicy
  } = _ref10;
  return isOutOfPolicy ? '19px' : '0';
}};

  &:hover {
    transition: opacity 0.3s;
    border-bottom: 1px solid ${getThemeProp('global.link--color')};
  }
`;
const BrandWrapper = styled(Flex)`
  line-height: 1.2;
  flex: 1 0 auto;
  justify-content: center;
`;
const FlexFares$1 = styled(BoxFareSpacer)`
  text-align: center;
  position: relative;
`;
const FareGroup = _ref11 => {
  let {
    position,
    fares,
    legNum,
    columnBrandsLength,
    onSelectFare,
    lastFareOption,
    isLoading,
    isCompare,
    airportOnly,
    referencePrice,
    onPriceHover,
    resetPriceHover,
    isOneWayFare,
    tripType,
    comparisonHandler,
    comparisonIds,
    onFareDetails,
    showNameHint,
    onBrandNameHover,
    resetBrandName,
    isChangeTrips,
    isLastLeg
  } = _ref11;
  const [fare, ...additionalFares] = fares;
  const brandDetails = fare.flightBrand;
  const drawerFares = additionalFares.filter(f => !f.soldOut);
  const selectHandler = airportOnly ? () => {} : () => onSelectFare({
    fare,
    legNum
  });
  const onHoverHandler = () => onPriceHover(position);
  const roundedFare = Math.ceil(fare.totalFare);
  const priceDiff = roundedFare - Math.ceil(referencePrice);
  const compareIsActive = isCompare && !fare.soldOut;
  const compareProps = useSpring({
    opacity: compareIsActive ? '100%' : '0%',
    height: compareIsActive ? '30px' : '0px'
  });
  const noDiff = referencePrice === 0 || priceDiff === 0 || isChangeTrips;
  const totalFare = noDiff ? formatFare(roundedFare) : formatFare(priceDiff, true);
  const compareClickHandler = () => comparisonHandler(fares);
  const numCompared = comparisonIds.includes(fare.fareId) ? fares.length : 0;
  return /*#__PURE__*/React.createElement(FlexFares$1, {
    withRightBorder: columnBrandsLength <= 3 && lastFareOption,
    columnCount: columnBrandsLength,
    height: "100%",
    px: "12px",
    pt: "8px",
    pb: "24px",
    alignItems: "center",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(SpecialRateWrapper$1, null, !isLoading && /*#__PURE__*/React.createElement(SpecialRateBadge, {
    text: fare.specialRateText,
    popup: fare.specialRatePopup
  })), /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    justifyContent: "flex-start",
    height: "100%"
  }, /*#__PURE__*/React.createElement(CompareRegion, {
    style: compareProps
  }, /*#__PURE__*/React.createElement(CompareButtonRegion, null, /*#__PURE__*/React.createElement(CompareButton$1, {
    onClick: compareClickHandler,
    numAdded: numCompared
  }))), /*#__PURE__*/React.createElement(Flex, {
    height: "34px",
    alignItems: "center",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(BrandWrapper, {
    isLoading: isLoading
  }, /*#__PURE__*/React.createElement(FareBrand, {
    isLoading: isLoading,
    onMouseEnter: onBrandNameHover,
    onMouseLeave: resetBrandName
  }, fare.soldOut ? /*#__PURE__*/React.createElement(FareBrandText, null, brandDetails.brandName) : /*#__PURE__*/React.createElement(FareBrandLink, {
    isLoading: isLoading,
    onClick: () => onFareDetails(),
    isOutOfPolicy: fare.outOfPolicy
  }, showNameHint ? 'Compare Fare' : brandDetails.brandName))), !isLoading && fare.outOfPolicy && /*#__PURE__*/React.createElement(OutOfPolicyWrapper$2, null, /*#__PURE__*/React.createElement(OutOfPolicyIndicator, {
    additionalText: fare.additionalOutOfPolicyText,
    details: fare.policyBreakDetails
  }))), fare.soldOut && !isLoading ? /*#__PURE__*/React.createElement(BoxSoldOut, null, fare.notOfferedReason || 'Sold Out') : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PriceBox, {
    airportOnly: airportOnly,
    onClick: selectHandler,
    onMouseEnter: onHoverHandler,
    onMouseLeave: resetPriceHover,
    hasDrawerFares: drawerFares.length > 0,
    isLoading: isLoading
  }, /*#__PURE__*/React.createElement(MainFare$1, {
    isLoading: isLoading,
    showingDiff: !noDiff,
    airportOnly: airportOnly
  }, /*#__PURE__*/React.createElement("span", null, isChangeTrips ? `${formatFare(Math.ceil(fare.totalItinerary), fare.totalItinerary !== 0)}` : totalFare)), /*#__PURE__*/React.createElement(BoxSummaryFare, {
    isLoading: isLoading,
    pt: "6px"
  }, /*#__PURE__*/React.createElement(AdditionalFareInfo, {
    isChangeTrips: isChangeTrips,
    isOneWayFare: isOneWayFare,
    residualCredit: fare.residualCredit,
    totalItinerary: fare.totalItinerary,
    exchangeType: fare.exchangeType,
    tripType: tripType,
    isLastLeg: isLastLeg
  }))), !airportOnly && drawerFares.length && !isLoading ? /*#__PURE__*/React.createElement(FareDrawer, {
    columnBrandsLength: columnBrandsLength,
    onFareDetails: () => onFareDetails(fares),
    legNum: legNum,
    fares: [fare, ...drawerFares]
  }) : null, !isLoading && fare.downgradeRequired && /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(MixedCabinIndicator, null)))));
};
FareGroup.propTypes = {
  fares: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  legNum: PropTypes.number.isRequired,
  airportOnly: PropTypes.bool.isRequired,
  columnBrandsLength: PropTypes.number.isRequired,
  position: PropTypes.number.isRequired,
  referencePrice: PropTypes.number.isRequired,
  onSelectFare: PropTypes.func.isRequired,
  onPriceHover: PropTypes.func.isRequired,
  resetPriceHover: PropTypes.func.isRequired,
  lastFareOption: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isCompare: PropTypes.bool.isRequired,
  isOneWayFare: PropTypes.bool.isRequired,
  tripType: PropTypes.string.isRequired,
  comparisonHandler: PropTypes.func.isRequired,
  comparisonIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onFareDetails: PropTypes.func.isRequired,
  onBrandNameHover: PropTypes.func.isRequired,
  resetBrandName: PropTypes.func.isRequired,
  showNameHint: PropTypes.bool.isRequired,
  isChangeTrips: PropTypes.bool,
  isLastLeg: PropTypes.bool.isRequired
};

/* eslint-disable react/no-array-index-key */
const FareButton = styled(Flex)`
  position: relative;
  background: ${getTokenValue(THEME_TOKENS.base.color.neutral0)};
  border-radius: 8px;
  min-height: 118px;
  width: 130px;
  border: 1px solid ${getTokenValue(THEME_TOKENS.base.color.infoBorder)};
  flex-direction: column;
  opacity: ${_ref => {
  let {
    disabled
  } = _ref;
  return disabled ? '0.5' : '1';
}};
  text-align: center;
  justify-content: space-between;
  ${_ref2 => {
  let {
    isLoading
  } = _ref2;
  return isLoading && css`
      filter: grayscale(60%);
    `;
}};
  cursor: ${_ref3 => {
  let {
    disabled
  } = _ref3;
  return disabled ? 'default' : 'pointer';
}};
`;
const Brand$1 = styled(Box)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  color: ${props => props.disabled ? 'unset' : getThemeProp('global.link--color')};
  font-size: 11px;
  visibility: ${_ref4 => {
  let {
    isLoading
  } = _ref4;
  return isLoading ? 'hidden' : 'unset';
}};
`;
const MainFare = styled.span`
  ${_ref5 => {
  let {
    isLoading
  } = _ref5;
  return skeletonLoadingStyles(isLoading);
}}
`;
const FareAmount$1 = styled.b`
  color: ${props => props.disabled ? 'unset' : getThemeProp('global.link--color')};
  font-size: 20px;
`;
const AmenitiyRegion = styled(Flex)`
  background: ${getTokenValue(THEME_TOKENS.card.color.background)};
  height: 30px;
  border-radius: 0 0 8px 8px;
`;
const SpecialRateWrapper = styled.div`
  position: absolute;
  top: -7px;
  left: 0;
  right: 0;
`;
const OutOfPolicyWrapper$1 = styled.div`
  position: absolute;
  top: 2px;
  right: 1px;
  transform: scale(0.8);
`;
const FareNotOffered = styled.div`
  font-size: 11px;
  text-align: center;
  padding: 0px 8px;
  line-height: 1.2;
  font-weight: bold;
`;
const AdditionalFareInfoContainer = styled(Box)`
  font-size: 12px;
  visibility: ${_ref6 => {
  let {
    isLoading
  } = _ref6;
  return isLoading ? 'hidden' : 'unset';
}};
`;
const FareGroupScrollable = _ref7 => {
  let {
    fares,
    airportOnly,
    isLoading,
    activeLeg,
    onSelectFare,
    legNum,
    tripType,
    isOneWayFare,
    isChangeTrips,
    isLastLeg
  } = _ref7;
  return /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    mb: 3,
    mt: [3, 3, 3, 4],
    pl: 3,
    pr: 5
  }, fares.flat().map((fare, idx) => {
    var _fare$amenities, _fare$amenities2, _fare$amenities3;
    const brandDetails = fare.flightBrand;
    const selectHandler = () => onSelectFare({
      fare,
      legNum
    });
    return /*#__PURE__*/React.createElement(FareButton, {
      onClick: fare.soldOut || airportOnly ? null : selectHandler,
      isLoading: isLoading,
      key: `${idx}-${fare.brandIndex}`,
      disabled: fare.soldOut || airportOnly,
      mr: 2
    }, !isLoading && /*#__PURE__*/React.createElement(SpecialRateWrapper, null, /*#__PURE__*/React.createElement(SpecialRateBadge, {
      text: fare.specialRateText,
      popup: fare.specialRatePopup
    })), !isLoading && fare.outOfPolicy && /*#__PURE__*/React.createElement(OutOfPolicyWrapper$1, null, /*#__PURE__*/React.createElement(OutOfPolicyIcon, null)), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Brand$1, {
      disabled: fare.soldOut || airportOnly,
      isLoading: isLoading,
      px: 2,
      pt: 3,
      pb: 1
    }, brandDetails.brandName), /*#__PURE__*/React.createElement(MainFare, {
      isLoading: isLoading
    }, /*#__PURE__*/React.createElement("span", null, fare.soldOut ? /*#__PURE__*/React.createElement(FareNotOffered, null, fare.notOfferedReason || 'Sold Out') : /*#__PURE__*/React.createElement(FareAmount$1, {
      disabled: airportOnly
    }, isChangeTrips ? `${formatFare(Math.ceil(fare.totalItinerary), fare.totalItinerary !== 0)}` : `${formatFare(Math.ceil(fare.totalFare))}`))), !fare.soldOut && !airportOnly ? /*#__PURE__*/React.createElement(AdditionalFareInfoContainer, {
      isLoading: isLoading,
      pt: 2,
      pb: 2
    }, /*#__PURE__*/React.createElement(AdditionalFareInfo, {
      isChangeTrips: isChangeTrips,
      isOneWayFare: isOneWayFare,
      residualCredit: fare.residualCredit,
      totalItinerary: fare.totalItinerary,
      exchangeType: "" // don't show on mobile for now
      ,
      tripType: tripType,
      isLastLeg: isLastLeg
    })) : null), /*#__PURE__*/React.createElement(AmenitiyRegion, {
      alignItems: "center",
      justifyContent: "center"
    }, !isLoading && hasCarryOnBagFee(brandDetails === null || brandDetails === void 0 ? void 0 : brandDetails.carryOnBag) ? /*#__PURE__*/React.createElement(AmenityNoCarryOn, {
      fee: brandDetails === null || brandDetails === void 0 ? void 0 : brandDetails.carryOnBag,
      suppressTooltip: true
    }) : null, !isLoading && /*#__PURE__*/React.createElement(AmenityWifi$1, {
      suppressTooltip: true,
      wifiStatus: fare !== null && fare !== void 0 && (_fare$amenities = fare.amenities) !== null && _fare$amenities !== void 0 && (_fare$amenities = _fare$amenities.segmentAmenities) !== null && _fare$amenities !== void 0 && (_fare$amenities = _fare$amenities[activeLeg]) !== null && _fare$amenities !== void 0 && _fare$amenities.wifiStatus ? fare.amenities.segmentAmenities[activeLeg].wifiStatus : '',
      wifiText: fare !== null && fare !== void 0 && (_fare$amenities2 = fare.amenities) !== null && _fare$amenities2 !== void 0 && (_fare$amenities2 = _fare$amenities2.segmentAmenities) !== null && _fare$amenities2 !== void 0 && (_fare$amenities2 = _fare$amenities2[activeLeg]) !== null && _fare$amenities2 !== void 0 && _fare$amenities2.wifiText ? fare.amenities.segmentAmenities[activeLeg].wifiText : ''
    }), (fare === null || fare === void 0 ? void 0 : fare.amenities) && !isLoading && /*#__PURE__*/React.createElement(Amenities, {
      suppressTooltip: true,
      amenities: fare !== null && fare !== void 0 && (_fare$amenities3 = fare.amenities) !== null && _fare$amenities3 !== void 0 && _fare$amenities3.segmentAmenities[activeLeg] ? fare.amenities.segmentAmenities[activeLeg] : []
    })));
  }));
};
FareGroupScrollable.propTypes = {
  fares: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))).isRequired,
  onSelectFare: PropTypes.func.isRequired,
  legNum: PropTypes.number.isRequired,
  activeLeg: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  airportOnly: PropTypes.bool.isRequired,
  tripType: PropTypes.oneOf([TRIP_TYPE.roundTrip, TRIP_TYPE.oneWay, TRIP_TYPE.multiCity]),
  isOneWayFare: PropTypes.bool.isRequired,
  isChangeTrips: PropTypes.bool,
  isLastLeg: PropTypes.bool.isRequired
};
FareGroupScrollable.defaultProps = {};

const FlexFares = styled(BoxFareSpacer)`
  text-align: center;
  position: relative;
`;
const FareCellContainer = styled(Flex)`
  border-left: 1px solid ${getThemeProp('global.border--color')};
`;
const NoFare = styled.div`
  height: 2px;
  width: 50%;
  background: ${getThemeProp('global.border--color')};
  margin: 0 auto;
`;
const ScrollableRegion = styled(ScrollArea)`
  /* Flights with stops have tabs for each leg which can get in the way so margin is added to account for it */
  margin-bottom: ${_ref => {
  let {
    $isNonstop
  } = _ref;
  return $isNonstop ? '0' : '36px';
}};
  margin-top: ${_ref2 => {
  let {
    $isNonstop
  } = _ref2;
  return $isNonstop ? '0' : '16px';
}};
`;
const NoFares = /*#__PURE__*/memo(_ref3 => {
  let {
    columnBrandsLength
  } = _ref3;
  return /*#__PURE__*/React.createElement(FlexFares, {
    columnCount: columnBrandsLength,
    height: "100%",
    p: 2,
    alignItems: "center",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    justifyContent: "center",
    height: "100%"
  }, /*#__PURE__*/React.createElement(NoFare, null)));
});
NoFares.propTypes = {
  columnBrandsLength: PropTypes.number.isRequired
};
const Fares = _ref4 => {
  let {
    fares,
    legNum,
    columnBrandsLength,
    onSelectFare,
    isLoading,
    isCompare,
    isOneWayFare,
    tripType,
    isNonstop,
    activeLeg,
    airportOnly,
    comparisonHandler,
    comparisonIds,
    onFareDetails,
    isChangeTrips,
    isLastLeg
  } = _ref4;
  const [refPrice, updateRefPrice] = useState(0);
  const resetPriceHoverData = () => updateRefPrice(0);
  const onPriceHover = x => updateRefPrice(fares[x][0].baseFare);
  const [showNameHint, updateShowNameHint] = useState(false);
  const activateNameHint = () => updateShowNameHint(true);
  const deactivateNameHint = () => updateShowNameHint(false);
  const fareCells = fares.map((fareGroup, i) => {
    if (fareGroup.length === 0) {
      // eslint-disable-next-line react/no-array-index-key
      return /*#__PURE__*/React.createElement(NoFares, {
        key: randomString(),
        columnBrandsLength: columnBrandsLength
      });
    }
    return /*#__PURE__*/React.createElement(FareGroup, {
      key: fareGroup[0].fareId,
      fares: fareGroup,
      airportOnly: airportOnly,
      referencePrice: refPrice,
      position: i,
      legNum: legNum,
      columnBrandsLength: columnBrandsLength,
      onSelectFare: onSelectFare,
      onPriceHover: onPriceHover,
      resetPriceHover: resetPriceHoverData,
      lastFareOption: i === columnBrandsLength - 1,
      isLoading: isLoading,
      isCompare: isCompare,
      isOneWayFare: isOneWayFare,
      tripType: tripType,
      comparisonHandler: comparisonHandler,
      comparisonIds: comparisonIds,
      onFareDetails: onFareDetails,
      showNameHint: showNameHint,
      onBrandNameHover: activateNameHint,
      resetBrandName: deactivateNameHint,
      isChangeTrips: isChangeTrips,
      isLastLeg: isLastLeg
    });
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.LargeAndLess, null, /*#__PURE__*/React.createElement(ScrollableRegion, {
    $isNonstop: isNonstop
  }, /*#__PURE__*/React.createElement(FareGroupScrollable, {
    onSelectFare: onSelectFare,
    legNum: legNum,
    isLoading: isLoading,
    activeLeg: activeLeg,
    fares: fares,
    airportOnly: airportOnly,
    tripType: tripType,
    isOneWayFare: isOneWayFare,
    isChangeTrips: isChangeTrips,
    isLastLeg: isLastLeg
  }))), /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, /*#__PURE__*/React.createElement(FareCellContainer, null, fareCells)));
};
Fares.propTypes = {
  fares: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({
    baseFare: PropTypes.number
  }))).isRequired,
  legNum: PropTypes.number.isRequired,
  columnBrandsLength: PropTypes.number.isRequired,
  onSelectFare: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isCompare: PropTypes.bool.isRequired,
  isOneWayFare: PropTypes.bool.isRequired,
  tripType: PropTypes.string.isRequired,
  isNonstop: PropTypes.bool.isRequired,
  activeLeg: PropTypes.number.isRequired,
  comparisonHandler: PropTypes.func.isRequired,
  comparisonIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onFareDetails: PropTypes.func.isRequired,
  airportOnly: PropTypes.bool.isRequired,
  isChangeTrips: PropTypes.bool,
  isLastLeg: PropTypes.bool.isRequired
};

const BoxWrapper = styled(Box)`
  border-top: 1px solid ${getThemeProp('global.border--color')};
  visibility: ${_ref => {
  let {
    isLoading
  } = _ref;
  return isLoading ? 'hidden' : 'visible';
}};
  width: calc(100vw - 16px);

  ${media.Medium`
    width: auto;
  `}
`;
const SegmentText = styled.span`
  font-size: 12px;
`;
const Codeshare = _ref2 => {
  let {
    segments,
    isLoading
  } = _ref2;
  if (segments.length < 1) return null;
  const codeshareArr = segments.filter(leg => leg.opAirlineCode !== leg.airlineCode);
  return /*#__PURE__*/React.createElement(BoxWrapper, {
    isLoading: isLoading,
    py: 1,
    px: SPACE.size24
  }, codeshareArr.map((leg, idx) => leg.opAirlineCode !== leg.airlineCode ? /*#__PURE__*/React.createElement(SegmentText, {
    key: `${leg.fromCode} – ${leg.toCode}`
  }, segments.length === 1 ? /*#__PURE__*/React.createElement("i", null, "Operated by ", leg.opAirlineName) : /*#__PURE__*/React.createElement(React.Fragment, null, leg.fromCode, " \u2013 ", leg.toCode, " ", /*#__PURE__*/React.createElement("i", null, "operated by ", leg.opAirlineName)), idx < codeshareArr.length - 1 ? ', ' : null) : null));
};
Codeshare.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool
};
Codeshare.defaultProps = {
  segments: [{}],
  isLoading: false
};

const TooltipTarget = styled.span`
  cursor: default;
  border-bottom: 1px dotted rgba(0, 0, 0, 0.4);

  &:hover {
    opacity: 1;
    transition: opacity 0.3s;
    border-bottom: 1px solid;
  }
`;
const FlightNumber = _ref => {
  let {
    segment
  } = _ref;
  return /*#__PURE__*/React.createElement(Tooltip, {
    maxWidth: "unset",
    kind: "info",
    placement: "top",
    content: /*#__PURE__*/React.createElement(SegmentDetails, {
      segment: segment
    })
  }, /*#__PURE__*/React.createElement(TooltipTarget, null, segment.flightNumber));
};
FlightNumber.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  segment: PropTypes.object.isRequired
};

/* eslint-disable react/require-default-props */
const BADGE_TYPE = {
  covid: 'COVID',
  good: 'GOOD',
  green: 'GREEN',
  bad: 'BAD',
  preferred: 'PREFERRED',
  fastest: 'FASTEST',
  airportOnly: 'AIRPORT_ONLY'
};
const getBadgeColor = _ref => {
  let {
    badgeType
  } = _ref;
  switch (badgeType) {
    case BADGE_TYPE.covid:
      return '#752533';
    case BADGE_TYPE.good:
      return '#573496';
    case BADGE_TYPE.green:
      return '#2e4b29';
    case BADGE_TYPE.bad:
      return '#8e0000';
    default:
      return 'unset';
  }
};
const getBackgroundBadgeColor = _ref2 => {
  let {
    badgeType
  } = _ref2;
  switch (badgeType) {
    case BADGE_TYPE.covid:
      return '#f3c0c9';
    case BADGE_TYPE.good:
      return '#dfc6ec';
    case BADGE_TYPE.green:
      return '#c1f2ab';
    case BADGE_TYPE.bad:
      return '#ffc2a4';
    default:
      return 'unset';
  }
};
const BadgeWrapper = styled.div`
  display: flex;

  ${media.Medium`
    align-items: flex-start;
    `}
`;
const Underline = styled.span`
  border-bottom: 1px dotted;
`;

// Only set background and color if kind is provided.
const OtherBadgeStyles = styled(Badge)`
  ${_ref3 => {
  let {
    important
  } = _ref3;
  return important && css`
      margin-right: 8px;
    `;
}}

  ${_ref4 => {
  let {
    kind
  } = _ref4;
  return !kind && css`
      background: ${getBackgroundBadgeColor};
      color: ${getBadgeColor};
    `;
}}

  ${_ref5 => {
  let {
    hasLink
  } = _ref5;
  return hasLink && css`
      @media (hover: hover) {
        &:hover {
          cursor: pointer;
          ${_ref6 => {
    let {
      kind
    } = _ref6;
    return !kind && css`
              background: ${getBackgroundBadgeColor};
              color: ${getBadgeColor};
            `;
  }}
        }
      }
    `;
}}
`;
const getBadgeIcon = badgeType => {
  switch (badgeType) {
    case BADGE_TYPE.covid:
      return /*#__PURE__*/React.createElement(IconCoronavirus, null);
    case BADGE_TYPE.good:
      return /*#__PURE__*/React.createElement(IconPlane, null);
    case BADGE_TYPE.green:
      return /*#__PURE__*/React.createElement(IconEco, null);
    case BADGE_TYPE.bad:
      return /*#__PURE__*/React.createElement(IconWarning, null);
    case BADGE_TYPE.fastest:
      return /*#__PURE__*/React.createElement(IconBolt, null);
    case BADGE_TYPE.preferred:
      return /*#__PURE__*/React.createElement(IconCheckCircleLine, null);
    case BADGE_TYPE.airportOnly:
      return /*#__PURE__*/React.createElement(IconWarning, null);
    default:
      return null;
  }
};
const CustomBadge = _ref7 => {
  let {
    title,
    badgeType,
    onClick = null,
    hasMoreInfo = false,
    details = '',
    kind = null,
    important = false
  } = _ref7;
  const MyBadge = /*#__PURE__*/React.createElement(OtherBadgeStyles, {
    important: important,
    badgeType: badgeType,
    hasLink: hasMoreInfo || details,
    noBorderRadius: true,
    onClick: onClick,
    kind: kind
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, getBadgeIcon(badgeType), /*#__PURE__*/React.createElement(Box, {
    pl: 1
  }, details ? /*#__PURE__*/React.createElement(Underline, null, title) : /*#__PURE__*/React.createElement(React.Fragment, null, title)), hasMoreInfo && /*#__PURE__*/React.createElement(Flex, {
    pl: 1
  }, /*#__PURE__*/React.createElement(IconNext, null))));
  if (details) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: details
    }, MyBadge);
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, MyBadge);
};
CustomBadge.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  details: PropTypes.string,
  kind: PropTypes.string,
  hasMoreInfo: PropTypes.bool,
  badgeType: PropTypes.string.isRequired,
  important: PropTypes.bool
};
const OtherBadges = _ref8 => {
  let {
    badges,
    cmsPostClickHandler,
    suppressEcoBadge
  } = _ref8;
  return /*#__PURE__*/React.createElement(React.Fragment, null, badges.map(badge => /*#__PURE__*/React.createElement(React.Fragment, {
    key: badge.id
  }, (badge.id === BADGE_TYPE.covid || badge.id === BADGE_TYPE.good || badge.id === BADGE_TYPE.bad || badge.id === BADGE_TYPE.green && !suppressEcoBadge) && /*#__PURE__*/React.createElement(CustomBadge, {
    details: badge.details,
    title: badge.label,
    badgeType: badge.id,
    hasMoreInfo: !!(cmsPostClickHandler && badge.url),
    onClick: cmsPostClickHandler && badge.url ? () => cmsPostClickHandler('flight_announcements', badge.url) : null
  }))));
};
OtherBadges.propTypes = {
  badges: PropTypes.arrayOf(PropTypes.shape({
    caption: PropTypes.string
  })),
  cmsPostClickHandler: PropTypes.func,
  suppressEcoBadge: PropTypes.bool
};
OtherBadges.defaultProps = {
  badges: [],
  cmsPostClickHandler: () => {},
  suppressEcoBadge: false
};
const NoOnlineBookingBadge = () => /*#__PURE__*/React.createElement(CustomBadge, {
  important: true,
  title: "No Online Booking",
  badgeType: BADGE_TYPE.airportOnly,
  kind: "danger",
  details: "This flight departs soon. Please contact our customer service team or the airline directly to book a seat."
});
const FlightBadges = _ref9 => {
  var _otherBadgesArr$filte;
  let {
    airportOnly,
    isPreferred,
    isFastest,
    otherBadges,
    cmsPostClickHandler,
    suppressEcoBadge
  } = _ref9;
  /**
   * Limit the display to 3 badges, regardless of how many are returned by otherBadges.
   * Insert "Preferred" at the beginning of the list (so it will always show) and "Fastest"
   * at the end (so it might not show).
   */
  const numberOfCustomBadgeToShow = isPreferred ? 2 : 3;
  const otherBadgesArr = (otherBadges === null || otherBadges === void 0 ? void 0 : otherBadges.slice(0, numberOfCustomBadgeToShow)) || [];
  const hasBadBadge = otherBadgesArr === null || otherBadgesArr === void 0 || (_otherBadgesArr$filte = otherBadgesArr.filter(b => b.id === BADGE_TYPE.bad)) === null || _otherBadgesArr$filte === void 0 ? void 0 : _otherBadgesArr$filte.length;
  const showFastestBadge = otherBadgesArr.length + Number(isPreferred) <= 2 && isFastest && !hasBadBadge;
  return /*#__PURE__*/React.createElement(BadgeWrapper, null, airportOnly && /*#__PURE__*/React.createElement(NoOnlineBookingBadge, null), isPreferred && /*#__PURE__*/React.createElement(CustomBadge, {
    title: "Preferred",
    badgeType: BADGE_TYPE.preferred,
    kind: "success"
  }), otherBadgesArr.length > 0 && /*#__PURE__*/React.createElement(OtherBadges, {
    cmsPostClickHandler: cmsPostClickHandler,
    suppressEcoBadge: suppressEcoBadge,
    badges: otherBadgesArr
  }), showFastestBadge && /*#__PURE__*/React.createElement(CustomBadge, {
    title: "Fastest",
    badgeType: BADGE_TYPE.fastest,
    kind: "primary"
  }));
};
FlightBadges.propTypes = {
  airportOnly: PropTypes.bool.isRequired,
  isPreferred: PropTypes.bool.isRequired,
  isFastest: PropTypes.bool.isRequired,
  otherBadges: PropTypes.arrayOf(PropTypes.shape({})),
  cmsPostClickHandler: PropTypes.func,
  /** This is so we don't show eco badge in the compare fares modal */
  suppressEcoBadge: PropTypes.bool
};
FlightBadges.defaultProps = {
  otherBadges: [],
  cmsPostClickHandler: null,
  suppressEcoBadge: false
};

const RouteFromWrapper = styled(Box)`
  flex: 0 0 108px;
  position: relative;
`;
const TrainIconWrapper = styled.div`
  font-size: ${_ref => {
  let {
    compact
  } = _ref;
  return compact ? '16px' : '24px';
}};
  opacity: 0.75;
  position: absolute;
  right: ${_ref2 => {
  let {
    compact
  } = _ref2;
  return compact ? '28px' : '20px';
}};
  bottom: ${_ref3 => {
  let {
    compact
  } = _ref3;
  return compact ? '23px' : '24px';
}};
`;
const RouteToWrapper = styled(Box)`
  flex: 0 0 108px;
`;
const Route = _ref4 => {
  let {
    fromCode,
    fromName,
    toCode,
    toName,
    departsDate,
    arrivesDate,
    dayChange,
    compact,
    isLoading,
    isDepartingArrivalMismatch,
    primaryTime
  } = _ref4;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RouteFromWrapper, {
    pr: 4,
    "data-temp": compact
  }, /*#__PURE__*/React.createElement(AirportCode, {
    isDepartingArrivalMismatch: isDepartingArrivalMismatch,
    small: true,
    isLoading: isLoading,
    code: fromCode,
    name: fromName
  }), /*#__PURE__*/React.createElement(Box, {
    pt: 1
  }, /*#__PURE__*/React.createElement(RouteTime$1, {
    isLoading: isLoading,
    date: departsDate,
    primary: primaryTime
  })), isTrainStation(fromCode) && !isLoading && /*#__PURE__*/React.createElement(TrainIconWrapper, {
    compact: compact
  }, /*#__PURE__*/React.createElement(IconTrain, null))), /*#__PURE__*/React.createElement(RouteToWrapper, {
    pr: 4
  }, /*#__PURE__*/React.createElement(AirportCode, {
    small: true,
    isLoading: isLoading,
    code: toCode,
    name: toName
  }), /*#__PURE__*/React.createElement(Box, {
    pt: 1
  }, /*#__PURE__*/React.createElement(RouteTime$1, {
    isLoading: isLoading,
    date: arrivesDate,
    showDayShort: dayChange !== 0 && compact,
    dayChange: dayChange,
    showDay: dayChange !== 0 && !compact,
    primary: primaryTime
  }))));
};
Route.propTypes = {
  /**
   * The three letter airport code for the point of origin for this leg.
   */
  fromCode: PropTypes.string.isRequired,
  /**
   * The name of the origin city or airport for this leg
   */
  fromName: PropTypes.string,
  /**
   * The three letter airport code for the destination point for this leg.
   */
  toCode: PropTypes.string.isRequired,
  /**
   * The name of the destination city or airport for this leg.
   */
  toName: PropTypes.string,
  /**
   * The departure date and time.
   */
  departsDate: PropTypes.instanceOf(Date).isRequired,
  /**
   * The arrival date and time (Note: the arrival may not be the same day as the departure).
   */
  arrivesDate: PropTypes.instanceOf(Date).isRequired,
  /**
   * The # of days between the arrival date and departure date; usually 0, but sometimes
   * can be 1, 2, or -1.
   */
  dayChange: PropTypes.number,
  /**
   *
   */
  compact: PropTypes.bool,
  isLoading: PropTypes.bool,
  isDepartingArrivalMismatch: PropTypes.bool,
  /**
   * Show the time using the primaryText color
   */
  primaryTime: PropTypes.bool
};
Route.defaultProps = {
  fromName: '',
  toName: '',
  dayChange: '',
  compact: false,
  isLoading: false,
  isDepartingArrivalMismatch: false,
  primaryTime: false
};

const SubheaderWrapper = styled(Flex)`
  height: 22px;
  align-items: center;
  width: 100%;
`;
const Subheader = styled(Box)`
  position: relative;
  z-index: ${ZINDEX.FIXED};
  width: 100%;

  &:after {
    z-index: ${ZINDEX.DROPDOWN};
    content: '';
    border-top: 1px solid ${getThemeProp('global.border--color')};
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
  }
`;
const DetailSubheaderTextRegion = styled.span`
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  font-weight: 600;
  z-index: ${ZINDEX.FIXED};
  position: relative;
  display: inline-block;
  background: white;
  padding-left: 6px;
  padding-right: 6px;
  margin-left: -6px;
`;
const DetailSubheaderText = styled.span`
  opacity: 0.6;
`;
const DetailSubheaderButton = styled.div`
  z-index: ${ZINDEX.FIXED};
`;
const DetailSubheader = _ref => {
  let {
    text,
    horizontalPadding,
    button
  } = _ref;
  return /*#__PURE__*/React.createElement(SubheaderWrapper, null, /*#__PURE__*/React.createElement(Subheader, null, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    justifyContent: "space-between",
    px: horizontalPadding
  }, /*#__PURE__*/React.createElement(DetailSubheaderTextRegion, null, /*#__PURE__*/React.createElement(DetailSubheaderText, null, text)), /*#__PURE__*/React.createElement(DetailSubheaderButton, null, button))));
};
DetailSubheader.propTypes = {
  text: PropTypes.node.isRequired,
  horizontalPadding: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
  button: PropTypes.node
};
DetailSubheader.defaultProps = {
  horizontalPadding: 0,
  button: null
};
DetailSubheader.propTypes = {};
DetailSubheader.defaultProps = {};

const PERK_PHRASES = ['free', 'free (fee for premium)', 'priority', 'priority (a1-a15)', 'yes', 'free (premium)', 'wifi available', 'fast wifi available', 'personal on demand video', 'streaming video', 'personal video', 'tablet video', 'live tv available', 'power available', 'power and usb', 'power with adapter', 'usb only'];
const DISADVANTAGE_PHRASES = ['not permitted', 'not offered', 'last group', 'no wifi', 'none', 'no in-flight entertainment', 'no live tv', 'no power'];
const getValueColor = () => props => {
  const {
    value
  } = props;
  if (typeof value === 'string') {
    if (PERK_PHRASES.includes(value.toLowerCase())) {
      return getThemeProp('global.success--color')(props);
    }
    if (DISADVANTAGE_PHRASES.includes(value.toLowerCase())) {
      return getThemeProp('global.warning--color')(props);
    }
  }
  return 'unset';
};
const Value = styled.span`
  font-weight: bold;
  color: ${getValueColor};
`;
const PerkOrDisadvantage = _ref => {
  let {
    value
  } = _ref;
  return /*#__PURE__*/React.createElement(Value, {
    value: value
  }, value || 'No Info Available');
};
PerkOrDisadvantage.propTypes = {
  // eslint-disable-next-line react/require-default-props
  value: PropTypes.string
};

const Detail = styled(Flex)`
  line-height: 1.7;
  font-size: 14px;
  max-width: 320px;
`;
const DetailLabel = styled(Box)`
  flex: 0 1 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Label = styled(Box)``;
const DetailItem = _ref => {
  let {
    label,
    value = ''
  } = _ref;
  return /*#__PURE__*/React.createElement(Detail, null, /*#__PURE__*/React.createElement(Label, {
    width: 130
  }, label), /*#__PURE__*/React.createElement(DetailLabel, {
    title: value
  }, /*#__PURE__*/React.createElement(PerkOrDisadvantage, {
    value: value
  })));
};
DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  value: PropTypes.string
};

const DescriptionBox = styled(Box)`
  font-style: italic;
  line-height: 1.3;
  font-size: 15px;
`;
const Component = _ref => {
  let {
    fare
  } = _ref;
  const {
    changeFee,
    refunds,
    seatAssignments,
    boardingPriority,
    mileage,
    flightBrand
  } = fare;
  let refundValue = '';
  if (refunds === 'Y') {
    refundValue = 'YES';
  } else if (refunds === 'N') {
    refundValue = 'Not Permitted';
  } else {
    refundValue = refunds;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, flightBrand !== null && flightBrand !== void 0 && flightBrand.fullDescription ? /*#__PURE__*/React.createElement(DescriptionBox, {
    maxWidth: "320px",
    minHeight: [null, null, null, '60px'],
    pb: 3
  }, flightBrand.fullDescription) : null, /*#__PURE__*/React.createElement(DetailSubheader, {
    text: "Fare Details"
  }), /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(DetailItem, {
    label: "Changes**",
    value: changeFee ? fare.changeFee.toString() : 'No Info Available'
  }), /*#__PURE__*/React.createElement(DetailItem, {
    label: "Refunds",
    value: refundValue
  }), /*#__PURE__*/React.createElement(DetailItem, {
    label: "Advanced Seat",
    value: SEAT_ASSIGNMENT_CODES[seatAssignments] || SEAT_ASSIGNMENT_CODES[0]
  }), /*#__PURE__*/React.createElement(DetailItem, {
    label: "Boarding Priority",
    value: boardingPriority || 'No Info Available'
  }), /*#__PURE__*/React.createElement(DetailItem, {
    label: "Mileage",
    value: mileage || 'No Info Available'
  })));
};
Component.propTypes = {
  fare: PropTypes.shape({
    changeFee: PropTypes.string,
    refunds: PropTypes.string,
    seatAssignments: PropTypes.number,
    boardingPriority: PropTypes.string,
    mileage: PropTypes.string,
    flightBrand: PropTypes.shape({
      fullDescription: PropTypes.string
    }).isRequired
  }).isRequired
};

const DetailCardBaggage = _ref => {
  let {
    details
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DetailSubheader, {
    text: "Baggage"
  }), /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(DetailItem, {
    label: "Personal Item*",
    value: details === null || details === void 0 ? void 0 : details.personalItem
  }), /*#__PURE__*/React.createElement(DetailItem, {
    label: "Carry-on*",
    value: details === null || details === void 0 ? void 0 : details.carryOnBag
  }), /*#__PURE__*/React.createElement(DetailItem, {
    label: "1st Checked*",
    value: details === null || details === void 0 ? void 0 : details.firstCheckedBag
  }), /*#__PURE__*/React.createElement(DetailItem, {
    label: "2nd Checked*",
    value: details === null || details === void 0 ? void 0 : details.secondCheckedBag
  })));
};
DetailCardBaggage.propTypes = {
  details: PropTypes.shape({
    personalItem: PropTypes.string,
    carryOnBag: PropTypes.string,
    firstCheckedBag: PropTypes.string,
    secondCheckedBag: PropTypes.string
  }).isRequired
};
DetailCardBaggage.defaultProps = {};

/* eslint-disable react/require-default-props */
const CO2Flex = styled(Flex)`
  font-size: 12px;
  visibility: ${_ref => {
  let {
    hide
  } = _ref;
  return hide ? 'hidden' : 'visible';
}};
  background: rgba(0, 0, 0, 0.05);
  padding: 3px 4px;
  display: inline-flex;
  ${_ref2 => {
  let {
    isGood
  } = _ref2;
  return isGood && css`
      color: #2e4b29;
      background: #c1f2ab;
    `;
}};
  ${_ref3 => {
  let {
    isBad
  } = _ref3;
  return isBad && css`
      background: #f8ddb9;
      color: #5c3909;
    `;
}};
`;
const CO2Emissions = _ref4 => {
  let {
    co2Status,
    totalCo2 = 0
  } = _ref4;
  return /*#__PURE__*/React.createElement(CO2Flex, {
    isGood: co2Status === 1,
    isBad: co2Status === -1,
    hide: totalCo2 === 0
  }, /*#__PURE__*/React.createElement(IconEarth, null), /*#__PURE__*/React.createElement(Box, {
    pl: 1
  }, /*#__PURE__*/React.createElement("b", null, Math.floor(totalCo2), " lbs CO", /*#__PURE__*/React.createElement("sub", null, "2"))));
};
CO2Emissions.propTypes = {
  co2Status: PropTypes.number,
  totalCo2: PropTypes.number
};

/* eslint-disable react/prop-types */
const SeatDetailsRegion = styled(Box)`
  background: ${props => `linear-gradient(180deg, ${getThemeProp('global.light--color')(props)}, rgba(255,255,255,0))`};
  border-radius: 10px;
`;
const AircraftType = styled.span`
  opacity: 0.6;
  font-size: 0.825rem;
`;
const FlexWifiContainer = styled(Flex)`
  opacity: 0.75;
  font-size: 24px;
  position: relative;
  width: 30px;
  height: 20px;
  justify-content: center;
  margin-bottom: 1px;
`;
const FlexPowerContainer = styled(FlexWifiContainer)`
  font-size: 20px;
`;
const FlexLiveTVContainer = styled(FlexWifiContainer)`
  font-size: 20px;
`;
const Figure = styled.figure`
  color: white;
  position: relative;
  display: block;
  margin: 0 auto;
  max-width: 320px;
  text-align: center;

  img {
    width: 215px;
    height: 143px;
    object-fit: cover;
  }
`;
const SeatDetails = _ref => {
  let {
    amenities
  } = _ref;
  const {
    seatType,
    allAisleAccess,
    adjacentSeatBlocked,
    seatText,
    seatPitch,
    seatWidth,
    seatConfig
  } = amenities;
  if (seatType === SEAT_SIZE.suite) {
    return /*#__PURE__*/React.createElement(SeatDetailsRegion, {
      p: 2,
      pb: 0,
      mt: 3
    }, /*#__PURE__*/React.createElement(SeatDetailsSuite, {
      allAisleAccess: allAisleAccess,
      adjacentSeatBlocked: adjacentSeatBlocked,
      seatText: seatText,
      seatConfig: seatConfig,
      compact: true
    }));
  }
  if (seatType === SEAT_SIZE.angledFlat) {
    return /*#__PURE__*/React.createElement(SeatDetailsRegion, {
      p: 2,
      pb: 0,
      mt: 3
    }, /*#__PURE__*/React.createElement(SeatDetailsAngledFlat, {
      allAisleAccess: allAisleAccess,
      adjacentSeatBlocked: adjacentSeatBlocked,
      seatText: seatText,
      seatConfig: seatConfig,
      compact: true
    }));
  }
  if (seatType === SEAT_SIZE.fullFlat) {
    return /*#__PURE__*/React.createElement(SeatDetailsRegion, {
      p: 2,
      pb: 0,
      mt: 3
    }, /*#__PURE__*/React.createElement(SeatDetailsFlat, {
      allAisleAccess: allAisleAccess,
      adjacentSeatBlocked: adjacentSeatBlocked,
      seatText: seatText,
      seatConfig: seatConfig,
      compact: true
    }));
  }
  if (seatType === SEAT_SIZE.flatPod) {
    return /*#__PURE__*/React.createElement(SeatDetailsRegion, {
      p: 2,
      pb: 0,
      mt: 3
    }, /*#__PURE__*/React.createElement(SeatDetailsPod, {
      allAisleAccess: allAisleAccess,
      adjacentSeatBlocked: adjacentSeatBlocked,
      seatText: seatText,
      seatConfig: seatConfig,
      compact: true
    }));
  }
  if (seatPitch) {
    return /*#__PURE__*/React.createElement(SeatDetailsRegion, {
      p: 2,
      pb: 0,
      mt: 3
    }, /*#__PURE__*/React.createElement(SeatDetailsStandard, {
      allAisleAccess: allAisleAccess,
      adjacentSeatBlocked: adjacentSeatBlocked,
      seatText: seatText,
      seatPitch: seatPitch,
      seatWidth: seatWidth,
      seatConfig: seatConfig,
      compact: true
    }));
  }
  return null;
};
const DetailCardAmenities = _ref2 => {
  var _segments$, _fareDetails$brandIma, _segments$activeLeg3, _segments$activeLeg4, _segments$activeLeg5, _segments$activeLeg6, _segments$activeLeg7;
  let {
    segments,
    segmentAmenities,
    co2,
    fareDetails
  } = _ref2;
  const [activeLeg, setActiveLeg] = useState(segments.findIndex(seg => seg.isLongestSegment));
  const amenities = segmentAmenities === null || segmentAmenities === void 0 ? void 0 : segmentAmenities[activeLeg];
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DetailSubheader, {
    text: `Seat & ${isTrainStation(segments === null || segments === void 0 || (_segments$ = segments[0]) === null || _segments$ === void 0 ? void 0 : _segments$.fromCode) ? 'Train' : 'Plane'}`,
    button: segments.length > 1 ? /*#__PURE__*/React.createElement(PopoverMenu, {
      items: segments.map((seg, idx) => {
        return {
          label: `${seg.fromCode} - ${seg.toCode}`,
          onClick: () => setActiveLeg(idx)
        };
      })
    }, _ref3 => {
      var _segments$activeLeg, _segments$activeLeg2;
      let {
        isOpen
      } = _ref3;
      return /*#__PURE__*/React.createElement(Button, {
        iconSuffix: isOpen ? /*#__PURE__*/React.createElement(IconCaretUp, null) : /*#__PURE__*/React.createElement(IconCaretDown, null),
        text: `${segments === null || segments === void 0 || (_segments$activeLeg = segments[activeLeg]) === null || _segments$activeLeg === void 0 ? void 0 : _segments$activeLeg.fromCode} - ${segments === null || segments === void 0 || (_segments$activeLeg2 = segments[activeLeg]) === null || _segments$activeLeg2 === void 0 ? void 0 : _segments$activeLeg2.toCode}`,
        pill: true,
        size: "small",
        borderedLight: true
      });
    }) : null
  }), fareDetails !== null && fareDetails !== void 0 && (_fareDetails$brandIma = fareDetails.brandImages) !== null && _fareDetails$brandIma !== void 0 && _fareDetails$brandIma.length ? /*#__PURE__*/React.createElement(Box, {
    maxWidth: "320px",
    pt: 3
  }, /*#__PURE__*/React.createElement(Carousel, {
    innerOverlayButtons: true,
    loopSlides: false,
    dragFree: false
  }, fareDetails.brandImages.map((img, idx) =>
  /*#__PURE__*/
  // eslint-disable-next-line react/no-array-index-key
  React.createElement(Figure, {
    key: idx
  }, /*#__PURE__*/React.createElement("img", {
    alt: "Plane",
    src: img.startsWith('http') ? img : `https://cdn.amtrav.com/airline-interior/${img}`
  }))))) : null, /*#__PURE__*/React.createElement(Box, {
    pt: 3
  }, segments === null || segments === void 0 || (_segments$activeLeg3 = segments[activeLeg]) === null || _segments$activeLeg3 === void 0 ? void 0 : _segments$activeLeg3.aircraftTypeName, (segments === null || segments === void 0 || (_segments$activeLeg4 = segments[activeLeg]) === null || _segments$activeLeg4 === void 0 ? void 0 : _segments$activeLeg4.aircraftTypeCategory) !== (segments === null || segments === void 0 || (_segments$activeLeg5 = segments[activeLeg]) === null || _segments$activeLeg5 === void 0 ? void 0 : _segments$activeLeg5.aircraftTypeName) && (segments === null || segments === void 0 || (_segments$activeLeg6 = segments[activeLeg]) === null || _segments$activeLeg6 === void 0 ? void 0 : _segments$activeLeg6.aircraftTypeCategory) && /*#__PURE__*/React.createElement(AircraftType, null, " (", segments === null || segments === void 0 || (_segments$activeLeg7 = segments[activeLeg]) === null || _segments$activeLeg7 === void 0 ? void 0 : _segments$activeLeg7.aircraftTypeCategory, ")")), /*#__PURE__*/React.createElement(Box, {
    pt: 2
  }, /*#__PURE__*/React.createElement(CO2Emissions, co2)), /*#__PURE__*/React.createElement(Flex, {
    pt: 3,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(FlexWifiContainer, null, amenities.wifiStatus === 'Fast' ? /*#__PURE__*/React.createElement(IconWiFiFast, null) : /*#__PURE__*/React.createElement(IconWiFi, null)), /*#__PURE__*/React.createElement(Box, {
    pl: 2
  }, /*#__PURE__*/React.createElement(PerkOrDisadvantage, {
    value: FRIENDLY_WIFI_LABEL[amenities.wifiStatus] || 'No Info Available'
  }))), /*#__PURE__*/React.createElement(Flex, {
    pt: 1,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(FlexWifiContainer, null, /*#__PURE__*/React.createElement(IconStreamingVideo, null)), /*#__PURE__*/React.createElement(Box, {
    pl: 2
  }, /*#__PURE__*/React.createElement(PerkOrDisadvantage, {
    value: FRIENDLY_IFE_LABEL[amenities.ifeStatus] || 'No Info Available'
  }))), /*#__PURE__*/React.createElement(Flex, {
    pt: 1,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(FlexLiveTVContainer, null, /*#__PURE__*/React.createElement(IconLiveTV, null)), /*#__PURE__*/React.createElement(Box, {
    pl: 2
  }, /*#__PURE__*/React.createElement(PerkOrDisadvantage, {
    value: FRIENDLY_LIVE_TV_LABEL[amenities.liveTVStatus] || 'No Info Available'
  }))), /*#__PURE__*/React.createElement(Flex, {
    pt: 1,
    pb: 2,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(FlexPowerContainer, null, /*#__PURE__*/React.createElement(IconPower, null)), /*#__PURE__*/React.createElement(Box, {
    pl: 2
  }, /*#__PURE__*/React.createElement(PerkOrDisadvantage, {
    value: FRIENDLY_POWER_LABEL[amenities.powerStatus] || 'No Info Available'
  }))), /*#__PURE__*/React.createElement(SeatDetails, {
    amenities: amenities
  }));
};
DetailCardAmenities.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  segmentAmenities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  co2: PropTypes.shape({}).isRequired
};
DetailCardAmenities.defaultProps = {};

/* eslint-disable no-nested-ternary */
const SelectFare = _ref => {
  let {
    onSelectFare,
    fare,
    legNum,
    tripType,
    isOneWayFare,
    airportOnly,
    isChangeTrips,
    isLastLeg
  } = _ref;
  const selectHandler = () => onSelectFare({
    fare,
    legNum
  });
  return /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    flexDirection: "column",
    mt: 3
  }, /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    onClick: selectHandler,
    disabled: fare.soldOut || airportOnly,
    text: fare.soldOut ? fare.notOfferedReason || 'Sold Out' : /*#__PURE__*/React.createElement(React.Fragment, null, "Select for\xA0", /*#__PURE__*/React.createElement("b", null, isChangeTrips ? `${formatFare(Math.ceil(fare.totalItinerary), fare.totalItinerary !== 0)}` : `${formatFare(Math.ceil(fare.totalFare))}`))
  }), !fare.soldOut && /*#__PURE__*/React.createElement(SelectFareRegion$1, {
    pt: 2
  }, /*#__PURE__*/React.createElement(AdditionalFareInfo, {
    isChangeTrips: isChangeTrips,
    isOneWayFare: isOneWayFare,
    residualCredit: fare.residualCredit,
    totalItinerary: fare.totalItinerary,
    exchangeType: fare.exchangeType,
    tripType: tripType,
    isLastLeg: isLastLeg
  })));
};
SelectFare.propTypes = {
  onSelectFare: PropTypes.func.isRequired,
  fare: PropTypes.shape({
    totalItinerary: PropTypes.number,
    totalFare: PropTypes.number,
    residualCredit: PropTypes.number,
    exchangeType: PropTypes.string,
    soldOut: PropTypes.bool,
    notOfferedReason: PropTypes.string
  }).isRequired,
  legNum: PropTypes.number.isRequired,
  isOneWayFare: PropTypes.bool.isRequired,
  tripType: PropTypes.string.isRequired,
  airportOnly: PropTypes.bool.isRequired,
  isChangeTrips: PropTypes.bool.isRequired,
  isLastLeg: PropTypes.bool.isRequired
};

/* eslint-disable no-nested-ternary */
const FlightCardHeader = styled(Flex)`
  background: ${getThemeProp('global.light--color')};
`;
const FlightCard = styled(Box)`
  border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};

  ${media.Small`
    max-width: 400px;
  `}
`;
styled.div`
  display: flex;
  justify-content: center;
`;
styled.div``;
styled(Box)``;
const DurationBadge = styled.span`
  display: inline-block;
  background: rgba(0, 0, 0, 0.08);
  padding: 3px 5px;
`;
const AirlineName$3 = styled(Box)`
  font-size: 14px;
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
`;
const BrandArea = styled(Flex)`
  align-items: center;
  gap: 8px;
`;
const MicroDetails = styled(AirlineName$3)`
  padding-bottom: 8px;
`;
const BrandHeader = styled.b`
  font-size: 18px;
`;
const LayoverText = styled.em`
  flex: 0 1 auto;
  font-weight: 600;
  font-size: 13px;
`;
const StickyHeader$1 = styled(Flex)`
  position: sticky;
  top: 0;
  background: white;
  z-index: ${ZINDEX.TOOLTIP};

  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    z-index: 2;
    height: 16px;
    bottom: -16px;
    background: linear-gradient(0deg, rgba(255, 255, 255, 0), white);
  }
`;
const DrawerFlightDetails = _ref => {
  let {
    segments,
    fares,
    isOneWayFare,
    tripType,
    onSelectFare,
    legNum,
    isPreferred,
    isFastest,
    airportOnly,
    otherBadges,
    isChangeTrips,
    isLastLeg
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FlightBadges, {
    airportOnly: airportOnly,
    isPreferred: isPreferred,
    isFastest: isFastest,
    otherBadges: otherBadges
  }), /*#__PURE__*/React.createElement(Box, {
    p: 3,
    pb: 7
  }, segments.map(_ref2 => {
    let {
      airlineCode,
      airlineName,
      flightNumber,
      fromCode,
      fromName,
      fromTerminal,
      toCode,
      toName,
      toTerminal,
      stopDetails,
      departsDate,
      arrivesDate,
      dayChange,
      aircraftTypeName,
      miles,
      onTimePercent,
      numStops,
      duration
    } = _ref2;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: `${fromCode}${fromName}`
    }, stopDetails && /*#__PURE__*/React.createElement(Flex, {
      p: 2,
      pl: 3,
      py: 3,
      width: 1,
      alignItems: "center",
      justifyContent: "space-between"
    }, /*#__PURE__*/React.createElement(LayoverText, null, "Layover in ", fromName), /*#__PURE__*/React.createElement(DurationBadge, null, /*#__PURE__*/React.createElement(Duration$1, {
      duration: stopDetails.duration
    }))), /*#__PURE__*/React.createElement(FlightCard, null, /*#__PURE__*/React.createElement(FlightCardHeader, {
      p: 2,
      pl: 3,
      alignItems: "center",
      justifyContent: "space-between"
    }, /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(AirlineLogo, {
      alCode: airlineCode,
      size: "14px"
    }), /*#__PURE__*/React.createElement(AirlineName$3, {
      pl: "6px"
    }, airlineName, " #", flightNumber)), /*#__PURE__*/React.createElement(DurationBadge, null, /*#__PURE__*/React.createElement(Duration$1, {
      duration: duration
    }))), /*#__PURE__*/React.createElement(Box, {
      p: 2,
      px: 3,
      pt: 0
    }, /*#__PURE__*/React.createElement(SegmentRoute, {
      fromCode: fromCode,
      fromName: fromName,
      fromTerminal: fromTerminal,
      toCode: toCode,
      toName: toName,
      toTerminal: toTerminal,
      departsDate: departsDate,
      arrivesDate: arrivesDate,
      dayChange: dayChange
    }), /*#__PURE__*/React.createElement(MicroDetails, {
      mt: dayChange ? 0 : '-16px'
    }, /*#__PURE__*/React.createElement(FlightMicroDetails, {
      aircraftTypeName: aircraftTypeName,
      miles: miles,
      onTimePercent: onTimePercent,
      numStops: numStops
    })))));
  }), fares.flat().map(fare => {
    const {
      flightBrand,
      amenities,
      fareId
    } = fare;
    const {
      co2Comps,
      co2Rank,
      co2Status,
      totalCo2
    } = amenities;
    const co2 = {
      co2Comps,
      co2Rank,
      co2Status,
      totalCo2
    };
    return /*#__PURE__*/React.createElement(FlightCard, {
      key: fareId,
      px: 3,
      pb: 3,
      mt: 4
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(StickyHeader$1, {
      pt: 3,
      pb: 2,
      justifyContent: "space-between",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(BrandArea, null, /*#__PURE__*/React.createElement(BrandHeader, null, flightBrand === null || flightBrand === void 0 ? void 0 : flightBrand.brandName), fare.outOfPolicy && /*#__PURE__*/React.createElement(OutOfPolicyIndicator, {
      additionalText: fare.additionalOutOfPolicyText,
      details: fare.policyBreakDetails
    }))), /*#__PURE__*/React.createElement(Box, {
      pt: 3,
      pb: 3
    }, /*#__PURE__*/React.createElement(Component, {
      fare: fare
    })), /*#__PURE__*/React.createElement(Box, {
      pt: 2,
      pb: 3
    }, /*#__PURE__*/React.createElement(DetailCardBaggage, {
      details: flightBrand
    })), /*#__PURE__*/React.createElement(Box, {
      pt: 2
    }, /*#__PURE__*/React.createElement(DetailCardAmenities, {
      fareDetails: fare,
      co2: co2,
      segments: segments,
      segmentAmenities: amenities === null || amenities === void 0 ? void 0 : amenities.segmentAmenities
    }))), /*#__PURE__*/React.createElement(Box, {
      mt: 3
    }, /*#__PURE__*/React.createElement(SelectFare, {
      airportOnly: airportOnly,
      onSelectFare: onSelectFare,
      fare: fare,
      legNum: legNum,
      tripType: tripType,
      isOneWayFare: isOneWayFare,
      isChangeTrips: isChangeTrips,
      isLastLeg: isLastLeg
    })));
  })));
};
DrawerFlightDetails.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fares: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({
    baseFare: PropTypes.number
  }))).isRequired,
  isOneWayFare: PropTypes.bool.isRequired,
  tripType: PropTypes.string.isRequired,
  onSelectFare: PropTypes.func.isRequired,
  legNum: PropTypes.number.isRequired,
  isPreferred: PropTypes.bool,
  airportOnly: PropTypes.bool,
  isFastest: PropTypes.bool,
  otherBadges: PropTypes.arrayOf(PropTypes.shape({})),
  isChangeTrips: PropTypes.bool.isRequired,
  isLastLeg: PropTypes.bool.isRequired
};
DrawerFlightDetails.defaultProps = {
  isPreferred: false,
  isFastest: false,
  otherBadges: null
};

/* eslint-disable react/prop-types */
const ShelfItem = styled(Box)`
  position: relative;
  background: white;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  border: 1px solid ${getThemeProp('global.success--color')};
  box-shadow: 0px 0px 25px 1px rgba(0, 0, 0, 0.05);
  background: white;
`;
const CloseButton$1 = styled(Button)`
  font-size: 12px;
  position: absolute;
  right: -10px;
  top: -10px;
`;
const AirlineName$2 = styled(Box)`
  font-size: 12px;
`;
const TopRegion = styled(Flex)`
  align-items: center;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  border-bottom: 2px solid
    ${props => tinycolor.mix(getThemeProp('global.success--color')(props), '#efefef', 85).toString()};
`;
const CompareShelfItem = _ref => {
  var _fare$flightBrand;
  let {
    details,
    comparisonHandler
  } = _ref;
  const {
    segments,
    airlineCode,
    airlineName,
    fare
  } = details;
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];
  const dayChange = segments.reduce((acc, seg) => acc + seg.dayChange, 0);
  const {
    fromCode
  } = firstSegment;
  const {
    toCode
  } = lastSegment;
  const {
    departsDate
  } = firstSegment;
  const {
    arrivesDate
  } = lastSegment;
  const removeComparison = () => comparisonHandler([fare]);
  return /*#__PURE__*/React.createElement(ShelfItem, null, /*#__PURE__*/React.createElement(CloseButton$1, {
    iconLineName: "X",
    kind: "danger",
    onClick: removeComparison,
    round: true,
    size: "small"
  }), /*#__PURE__*/React.createElement(TopRegion, {
    p: 1,
    px: 3
  }, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: airlineCode,
    size: "16px"
  }), /*#__PURE__*/React.createElement(AirlineName$2, {
    ml: 2
  }, /*#__PURE__*/React.createElement(Box, {
    pb: 1
  }, airlineName), /*#__PURE__*/React.createElement("b", null, fare === null || fare === void 0 || (_fare$flightBrand = fare.flightBrand) === null || _fare$flightBrand === void 0 ? void 0 : _fare$flightBrand.brandName))), /*#__PURE__*/React.createElement(Flex, {
    p: 2,
    px: 3
  }, /*#__PURE__*/React.createElement(Route, {
    compact: true,
    fromCode: fromCode,
    toCode: toCode,
    departsDate: departsDate,
    arrivesDate: arrivesDate,
    dayChange: dayChange
  })));
};
CompareShelfItem.propTypes = {
  details: PropTypes.shape({
    fromCode: PropTypes.string,
    departs: PropTypes.instanceOf(Date),
    toCode: PropTypes.string,
    toName: PropTypes.string,
    arrives: PropTypes.instanceOf(Date),
    airlineCode: PropTypes.string,
    airlineName: PropTypes.string,
    segments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    fare: PropTypes.shape({
      brandIndex: PropTypes.number
    }).isRequired
  }).isRequired,
  comparisonHandler: PropTypes.func.isRequired
};
CompareShelfItem.defaultProps = {};

const Brand = styled(Box)`
  font-size: 18px;
  font-weight: 700;
`;
const AirlineFlightNumbers$1 = styled.span`
  font-size: 0.825rem;
`;
const AirlineName$1 = styled.span`
  opacity: 0.75;
`;
const Bullet = styled(AirlineName$1)``;
const MixedCabinIndicatorWrapper = styled.span`
  display: inline-block;
  margin-left: 4px;
  padding-top: 1px;
`;
const DetailCardFlightOverview = _ref => {
  let {
    fromCode,
    fromName,
    departsDate,
    toCode,
    toName,
    arrivesDate,
    dayChange,
    airlineCode,
    airlineName,
    segments,
    duration,
    stops,
    brandDetails,
    downgradeRequired,
    hideRouteDetails
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flex, {
    pt: hideRouteDetails ? 3 : 4,
    px: 3,
    pb: hideRouteDetails ? 2 : 3
  }, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: airlineCode,
    size: "32px"
  }), /*#__PURE__*/React.createElement(Box, {
    pl: 3
  }, /*#__PURE__*/React.createElement(Brand, {
    pb: 1
  }, brandDetails === null || brandDetails === void 0 ? void 0 : brandDetails.brandName, " "), /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(AirlineFlightNumbers$1, null, /*#__PURE__*/React.createElement(AirlineName$1, null, airlineName, " "), segments.map((s, i) => /*#__PURE__*/React.createElement("span", {
    key: s.fromCode + s.flightNumber
  }, i !== 0 ? /*#__PURE__*/React.createElement(Bullet, null, " \xB7 ") : null, /*#__PURE__*/React.createElement(FlightNumber, {
    segment: s
  }))), downgradeRequired && /*#__PURE__*/React.createElement(Bullet, null, " \xB7 ")), downgradeRequired && /*#__PURE__*/React.createElement(MixedCabinIndicatorWrapper, null, /*#__PURE__*/React.createElement(MixedCabinIndicator, null))))), !hideRouteDetails ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flex, {
    pt: 3,
    pb: 2,
    px: 3
  }, /*#__PURE__*/React.createElement(Route, {
    fromName: fromName,
    fromCode: fromCode,
    toCode: toCode,
    toName: toName,
    departsDate: departsDate,
    arrivesDate: arrivesDate,
    dayChange: dayChange
  })), /*#__PURE__*/React.createElement(Box, {
    pt: 3,
    px: 3,
    pb: 3
  }, /*#__PURE__*/React.createElement(DurationStops$1, {
    segments: segments,
    duration: duration,
    stops: stops
  }))) : null);
};
DetailCardFlightOverview.propTypes = {
  fromCode: PropTypes.string.isRequired,
  fromName: PropTypes.string.isRequired,
  departsDate: PropTypes.instanceOf(Date).isRequired,
  toCode: PropTypes.string.isRequired,
  toName: PropTypes.string.isRequired,
  arrivesDate: PropTypes.instanceOf(Date).isRequired,
  dayChange: PropTypes.number.isRequired,
  airlineCode: PropTypes.string.isRequired,
  airlineName: PropTypes.string.isRequired,
  segments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  duration: PropTypes.number.isRequired,
  stops: PropTypes.number.isRequired,
  brandDetails: PropTypes.shape({
    brandName: PropTypes.string
  }).isRequired,
  downgradeRequired: PropTypes.bool.isRequired,
  hideRouteDetails: PropTypes.bool
};
DetailCardFlightOverview.defaultProps = {};

/* eslint-disable react/prop-types */
const getBorderColor = props => {
  return tinycolor.mix(getThemeProp('global.border--color')(props), 'black', 10).toString();
};
const SingleCardWrapper = styled(Flex)`
  position: relative;
  margin-left: 6px;
  margin-right: 12px;
`;
const DetailItemContainer = styled(Box)`
  border-style: solid;
  border-color: ${props => getBorderColor(props)};
  border-width: 0 1px;
`;
const SingleCard = styled(Flex)`
  min-width: 320px;
  flex-direction: column;
`;
const CloseButton = styled(Button)`
  position: absolute;
  right: -11px;
  top: -11px;

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    top: 0;
    right: 0;
    z-index: 10000;
  }
`;
const OutOfPolicyWrapper = styled.div`
  position: absolute;
  right: 15px;
  top: ${_ref => {
  let {
    moveUp
  } = _ref;
  return moveUp ? '22px' : '40px';
}};
`;
const FareBrandRegion = styled(Box)`
  background: white;
  position: sticky;
  top: 0;
  z-index: ${ZINDEX.TOOLTIP};
  box-shadow: ${props => `0 1px 0 0px ${getBorderColor(props)} inset, 1px 0px 0 0px ${getBorderColor(props)} inset, -1px 0px 0 0px ${getBorderColor(props)} inset`};
  border-top: 12px solid white;

  &:before {
    content: '';
    height: 1px;
    background: ${props => getBorderColor(props)};
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
  }

  &:after {
    content: '';
    background: linear-gradient(0deg, rgba(255, 255, 255, 0), #fff);
    height: 20px;
    position: absolute;
    right: 1px;
    left: 1px;
    bottom: -20px;
  }
`;
const SelectFareRegion = styled(Flex)`
  background: ${getTokenValue(THEME_TOKENS.card.color.background)};
  position: sticky;
  bottom: 0;
  z-index: ${ZINDEX.TOOLTIP};
  box-shadow: 0 -1px 0 0px ${props => getBorderColor(props)} inset;
  border-style: solid;
  border-color: ${props => getBorderColor(props)};
  border-width: 0 1px;

  &:before {
    content: '';
    height: 1px;
    background: ${props => getBorderColor(props)};
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
  }

  &:after {
    content: '';
    background: linear-gradient(-180deg, rgba(255, 255, 255, 0), #fff);
    height: 20px;
    position: absolute;
    right: 0;
    left: 0;
    top: -20px;
  }
`;
const FlightBadgesWrapper$1 = styled.div`
  position: absolute;
  top: 1px;
  left: 1px;
  right: 1px;
  transform: scale(0.9);
  transform-origin: top left;
  overflow: visible;
`;
const DetailCard = _ref2 => {
  var _fare$amenities;
  let {
    details,
    hideRouteDetails
  } = _ref2;
  const {
    airportOnly,
    tripType,
    airlineCode,
    airlineName,
    segments,
    duration,
    numStops,
    isPreferred,
    isFastest,
    fare,
    onSelectFare,
    legNum,
    isOneWayFare,
    comparisonHandler,
    badges,
    isChangeTrips,
    isLastLeg
  } = details;
  if (fare.soldOut) {
    return null;
  }
  const brandDetails = fare.flightBrand;
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];
  const dayChange = segments.reduce((acc, seg) => acc + seg.dayChange, 0);
  const removeComparison = comparisonHandler ? () => comparisonHandler([fare]) : null;
  const {
    co2Comps,
    co2Rank,
    co2Status,
    totalCo2
  } = (fare === null || fare === void 0 ? void 0 : fare.amenities) || {};
  const co2 = {
    co2Comps,
    co2Rank,
    co2Status,
    totalCo2
  };
  const {
    fromCode,
    fromName
  } = firstSegment;
  const {
    toCode,
    toName
  } = lastSegment;
  const {
    departsDate
  } = firstSegment;
  const {
    arrivesDate
  } = lastSegment;
  return /*#__PURE__*/React.createElement(SingleCardWrapper, null, /*#__PURE__*/React.createElement(SingleCard, null, /*#__PURE__*/React.createElement(FareBrandRegion, null, removeComparison && /*#__PURE__*/React.createElement(CloseButton, {
    iconLineName: "X",
    kind: "danger",
    onClick: removeComparison,
    round: true,
    size: "small"
  }), !hideRouteDetails ? /*#__PURE__*/React.createElement(FlightBadgesWrapper$1, null, /*#__PURE__*/React.createElement(FlightBadges, {
    airportOnly: airportOnly,
    isPreferred: isPreferred,
    isFastest: isFastest,
    otherBadges: badges,
    suppressEcoBadge: true
  })) : null, fare.outOfPolicy && /*#__PURE__*/React.createElement(OutOfPolicyWrapper, {
    moveUp: hideRouteDetails
  }, /*#__PURE__*/React.createElement(OutOfPolicyIndicator, {
    additionalText: fare.additionalOutOfPolicyText,
    details: fare.policyBreakDetails
  })), /*#__PURE__*/React.createElement(DetailCardFlightOverview, {
    fromCode: fromCode,
    fromName: fromName,
    departsDate: departsDate,
    toCode: toCode,
    toName: toName,
    arrivesDate: arrivesDate,
    dayChange: dayChange,
    airlineCode: airlineCode,
    airlineName: airlineName,
    segments: segments,
    duration: duration,
    stops: numStops,
    brandDetails: brandDetails,
    downgradeRequired: fare.downgradeRequired,
    hideRouteDetails: hideRouteDetails
  })), /*#__PURE__*/React.createElement(DetailItemContainer, {
    pt: "25px",
    pb: 3,
    px: 3
  }, /*#__PURE__*/React.createElement(Component, {
    fare: fare
  })), /*#__PURE__*/React.createElement(DetailItemContainer, {
    pt: 2,
    pb: 3,
    px: 3
  }, /*#__PURE__*/React.createElement(DetailCardBaggage, {
    details: brandDetails
  })), /*#__PURE__*/React.createElement(DetailItemContainer, {
    flex: "1 0 auto",
    pt: 2,
    pb: 4,
    px: 3
  }, /*#__PURE__*/React.createElement(DetailCardAmenities, {
    fareDetails: fare,
    co2: co2,
    segments: segments,
    segmentAmenities: fare === null || fare === void 0 || (_fare$amenities = fare.amenities) === null || _fare$amenities === void 0 ? void 0 : _fare$amenities.segmentAmenities
  })), /*#__PURE__*/React.createElement(SelectFareRegion, {
    justifyContent: "center",
    pb: 3
  }, /*#__PURE__*/React.createElement(SelectFare, {
    airportOnly: airportOnly,
    onSelectFare: onSelectFare,
    fare: fare,
    legNum: legNum,
    tripType: tripType,
    isOneWayFare: isOneWayFare,
    isChangeTrips: isChangeTrips,
    isLastLeg: isLastLeg
  }))));
};
DetailCard.propTypes = {
  details: PropTypes.shape({
    airlineCode: PropTypes.string,
    airlineName: PropTypes.string,
    segments: PropTypes.arrayOf(PropTypes.shape({})),
    duration: PropTypes.number,
    numStops: PropTypes.number,
    isPreferred: PropTypes.bool,
    isFastest: PropTypes.bool,
    fare: PropTypes.shape({
      brandIndex: PropTypes.number,
      totalFare: PropTypes.number,
      downgradeRequired: PropTypes.bool,
      soldOut: PropTypes.bool,
      outOfPolicy: PropTypes.bool,
      policyBreakDetails: PropTypes.arrayOf(PropTypes.string)
    }).isRequired,
    onSelectFare: PropTypes.func.isRequired,
    legNum: PropTypes.number.isRequired,
    isOneWayFare: PropTypes.bool.isRequired,
    comparisonHandler: PropTypes.func
  }).isRequired,
  hideRouteDetails: PropTypes.bool
};
DetailCard.defaultProps = {};

// this export allows for overriding styles
const CompareShelfStyles = {};
CompareShelfStyles.Wrapper = styled(animated.div)`
  height: 136px;
  width: 100%;
  bottom: 0;
  background: ${props => tinycolor.mix(getThemeProp('global.success--color')(props), 'white', 90).toString()};
  border-top: 1px solid ${getThemeProp('global.success--color')};
  z-index: ${ZINDEX.FIXED};
  position: sticky;
  opacity: 0;

  @media (min-width: 1490px) {
    position: fixed;
  }
`;
CompareShelfStyles.Shelf = styled(Flex)`
  height: 100%;
  justify-content: space-between;
  align-items: center;
  max-width: 1280px;
`;
const CompareButton = styled(Button)`
  padding: 0 8px;
  font-size: 18px;
`;
const CompareModalFlex = styled(Flex)`
  max-width: 90vw;
  align-items: flex-start;
  overflow-x: auto;
  max-height: calc(100vh - 155px);

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    width: 100%;
  }

  &::-webkit-scrollbar {
    all: unset;
    height: 24px;
  }

  &::-webkit-scrollbar-thumb {
    all: unset;
    background: #999;
    border-color: white;
    border-style: solid;
    border-width: 7px 0;

    &:vertical {
      border-width: 0px 0 0 5px;
    }
  }

  &::-webkit-scrollbar-track {
    all: unset;
    border-color: white;
    border-style: solid;
    border-width: 7px 0;
    background: #ddd;

    &:vertical {
      border-width: 0px 0 0 5px;
    }
  }
`;
const TinyType = styled(Box)`
  font-size: 11px;
  opacity: 0.6;
  line-height: 1.4;
`;
const BoxShelfWrapper = styled(Box)`
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    overflow-x: auto;
  }
`;
const CompareShelf = _ref => {
  let {
    isActive,
    compareList,
    onSelectFare,
    legNum,
    isOneWayFare,
    comparisonHandler,
    tripType,
    isChangeTrips,
    isLastLeg
  } = _ref;
  const props = useSpring({
    bottom: isActive && compareList.length > 0 ? '0px' : '-136px',
    opacity: isActive && compareList.length > 0 ? '100' : '0'
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const selectFareAndCloseModal = fare => {
    onSelectFare(fare);
    closeModal();
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CompareShelfStyles.Wrapper, {
    style: props
  }, /*#__PURE__*/React.createElement(CompareShelfStyles.Shelf, null, /*#__PURE__*/React.createElement(BoxShelfWrapper, {
    width: "calc(100% - 250px)"
  }, /*#__PURE__*/React.createElement(Flex, {
    pl: 4
  }, /*#__PURE__*/React.createElement(Carousel, {
    slideWidth: "250px",
    overlayButtons: true,
    loopSlides: false
  }, compareList.map(item => {
    return /*#__PURE__*/React.createElement(Box, {
      key: item.fare.bic,
      pr: 3,
      width: 250,
      pt: "10px"
    }, /*#__PURE__*/React.createElement(CompareShelfItem, {
      details: item,
      comparisonHandler: comparisonHandler
    }));
  })))), /*#__PURE__*/React.createElement(Box, {
    pr: [4]
  }, /*#__PURE__*/React.createElement(CompareButton, {
    disabled: false,
    kind: "success",
    onClick: openModal,
    text: "Compare Fares"
  })))), isModalOpen && /*#__PURE__*/React.createElement(Modal, {
    kind: "success",
    onClose: closeModal
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Compare Fares"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CompareModalFlex, {
    justifyContent: compareList.length === 1 ? 'center' : 'flex-start',
    alignItems: "flex-start"
  }, /*#__PURE__*/React.createElement(Flex, null, compareList.map(item => {
    const fareLevelDetails = {
      ...item,
      onSelectFare: selectFareAndCloseModal,
      legNum,
      isOneWayFare,
      tripType,
      comparisonHandler: compareList.length > 1 ? comparisonHandler : null,
      isChangeTrips,
      isLastLeg
    };
    return /*#__PURE__*/React.createElement(DetailCard, {
      key: item.fare.fareId,
      details: fareLevelDetails
    });
  }))), /*#__PURE__*/React.createElement(TinyType, {
    px: 3,
    pt: 2
  }, "* Weight and size limits apply.", /*#__PURE__*/React.createElement("br", null), "** Whether or not there is a change penalty, a fare difference may also apply if you're changing to a more expensive itinerary.")))));
};
CompareShelf.propTypes = {
  isActive: PropTypes.bool,
  compareList: PropTypes.arrayOf(PropTypes.shape({})),
  onSelectFare: PropTypes.func.isRequired,
  legNum: PropTypes.number.isRequired,
  isOneWayFare: PropTypes.bool.isRequired,
  comparisonHandler: PropTypes.func.isRequired,
  tripType: PropTypes.oneOf([TRIP_TYPE.roundTrip, TRIP_TYPE.oneWay, TRIP_TYPE.multiCity]).isRequired,
  isChangeTrips: PropTypes.bool.isRequired,
  isLastLeg: PropTypes.bool.isRequired
};
CompareShelf.defaultProps = {
  isActive: false,
  compareList: []
};

const FlightWrapper = styled(Flex)``;
const FlightDetailsStyled = styled(Flex)`
  flex: 1 0 auto;
  flex-direction: column;
  position: relative;
  background: white;
  line-height: 1;
  border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};

  ${_ref => {
  let {
    isLoading
  } = _ref;
  return isLoading && css`
      overflow: hidden;

      &:after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
      }
    `;
}}
`;
const AirlineFlightNumbers = styled.span`
  display: none;
  font-size: 14px;

  ${media.Medium`
    display: flex;
    align-items: center;
  `};

  ${_ref2 => {
  let {
    isLoading
  } = _ref2;
  return skeletonLoadingStyles(isLoading);
}};
`;
const AirlineNameWrapper = styled.span`
  font-weight: 600;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const BulletWrapper = styled.span``;
styled.div`
  width: 40px;
  height: 40px;
  background-color: white;
  display: flex;
  border-radius: 49.99%; /* odd bug, at 50% the background disappears */
  justify-content: center;
  position: relative;
  background-clip: padding-box;
  border: solid 2px transparent;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    margin: -2px;
    border-radius: inherit;
    background: linear-gradient(to right, #f5f5f5 50%, #ececec 0);
  }
`;
styled.div`
  position: absolute;
  top: 0;
  transform: translate(-55px, 0);
`;
const FlightNumbers = styled(Flex)`
  gap: ${SPACE.size8};
`;
styled(Box)`
  position: relative;
  display: none;

  ${media.Large`
    display: block;
  `};
`;
const DurationStopsRegion = styled.div`
  transform: translateY(-1px);

  ${media.XLargeOnly`
    transform: ${_ref3 => {
  let {
    withStops
  } = _ref3;
  return withStops ? 'translateY(-15px)' : 'translateY(2px)';
}};
  `};
`;
const SmallDisplayHeader = styled(Flex)`
  font-size: 12px;
  height: 40px;
  align-items: center;
  border-top-left-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};
  border-top-right-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};
  background-color: ${getTokenValue(THEME_TOKENS.base.color.infoBackground)};
  border-bottom: 1px solid ${getTokenValue(THEME_TOKENS.base.color.infoBorder)};
`;
const SmallDisplayFlightInfo = styled(Box)`
  ${_ref4 => {
  let {
    isLoading
  } = _ref4;
  return skeletonLoadingStyles(isLoading);
}};
`;
const AirlineLogoRegion = styled.div`
  ${_ref5 => {
  let {
    isLoading
  } = _ref5;
  return skeletonLoadingStyles(isLoading);
}};
`;
styled(Box)`
  font-weight: bold;
  font-size: 13px;
  cursor: pointer;
  color: ${getThemeProp('global.link--color')};
`;
const FlightBadgesWrapper = styled.div`
  position: absolute;
  top: 40px;
  left: 0;
  z-index: ${ZINDEX.DROPDOWN};

  ${media.Large`
    top: 12px;
  `}
`;
const DurationStopsRegionWrapper = styled(Box)`
  flex: 0 0 calc(100% - 234px);
`;
const FlightDetails = _ref6 => {
  let {
    details,
    columnBrandsLength,
    legNum,
    onSelectFare,
    isLoading,
    isCompare,
    isOneWayFare,
    tripType,
    comparisonHandler,
    comparisonIds,
    cmsPostClickHandler,
    fetchSeatAvailability,
    baseUrl,
    isChangeTrips,
    isLastLeg
  } = _ref6;
  const {
    airlineCode,
    badges,
    airlineName,
    airportOnly,
    numStops,
    duration,
    segments,
    structuredFares,
    isPreferred,
    isFastest
  } = details;
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];
  const dayChange = segments.reduce((acc, seg) => acc + seg.dayChange, 0);
  const {
    fromCode,
    fromName
  } = firstSegment;
  const isDepartingArrivalMismatch = firstSegment === null || firstSegment === void 0 ? void 0 : firstSegment.isDepartingArrivalMismatch;
  const {
    toCode,
    toName
  } = lastSegment;
  const {
    departsDate
  } = firstSegment;
  const {
    arrivesDate
  } = lastSegment;
  const hasBadges = isPreferred || isFastest || airportOnly || badges && (badges === null || badges === void 0 ? void 0 : badges.length) > 0;
  const [activeLeg, setActiveLeg] = useState(segments.findIndex(seg => seg.isLongestSegment));
  const [isOpen, setIsOpen] = useState(false);
  const codeshareArr = segments.filter(leg => leg.opAirlineCode !== leg.airlineCode);
  const hasCodeShare = codeshareArr.length > 0;
  const isMultiSegment = segments.length > 1;
  const [fareDetailsOpen, setFareDetailsOpen] = useState(false);

  /**
   *
   * @param {Array[Fares]|undefined|null} selectedCabinToCompare - is optional.
   * If selectedCabinToCompare is provided, it will limit comparison to only selected self/cabin.
   *
   */
  const toggleFareDetailsModal = selectedCabinToCompare => {
    if (selectedCabinToCompare && !fareDetailsOpen) {
      setFareDetailsOpen(selectedCabinToCompare);
    } else {
      setFareDetailsOpen(!fareDetailsOpen);
    }
  };
  const openDetailsModal = () => setIsOpen(true);
  const closeDetailsModal = () => setIsOpen(false);
  const selectFareAndCloseModal = fare => {
    onSelectFare(fare);
    closeDetailsModal();
    toggleFareDetailsModal();
  };

  // fareDetailsOpen can be the selected Fare Details or boolean.
  // If it is, we can use this to show only those fares. structuredFares.flat() is old logic but might be else where.
  const whichFareDetailFare = typeof fareDetailsOpen === 'object' ? fareDetailsOpen : structuredFares.flat();
  return /*#__PURE__*/React.createElement(FlightWrapper, {
    pl: [2, 2, 2, 4],
    pr: [2, 2, 2, SPACE.size16],
    pb: "24px",
    flex: "1 0 auto",
    width: "100%"
  }, /*#__PURE__*/React.createElement(FlightDetailsStyled, {
    width: "100%",
    isLoading: isLoading
  }, /*#__PURE__*/React.createElement(MatchMedia.MediumAndLess, null, /*#__PURE__*/React.createElement(SmallDisplayHeader, {
    pr: SPACE.size8
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    justifyContent: "space-between",
    flex: "1 0 auto",
    pl: 2
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(AirlineLogoRegion, {
    isLoading: isLoading
  }, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: airlineCode,
    size: "20px"
  })), /*#__PURE__*/React.createElement(SmallDisplayFlightInfo, {
    isLoading: isLoading,
    ml: 2
  }, /*#__PURE__*/React.createElement("span", null, airlineName, ' ', segments.map(s => /*#__PURE__*/React.createElement("span", {
    key: s.fromCode + s.flightNumber
  }, /*#__PURE__*/React.createElement(BulletWrapper, null, " \xB7 "), s.flightNumber))))), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    borderedLight: true,
    text: "Details",
    onClick: openDetailsModal
  }), isOpen && /*#__PURE__*/React.createElement(Modal, {
    noPadding: true,
    drawer: true,
    onClose: closeDetailsModal
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Flight Details"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(DrawerFlightDetails, {
    fares: structuredFares,
    segments: segments,
    isOneWayFare: isOneWayFare,
    tripType: tripType,
    onSelectFare: selectFareAndCloseModal,
    legNum: legNum,
    isPreferred: isPreferred,
    isFastest: isFastest,
    airportOnly: airportOnly,
    otherBadges: details.badges,
    isChangeTrips: isChangeTrips,
    isLastLeg: isLastLeg
  })))))), !isLoading && /*#__PURE__*/React.createElement(FlightBadgesWrapper, null, /*#__PURE__*/React.createElement(FlightBadges, {
    cmsPostClickHandler: cmsPostClickHandler,
    airportOnly: airportOnly,
    isPreferred: isPreferred,
    isFastest: isFastest,
    otherBadges: details.badges
  })), /*#__PURE__*/React.createElement(Flex, {
    flexDirection: ['column', 'column', 'column', 'row'],
    justifyContent: ['unset', 'unset', 'unset', 'unset', columnBrandsLength >= 4 ? 'space-between' : 'unset']
  }, /*#__PURE__*/React.createElement(BoxFlightDetailsSpacer, {
    columnCount: columnBrandsLength,
    pt: [hasBadges ? 4 : 3, hasBadges ? 4 : 3, hasBadges ? '54px' : SPACE.size32],
    pl: [3, 3, 3, SPACE.size24],
    pb: [2, 2, 2, isMultiSegment ? '60px' : SPACE.size32]
  }, /*#__PURE__*/React.createElement(MatchMedia.Large, null, /*#__PURE__*/React.createElement(Flex, {
    pb: SPACE.size16,
    justifyContent: "space-between",
    alignItems: "center",
    pr: 2
  }, /*#__PURE__*/React.createElement(AirlineFlightNumbers, {
    isLoading: isLoading
  }, /*#__PURE__*/React.createElement(Flex, {
    mr: SPACE.size6
  }, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: airlineCode,
    size: "20px"
  })), /*#__PURE__*/React.createElement(AirlineNameWrapper, null, airlineName), /*#__PURE__*/React.createElement(FlightNumbers, {
    pl: SPACE.size8
  }, segments.map(s => /*#__PURE__*/React.createElement(FlightNumber, {
    key: s.fromCode + s.flightNumber,
    segment: s
  })))), fareDetailsOpen && /*#__PURE__*/React.createElement(Modal, {
    onClose: () => toggleFareDetailsModal()
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Fare Details"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CompareModalFlex, {
    alignItems: "flex-start",
    justifyContent: whichFareDetailFare.length === 1 ? 'center' : 'flex-start'
  }, /*#__PURE__*/React.createElement(Flex, null, whichFareDetailFare.map(item => {
    const fareLevelDetails = {
      ...details,
      fare: item,
      onSelectFare: selectFareAndCloseModal,
      legNum,
      tripType,
      isOneWayFare,
      isChangeTrips,
      isLastLeg
    };
    return /*#__PURE__*/React.createElement(DetailCard, {
      hideRouteDetails: true,
      key: item.fareId,
      details: fareLevelDetails
    });
  }))), /*#__PURE__*/React.createElement(TinyType, {
    px: 3,
    pt: 2
  }, "* Weight and size limits apply.", /*#__PURE__*/React.createElement("br", null), "** Whether or not there is a change penalty, a fare difference may also apply if you're changing to a more expensive itinerary.")))))), /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Route, {
    fromCode: fromCode,
    fromName: fromName,
    toCode: toCode,
    toName: toName,
    departsDate: departsDate,
    arrivesDate: arrivesDate,
    dayChange: dayChange,
    isLoading: isLoading,
    isDepartingArrivalMismatch: isDepartingArrivalMismatch,
    primaryTime: true
  }), /*#__PURE__*/React.createElement(DurationStopsRegionWrapper, {
    alignSelf: "flex-end"
  }, /*#__PURE__*/React.createElement(DurationStopsRegion, {
    withStops: numStops !== 0
  }, /*#__PURE__*/React.createElement(DurationStops$1, {
    isLoading: isLoading,
    segments: segments,
    duration: duration,
    stops: numStops
  }))))), /*#__PURE__*/React.createElement(Fares, {
    isLoading: isLoading,
    isCompare: isCompare,
    fares: structuredFares,
    legNum: legNum,
    columnBrandsLength: columnBrandsLength,
    onSelectFare: onSelectFare,
    isOneWayFare: isOneWayFare,
    tripType: tripType,
    isNonstop: numStops === 0,
    activeLeg: activeLeg,
    airportOnly: airportOnly,
    comparisonHandler: comparisonHandler,
    comparisonIds: comparisonIds,
    onFareDetails: toggleFareDetailsModal,
    isChangeTrips: isChangeTrips,
    isLastLeg: isLastLeg
  })), /*#__PURE__*/React.createElement(Segments$1, {
    activeLeg: activeLeg,
    setActiveLeg: setActiveLeg,
    isLoading: isLoading,
    columnBrandCount: columnBrandsLength,
    segments: segments,
    fares: structuredFares,
    cmsPostClickHandler: cmsPostClickHandler,
    fetchSeatAvailability: fetchSeatAvailability,
    baseUrl: baseUrl,
    hasCodeShare: hasCodeShare
  }), hasCodeShare && !isLoading ? /*#__PURE__*/React.createElement(Codeshare, {
    isLoading: isLoading,
    segments: segments
  }) : null));
};
FlightDetails.propTypes = {
  columnBrandsLength: PropTypes.number.isRequired,
  details: PropTypes.shape({
    airlineCode: PropTypes.string.isRequired,
    airlineName: PropTypes.string.isRequired,
    airportOnly: PropTypes.bool.isRequired,
    numStops: PropTypes.number.isRequired,
    duration: PropTypes.number.isRequired,
    badges: PropTypes.arrayOf(PropTypes.shape({})),
    segments: PropTypes.arrayOf(PropTypes.shape({
      fromCode: PropTypes.string,
      /**
       * The name of the origin city or airport for this leg
       */
      fromName: PropTypes.string,
      /**
       * The three letter airport code for the destination point for this leg.
       */
      toCode: PropTypes.string,
      /**
       * The name of the destination city or airport for this leg.
       */
      toName: PropTypes.string,
      /**
       * The departure date and time.
       */
      departsDate: PropTypes.instanceOf(Date).isRequired,
      /**
       * The arrival date and time (Note: the arrival may not be the same day as the departure).
       */
      arrivesDate: PropTypes.instanceOf(Date).isRequired,
      isDepartingArrivalMismatch: PropTypes.bool
    })),
    structuredFares: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
    fares: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isPreferred: PropTypes.bool.isRequired,
    isFastest: PropTypes.bool.isRequired
  }).isRequired,
  legNum: PropTypes.number.isRequired,
  onSelectFare: PropTypes.func.isRequired,
  isPreferred: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  isCompare: PropTypes.bool.isRequired,
  isOneWayFare: PropTypes.bool.isRequired,
  tripType: PropTypes.string.isRequired,
  comparisonHandler: PropTypes.func.isRequired,
  comparisonIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  cmsPostClickHandler: PropTypes.func.isRequired,
  fetchSeatAvailability: PropTypes.func.isRequired,
  baseUrl: PropTypes.string,
  isChangeTrips: PropTypes.bool,
  isLastLeg: PropTypes.bool.isRequired
};
FlightDetails.defaultProps = {
  isPreferred: false
};

const FlightOption = _ref => {
  let {
    details,
    columnBrandsLength,
    onSelectFare,
    legNum,
    isLoading,
    isCompare,
    isOneWayFare,
    tripType,
    comparisonIds = [],
    comparisonHandler = () => {},
    cmsPostClickHandler,
    fetchSeatAvailability,
    baseUrl,
    isChangeTrips = false,
    isLastLeg
  } = _ref;
  return /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(FlightDetails, {
    details: details,
    isLoading: isLoading,
    isCompare: isCompare,
    legNum: legNum,
    columnBrandsLength: columnBrandsLength,
    onSelectFare: onSelectFare,
    isOneWayFare: isOneWayFare,
    tripType: tripType,
    comparisonHandler: comparisonHandler,
    comparisonIds: comparisonIds,
    cmsPostClickHandler: cmsPostClickHandler,
    fetchSeatAvailability: fetchSeatAvailability,
    baseUrl: baseUrl,
    isChangeTrips: isChangeTrips,
    isLastLeg: isLastLeg
  }));
};
FlightOption.propTypes = {
  details: PropTypes.shape({
    airlineCode: PropTypes.string,
    airlineName: PropTypes.string,
    numStops: PropTypes.number,
    duration: PropTypes.number,
    segments: PropTypes.arrayOf(PropTypes.shape({
      fromCode: PropTypes.string,
      fromName: PropTypes.string,
      toCode: PropTypes.string,
      toName: PropTypes.string,
      departsDate: PropTypes.instanceOf(Date),
      arrivesDate: PropTypes.instanceOf(Date),
      dayChange: PropTypes.number
    })),
    structuredFares: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
    isPreferred: PropTypes.bool,
    isFastest: PropTypes.bool.isRequired
  }).isRequired,
  columnBrandsLength: PropTypes.number.isRequired,
  legNum: PropTypes.number.isRequired,
  onSelectFare: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isCompare: PropTypes.bool.isRequired,
  isChangeTrips: PropTypes.bool,
  isOneWayFare: PropTypes.bool.isRequired,
  tripType: PropTypes.string.isRequired,
  comparisonHandler: PropTypes.func.isRequired,
  cmsPostClickHandler: PropTypes.func.isRequired,
  fetchSeatAvailability: PropTypes.func.isRequired,
  baseUrl: PropTypes.string,
  comparisonIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  isLastLeg: PropTypes.bool.isRequired
};
const isEqual = (preProps, nextProps) => {
  return preProps.memoId === nextProps.memoId && preProps.isLoading === nextProps.isLoading && preProps.isCompare === nextProps.isCompare && preProps.comparisonIds.join() === nextProps.comparisonIds.join();
};
var FlightOption$1 = /*#__PURE__*/memo(FlightOption, isEqual);

const IconSortWrapper = styled.span`
  font-size: 15px;
`;
const SortPopover = _ref => {
  let {
    sortOptions,
    activeSort
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IconSortWrapper, null, /*#__PURE__*/React.createElement(IconLine, {
    icon: "SortDescending",
    size: "small"
  })), /*#__PURE__*/React.createElement(PopoverMenu, {
    items: sortOptions
  }, _ref2 => {
    let {
      isOpen
    } = _ref2;
    return /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Box, {
      px: 2
    }, " ", activeSort), isOpen ? /*#__PURE__*/React.createElement(IconCaretUp, null) : /*#__PURE__*/React.createElement(IconCaretDown, null));
  }));
};
SortPopover.propTypes = {
  sortOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func
  })).isRequired,
  activeSort: PropTypes.string.isRequired
};
SortPopover.defaultProps = {};

const FlightOptionFaresContainer = styled(Flex)`
  font-size: 14px;
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)};

  ${BoxFareSpacer} {
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const FlightHeaderRegion = styled(Flex)`
  box-shadow: ${getTokenValue(THEME_TOKENS.base.shadow.raised)};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusSmall)};
  background-color: white;
`;
const StickyHeader = styled(Flex)`
  ${media.XLargeOnly`
    position: sticky;
    top: 0;
    display: flex;
    z-index: ${ZINDEX.FIXED};
  `}
`;
const sortingOptions = {
  default: 'Stops & Departure',
  departs_asc: 'Departure Time',
  arrives_asc: 'Arrival Time',
  duration: 'Duration',
  price: 'Price'
};
const FlexDetailsHeader = styled(BoxFlightDetailsSpacer)`
  display: Flex;
`;
const FlightOptionsHeader = _ref => {
  let {
    sortBy,
    columnBrands,
    onSortChange
  } = _ref;
  return /*#__PURE__*/React.createElement(StickyHeader, {
    pl: [2, 2, 2, 4],
    pr: [2, 2, 2, SPACE.size16],
    pt: [3, 3, 3, 0],
    pb: 3,
    width: "100%"
  }, /*#__PURE__*/React.createElement(FlightHeaderRegion, {
    width: "100%",
    justifyContent: columnBrands.length >= 4 ? 'space-between' : 'unset'
  }, /*#__PURE__*/React.createElement(FlexDetailsHeader, {
    columnCount: columnBrands.length
  }, /*#__PURE__*/React.createElement(FlightOptionFaresContainer, {
    columnCount: columnBrands.length,
    alignItems: "center",
    py: "2",
    pl: [2, 2, 2, 3]
  }, /*#__PURE__*/React.createElement(SortPopover, {
    activeSort: sortingOptions[sortBy] || 'Default',
    sortOptions: Object.keys(sortingOptions).map(sort => ({
      label: sortingOptions[sort],
      onClick: () => {
        onSortChange(sort);
      }
    }))
  }))), /*#__PURE__*/React.createElement(FlightOptionFaresContainer, {
    alignItems: "center",
    py: "2"
  }, columnBrands.map(fb => /*#__PURE__*/React.createElement(BoxFareSpacer, {
    columnCount: columnBrands.length,
    key: `${fb.description}`,
    px: 2
  }, `${fb.description}`)))));
};
FlightOptionsHeader.propTypes = {
  sortBy: PropTypes.string.isRequired,
  columnBrands: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onSortChange: PropTypes.func
};
FlightOptionsHeader.defaultProps = {
  onSortChange: () => {}
};

const initialOrder = flightOptions => {
  return flightOptions.sort((a, b) => {
    if (a.initialOrder < b.initialOrder) {
      return -1;
    }
    if (a.initialOrder > b.initialOrder) {
      return 1;
    }
    return 0;
  });
};
const sortByDepartsDate = (flightOptions, isAscending) => {
  return flightOptions.sort((a, b) => {
    if (a.segments[0].departsDate < b.segments[0].departsDate) {
      return isAscending ? -1 : 1;
    }
    if (a.segments[0].departsDate > b.segments[0].departsDate) {
      return isAscending ? 1 : -1;
    }
    return 0;
  });
};
const sortByArrivesDate = (flightOptions, isAscending) => {
  return flightOptions.sort((a, b) => {
    const segIndexA = a.segments.length - 1;
    const segIndexB = b.segments.length - 1;
    if (a.segments[segIndexA].arrivesDate < b.segments[segIndexB].arrivesDate) {
      return isAscending ? -1 : 1;
    }
    if (a.segments[segIndexA].arrivesDate > b.segments[segIndexB].arrivesDate) {
      return isAscending ? 1 : -1;
    }
    return 0;
  });
};
const sortByDuration = (flightOptions, isAscending) => {
  return flightOptions.sort((a, b) => {
    if (a.duration < b.duration) {
      return isAscending ? -1 : 1;
    }
    if (a.duration > b.duration) {
      return isAscending ? 1 : -1;
    }
    return 0;
  });
};

/**
 * Normally, price sorting is based on the lowestLegPrice (totalFare). However, for change trips,
 * sorting needs to be based on the lowestItineraryPrice (totalItinerary) first, followed by a secondary
 * sort based on lowestNetCost (which takes into account the residual credit).
 */
const sortByPriceChangeTrip = (flightOptions, isAscending) => {
  return flightOptions.sort((a, b) => {
    if (a.lowestItineraryPrice < b.lowestItineraryPrice) {
      return isAscending ? -1 : 1;
    }
    if (a.lowestItineraryPrice > b.lowestItineraryPrice) {
      return isAscending ? 1 : -1;
    }
    // if lowestItineraryPrice are equal, apply secondary sort based on lowestNetCost
    if (a.lowestNetCost < b.lowestNetCost) {
      return isAscending ? -1 : 1;
    }
    if (a.lowestNetCost > b.lowestNetCost) {
      return isAscending ? 1 : -1;
    }
    return 0;
  });
};
const sortByPrice = (flightOptions, isAscending) => {
  return flightOptions.sort((a, b) => {
    if (a.lowestLegPrice < b.lowestLegPrice) {
      return isAscending ? -1 : 1;
    }
    if (a.lowestLegPrice > b.lowestLegPrice) {
      return isAscending ? 1 : -1;
    }
    return 0;
  });
};

const NoResults = _ref => {
  let {
    onClearFilters
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    px: 3
  }, /*#__PURE__*/React.createElement(EmptyState, {
    transparent: true,
    size: "fullWidthLargeText",
    iconLineName: "Plane",
    illustrationKey: "error-404",
    text: "No available flights that matched your filters",
    secondaryContent: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
      py: SPACE.size16
    }, "Try adjusting your preferences"), /*#__PURE__*/React.createElement(Button, {
      onClick: onClearFilters,
      text: "Clear Filters"
    }))
  }));
};
NoResults.propTypes = {
  onClearFilters: PropTypes.func.isRequired
};

const SORT_OPTIONS = {
  default: initialOrder,
  departs: sortByDepartsDate,
  arrives: sortByArrivesDate,
  duration: sortByDuration,
  price: sortByPrice
};
const sortFlightOptions = (flights, sortBy, isChangeTrips) => {
  const [sortType, direction = 'asc'] = sortBy.split('_');
  const sortFunc = SORT_OPTIONS[sortType];
  if (!sortFunc) {
    console.error(`sortBy of ${sortBy} is not supported.`);
    return flights;
  }
  // change trip requires a different price sort function
  if (sortBy === 'price' && isChangeTrips) {
    return sortByPriceChangeTrip(flights, direction === 'asc');
  }
  return sortFunc(flights, direction === 'asc');
};
const comparisonMap = new Map();
const FlightOptions = _ref => {
  let {
    data,
    defaultSortBy,
    legNum,
    isLoading,
    isPatching,
    isCompare,
    exchangeId,
    onSortChange,
    onSelectFare,
    onClearFilters,
    tripType,
    cmsPostClickHandler,
    fetchSeatAvailability,
    baseUrl = '',
    resultId,
    additionalOutOfPolicyText = '',
    isLastLeg
  } = _ref;
  const {
    flights,
    columnBrands,
    flightBrands,
    selectedFlights
  } = data;
  // This memoization prevents rerenders but also keeps the fareIds from changing.
  // If this useMemo is removed, the compare feature will no longer work.
  const previouslySelectedFlight = selectedFlights === null || selectedFlights === void 0 ? void 0 : selectedFlights[legNum - 2];
  const amendedFlights = useMemo(() => processFlightsData({
    flights,
    columnBrands,
    flightBrands,
    previouslySelectedFlight,
    additionalOutOfPolicyText
  }), [resultId, columnBrands]);
  const [sortBy, setSortBy] = useState(defaultSortBy);
  const [comparisonIds, setComparisonIds] = useState([]);

  // We have the exchangeId when the onSelectFare callback is invoked.
  // Not sure if you need it in here, but it is passed in
  const isChangeTrips = exchangeId !== null;
  const resetComparisons = () => {
    comparisonMap.clear();
    setComparisonIds([...comparisonMap.keys()]);
  };
  const onSelectFareHandler = fare => {
    window.scrollTo(0, 0);
    onSelectFare(fare);
    resetComparisons();
  };
  useEffect(() => resetComparisons(), [resultId]);
  const sortChangeHandler = newSort => {
    setSortBy(newSort);
    onSortChange(newSort);
  };
  const comparisonHandler = fares => {
    const flight = amendedFlights.find(f => f.tag === fares[0].flightTag);
    const incomingComparisons = fares.map(f => ({
      ...flight,
      fare: f
    }));

    // NOTE: We are mutating the Map in the heap and keeping only the Ids in react state.
    // This is to prevent downstream memoization from using stale closure data.
    if (comparisonMap.has(incomingComparisons[0].fare.fareId)) {
      incomingComparisons.forEach(f => comparisonMap.delete(f.fare.fareId));
    } else {
      incomingComparisons.forEach(f => comparisonMap.set(f.fare.fareId, f));
    }
    setComparisonIds([...comparisonMap.keys()]);
  };

  // Memoize this to force rerenders when needed.
  const comparisons = useMemo(() => [...comparisonMap.values()], [comparisonIds]);
  if (flights.length === 0) {
    return /*#__PURE__*/React.createElement(NoResults, {
      onClearFilters: onClearFilters
    });
  }

  // Generate an array of sorted flight options
  const sortedFlights = sortFlightOptions(amendedFlights, sortBy, isChangeTrips).map(flight => {
    // only pass comparisonIds intersecting with this flight.
    const flightCompareIds = flight.fareIds.filter(fId => comparisonIds.includes(fId));
    return /*#__PURE__*/React.createElement(FlightOption$1, {
      isLoading: isLoading,
      isCompare: isCompare,
      key: flight.uniqueId,
      memoId: flight.uniqueId,
      details: flight,
      legNum: legNum,
      columnBrandsLength: data.columnBrands.length,
      flightBrands: flightBrands,
      onSelectFare: onSelectFareHandler,
      isOneWayFare: data.fareDisplayType === 'OneWay',
      tripType: tripType,
      comparisonIds: flightCompareIds,
      comparisonHandler: comparisonHandler,
      cmsPostClickHandler: cmsPostClickHandler,
      fetchSeatAvailability: fetchSeatAvailability,
      baseUrl: baseUrl,
      additionalOutOfPolicyText: additionalOutOfPolicyText,
      isChangeTrips: isChangeTrips,
      isLastLeg: isLastLeg
    });
  });
  return /*#__PURE__*/React.createElement(Box, null, isPatching ? /*#__PURE__*/React.createElement(FullPageLoader, null) : null, /*#__PURE__*/React.createElement(FlightOptionsHeader, {
    sortBy: sortBy,
    columnBrands: columnBrands,
    onSortChange: sortChangeHandler
  }), /*#__PURE__*/React.createElement(Box, {
    pb: 6
  }, sortedFlights), /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, /*#__PURE__*/React.createElement(CompareShelf, {
    isOneWayFare: data.fareDisplayType === 'OneWay',
    tripType: tripType,
    legNum: legNum,
    onSelectFare: onSelectFareHandler,
    compareList: comparisons,
    comparisonHandler: comparisonHandler,
    isActive: isCompare,
    isChangeTrips: isChangeTrips,
    isLastLeg: isLastLeg
  })));
};
FlightOptions.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  isPatching: PropTypes.bool,
  isCompare: PropTypes.bool,
  exchangeId: PropTypes.string,
  data: PropTypes.shape({
    flights: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    columnBrands: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    flightBrands: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    fareDisplayType: PropTypes.string.isRequired,
    selectedFlights: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  legNum: PropTypes.number.isRequired,
  defaultSortBy: PropTypes.string,
  onSortChange: PropTypes.func,
  onClearFilters: PropTypes.func,
  onSelectFare: PropTypes.func.isRequired,
  tripType: PropTypes.oneOf([TRIP_TYPE.roundTrip, TRIP_TYPE.oneWay, TRIP_TYPE.multiCity]),
  cmsPostClickHandler: PropTypes.func,
  fetchSeatAvailability: PropTypes.func.isRequired,
  baseUrl: PropTypes.string,
  resultId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  additionalOutOfPolicyText: PropTypes.string,
  isLastLeg: PropTypes.bool.isRequired
};
FlightOptions.defaultProps = {
  defaultSortBy: 'default',
  isPatching: false,
  exchangeId: null,
  onSortChange: () => {},
  onClearFilters: () => {},
  cmsPostClickHandler: () => {},
  baseUrl: '',
  isCompare: false,
  tripType: TRIP_TYPE.roundTrip,
  resultId: -1
};

/* eslint-disable react/require-default-props */

// Simple hack to keep JSR out of render cycle but accessible when needed. JSR is not available
// in electron so we can no longer assume we should init it in the global scope.
let jsr;
const DateSummary = styled(Flex)`
  align-items: center;
  font-weight: 400;
  font-size: 14px;
  color: var(${THEME_TOKENS.base.color.secondaryText}, ${THEME_TOKENS.base.color.neutral700});

  ${media.Large`
    font-size: 16px;
  `};
`;
const goToSearch = () => {
  const searchPageJsr = {};
  // keep invitation token
  if (jsr.invitationToken) {
    searchPageJsr.invitationToken = jsr.invitationToken;
  }
  searchPageJsr.organicSearch = true;
  searchPageJsr.change = true; // lets search page know we came from results
  document.location.href = `/flights?jsr=${encodeObjToJsrString(searchPageJsr)}`;
};
const IconWrapper = styled.span`
  display: inline-flex;
  font-size: 11px;
  padding: 1px 4px 0px 2px;
`;
const ChangeButton = styled(Button)`
  ${ButtonTextStyled} {
    padding: 2px 0 2px 12px;
  }
`;
const PageHeader = _ref => {
  var _jsr;
  let {
    routeSummary,
    dateSummaryObj,
    isEditSearchOpen = false,
    toggleEditSearch = goToSearch,
    tripType,
    isLoading
  } = _ref;
  if (jsr === undefined) {
    try {
      jsr = getJsr(); // get jsr from url
    } catch (err) {
      console.warn('JSR is not present in the URL. Initializing to empty object', err);
      jsr = {};
    }
  }
  const hideEditSearch = Boolean((_jsr = jsr) === null || _jsr === void 0 ? void 0 : _jsr.exchangeId) || isLoading;
  return /*#__PURE__*/React.createElement(React.Fragment, null, routeSummary, /*#__PURE__*/React.createElement(Flex, {
    pt: 2,
    alignItems: "center"
  }, dateSummaryObj.fromDate && dateSummaryObj.toDate ? /*#__PURE__*/React.createElement(DateSummary, {
    pr: 3
  }, dateSummaryObj.fromDate, /*#__PURE__*/React.createElement(Box, {
    px: SPACE.size8
  }, "\u2013"), dateSummaryObj.toDate) : /*#__PURE__*/React.createElement(DateSummary, {
    pr: 3
  }, dateSummaryObj.fromDate), !hideEditSearch && (tripType === TRIP_TYPE.roundTrip || tripType === TRIP_TYPE.oneWay ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.MediumAndLess, null, /*#__PURE__*/React.createElement(Button, {
    onClick: goToSearch,
    size: "small",
    borderedLight: true,
    text: "Change"
  })), /*#__PURE__*/React.createElement(MatchMedia.LargeOnly, null, /*#__PURE__*/React.createElement(Button, {
    onClick: goToSearch,
    size: "small",
    borderedLight: true,
    text: "Change Search"
  })), /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, /*#__PURE__*/React.createElement(ChangeButton, {
    iconSuffix: /*#__PURE__*/React.createElement(IconWrapper, null, isEditSearchOpen ? /*#__PURE__*/React.createElement(IconCaretUp, null) : /*#__PURE__*/React.createElement(IconCaretDown, null)),
    onClick: toggleEditSearch,
    size: "small",
    borderedLight: true,
    text: "Change Search"
  }))) : /*#__PURE__*/React.createElement(Button, {
    onClick: goToSearch,
    size: "small",
    borderedLight: true,
    text: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.MediumAndLess, null, "Change"), /*#__PURE__*/React.createElement(MatchMedia.Large, null, "Change Search"))
  }))));
};
PageHeader.propTypes = {
  routeSummary: PropTypes.string.isRequired,
  dateSummaryObj: PropTypes.shape({
    fromDate: PropTypes.string,
    toDate: PropTypes.string
  }).isRequired,
  toggleEditSearch: PropTypes.func,
  tripType: PropTypes.string.isRequired,
  isEditSearchOpen: PropTypes.bool,
  isLoading: PropTypes.bool
};

/* eslint-disable react/no-array-index-key */
const BoxUpgradeRegion = styled(Box)`
  max-width: 620px;
`;
const FareDescription = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
`;
const UpgradeFare = _ref => {
  let {
    selectedBrandName,
    selectedBrandHeader,
    selectedBrandRestrictions,
    upgradeBrandName,
    upgradeBenefits,
    upgradeDifference,
    hasUpgradeOption,
    onContinue,
    onCancel,
    onUpgrade
  } = _ref;
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onCancel
  }, /*#__PURE__*/React.createElement(Modal.Header, null, selectedBrandName), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(BoxUpgradeRegion, null, /*#__PURE__*/React.createElement(FareDescription, null, selectedBrandHeader), selectedBrandRestrictions ? /*#__PURE__*/React.createElement(List, {
    py: 3
  }, selectedBrandRestrictions.map((desc, idx) => /*#__PURE__*/React.createElement(List.Item, {
    key: idx,
    icon: /*#__PURE__*/React.createElement(IconWarning, null)
  }, desc))) : null, hasUpgradeOption && /*#__PURE__*/React.createElement(Card, {
    kind: "success"
  }, /*#__PURE__*/React.createElement(Card.Header, null, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: ['column-reverse', 'row'],
    justifyContent: "space-between",
    alignItems: [null, 'center']
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement("b", null, "Get more when you select ", upgradeBrandName)), /*#__PURE__*/React.createElement(Box, {
    pb: [2, 0]
  }, /*#__PURE__*/React.createElement(Badge, {
    kind: "success"
  }, "Recommended")))), /*#__PURE__*/React.createElement(Card.Body, null, hasUpgradeOption && /*#__PURE__*/React.createElement(Box, {
    mx: -3
  }, upgradeBenefits && /*#__PURE__*/React.createElement(List, null, upgradeBenefits.map((desc, idx) => /*#__PURE__*/React.createElement(List.Item, {
    key: idx,
    icon: /*#__PURE__*/React.createElement(IconCheckCircle, null)
  }, desc))), /*#__PURE__*/React.createElement(Box, {
    px: 3,
    pt: 3
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: onUpgrade,
    fullWidth: true,
    text: `Upgrade for only $${upgradeDifference} more`,
    kind: "success"
  }))))))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "center",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: onContinue,
    bordered: true,
    text: `Continue with ${selectedBrandName}`
  }))));
};
UpgradeFare.propTypes = {
  selectedBrandName: PropTypes.string.isRequired,
  selectedBrandHeader: PropTypes.string.isRequired,
  selectedBrandRestrictions: PropTypes.arrayOf(PropTypes.string),
  hasUpgradeOption: PropTypes.bool.isRequired,
  upgradeBrandName: PropTypes.string,
  upgradeBenefits: PropTypes.arrayOf(PropTypes.string),
  upgradeDifference: PropTypes.number,
  onContinue: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onUpgrade: PropTypes.func
};
UpgradeFare.defaultProps = {
  upgradeBenefits: [],
  upgradeBrandName: '',
  upgradeDifference: 0,
  selectedBrandRestrictions: null,
  onUpgrade: () => {}
};

const PriceChangeMessage = styled.div`
  max-width: 470px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
`;
const InteractiveResponse = _ref => {
  let {
    interactiveResponse,
    onContinue,
    onCancel
  } = _ref;
  const {
    type,
    message
  } = interactiveResponse;
  const getResponseKind = responseType => {
    switch (responseType) {
      case 'RateChangedLower':
        return 'success';
      case 'RateChangedSlightlyLower':
        return 'success';
      case 'RateChangedHigher':
        return 'warning';
      case 'RateChangedSlightlyHigher':
        return 'warning';
      default:
        return 'primary';
    }
  };
  return /*#__PURE__*/React.createElement(Dialog, {
    title: "Price Change",
    primaryActionText: "Continue",
    primaryAction: onContinue,
    secondaryActionText: "Cancel",
    secondaryAction: onCancel,
    onClose: onCancel,
    kind: getResponseKind(type)
  }, /*#__PURE__*/React.createElement(PriceChangeMessage, null, message));
};
InteractiveResponse.propTypes = {
  interactiveResponse: PropTypes.shape({
    type: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired
  }).isRequired,
  onContinue: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

const ContentRegion = styled(Box)`
  max-width: 620px;

  ul {
    list-style: square;
    padding: 0 0 1rem 2rem;
  }
  li {
    line-height: 1.4;
  }
`;
const CmsContentModal = _ref => {
  let {
    onClose = () => {},
    title = 'Loading...',
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose
  }, /*#__PURE__*/React.createElement(Modal.Header, null, title), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(ContentRegion, null, children)), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "center",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: onClose,
    bordered: true,
    text: "Close"
  }))));
};
CmsContentModal.propTypes = {
  title: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired
};

const getCrumbTitle = (legNum, tripType) => {
  if (tripType === TRIP_TYPE.roundTrip && legNum === 1) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, "Select Departure");
  }
  if (tripType === TRIP_TYPE.roundTrip && legNum === 2) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, "Select Return");
  }
  return 'Select';
};
const Crumb = styled(Heading4)``;
const CrumbLink = styled(Crumb)`
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
  cursor: pointer;
`;
const IconBox = styled(Box)`
  ${Heading4} {
    color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
  }
`;
const CrumbDetails = styled(Box)`
  font-size: 12px;
`;
const SelectedFlight = _ref => {
  let {
    from,
    to,
    legNum,
    onClick,
    selectedAlCode
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    alignSelf: "flex-start",
    pt: "2px",
    pr: 1
  }, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: selectedAlCode,
    size: "16px"
  })), /*#__PURE__*/React.createElement(CrumbLink, {
    flex: "0 0 auto",
    onClick: onClick,
    "data-legnum": legNum
  }, from, " \u2013 ", to), /*#__PURE__*/React.createElement(IconBox, {
    flex: "0 0 auto",
    px: SPACE.size12
  }, /*#__PURE__*/React.createElement(Heading4, null, "/")));
};
SelectedFlight.propTypes = {
  from: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  legNum: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  selectedAlCode: PropTypes.string.isRequired
};
const Breadcrumb = _ref2 => {
  let {
    legNum,
    legs,
    locations,
    onLegClick,
    selectedFlights
  } = _ref2;
  const crumbs = legs.map((leg, i) => {
    var _locations$find, _locations$find2;
    const from = (_locations$find = locations.find(l => (leg === null || leg === void 0 ? void 0 : leg.fromLocId) === (l === null || l === void 0 ? void 0 : l.locId))) === null || _locations$find === void 0 ? void 0 : _locations$find.cityCode;
    const to = (_locations$find2 = locations.find(l => (leg === null || leg === void 0 ? void 0 : leg.toLocId) === (l === null || l === void 0 ? void 0 : l.locId))) === null || _locations$find2 === void 0 ? void 0 : _locations$find2.cityCode;
    const date = parseDate(leg === null || leg === void 0 ? void 0 : leg.date);
    const dateStr = `${monthShortFormatter(date)} ${dayOfMonthFormatter(date)}`;
    const active = legNum === i + 1;
    const pastLeg = i < legNum - 1;
    const tripType = getTripType(legs);
    const isMultiCity = tripType === TRIP_TYPE.multiCity;
    const breadcrumbClickHandler = _ref3 => {
      let {
        target
      } = _ref3;
      const selectedLeg = parseInt(target.dataset.legnum, 10);
      onLegClick(selectedLeg);
    };
    if (active) {
      return /*#__PURE__*/React.createElement(Crumb, {
        flex: "0 0 auto",
        large: legNum === 1,
        key: `${from}${to}${dateStr}`
      }, /*#__PURE__*/React.createElement(Box, null, getCrumbTitle(legNum, tripType)), isMultiCity && /*#__PURE__*/React.createElement(CrumbDetails, {
        pt: 2
      }, from, " \u2013 ", to, ", ", dateStr));
    }
    if (pastLeg) {
      const selectedLeg = selectedFlights === null || selectedFlights === void 0 ? void 0 : selectedFlights[i];
      const selectedFrom = (selectedLeg === null || selectedLeg === void 0 ? void 0 : selectedLeg.fromCode) || from;
      const selectedTo = (selectedLeg === null || selectedLeg === void 0 ? void 0 : selectedLeg.toCode) || to;
      return !selectedLeg ? null : /*#__PURE__*/React.createElement(React.Fragment, {
        key: `${selectedFrom}${selectedTo}${dateStr}`
      }, /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, /*#__PURE__*/React.createElement(SelectedFlight, {
        selectedAlCode: selectedLeg.airlineCode,
        from: selectedFrom,
        to: selectedTo,
        legNum: i + 1,
        onClick: breadcrumbClickHandler
      })));
    }
    return null;
  });
  return /*#__PURE__*/React.createElement(Flex, {
    alignItems: "baseline"
  }, crumbs);
};
Breadcrumb.propTypes = {
  legNum: PropTypes.number.isRequired,
  legs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onLegClick: PropTypes.func.isRequired,
  selectedFlights: PropTypes.arrayOf(PropTypes.shape({}))
};
Breadcrumb.defaultProps = {
  selectedFlights: []
};

const FilterRegion = styled(Flex)`
  display: ${_ref => {
  let {
    hide
  } = _ref;
  return hide ? 'none' : 'flex';
}};
  flex: 1 1 auto;
  justify-content: flex-end;
  min-height: 40px;

  ${media.Large`
    padding-left: 20px;
    align-items: center;
  `}
`;
const AirlineCarouselWrapper = styled(Box)`
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    max-width: 1250px;
    overflow-x: auto;
  }
`;
const BreadcrumbFilterHeader = _ref2 => {
  let {
    breadcrumb,
    filters,
    hideFilters,
    compare,
    airlineCarousel
  } = _ref2;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flex, {
    alignItems: ['flex-start', 'flex-start', 'flex-start', 'center'],
    flexDirection: ['column', 'column', 'column', 'row'],
    justifyContent: [null, null, null, null, 'space-between'],
    pl: [2, 2, 2, 4],
    pr: [2, 2, 2, SPACE.size16],
    mb: [0, 0, 0, 3]
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "0 0 auto",
    pr: 3,
    pb: [3, 3, 3, 0]
  }, breadcrumb), /*#__PURE__*/React.createElement(FilterRegion, {
    hide: hideFilters
  }, filters.map((filter, idx) =>
  /*#__PURE__*/
  // eslint-disable-next-line react/no-array-index-key
  React.createElement(Box, {
    pr: [0, 0, 0, 0, 3],
    key: idx,
    flex: "0 1 auto"
  }, filter))), /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, /*#__PURE__*/React.createElement(Box, null, compare))), /*#__PURE__*/React.createElement(AirlineCarouselWrapper, {
    pl: [2, 2, 2, 4],
    pr: [2, 2, 2, SPACE.size24],
    mb: [0, 0, 0, 3],
    mt: [2, 2, 2, 0]
  }, airlineCarousel));
};
BreadcrumbFilterHeader.propTypes = {
  breadcrumb: PropTypes.node,
  filters: PropTypes.arrayOf(PropTypes.node).isRequired,
  compare: PropTypes.node.isRequired,
  hideFilters: PropTypes.bool.isRequired,
  airlineCarousel: PropTypes.node.isRequired
};
BreadcrumbFilterHeader.defaultProps = {
  breadcrumb: null
};

const TravelAlertNotice = _ref => {
  let {
    alertMessage
  } = _ref;
  if (!alertMessage || !(alertMessage !== null && alertMessage !== void 0 && alertMessage.alert)) {
    return null;
  }
  const createMarkup = () => {
    return {
      __html: alertMessage.message
    };
  };
  const Message = () => {
    return /*#__PURE__*/React.createElement("span", {
      dangerouslySetInnerHTML: createMarkup()
    });
  };
  return /*#__PURE__*/React.createElement(Notice, {
    kind: "warning",
    mx: [2, 2, 2, 4],
    mb: 3,
    compact: true,
    closable: true
  }, /*#__PURE__*/React.createElement(Message, null));
};
TravelAlertNotice.propTypes = {
  alertMessage: PropTypes.shape({
    alert: PropTypes.bool,
    message: PropTypes.string
  })
};
TravelAlertNotice.defaultProps = {
  alertMessage: null
};

const AirlineName = styled(Box)`
  font-size: 14px;
  color: ${getTokenValue(THEME_TOKENS.flightOptions.color.airlineCardAirlineName)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const AlCard = styled(Box)`
  border-width: 1px;
  border-style: solid;
  border-color: ${_ref => {
  let {
    active
  } = _ref;
  return active ? getTokenValue(THEME_TOKENS.base.color.action) : getThemeProp('global.border--color');
}};
  border-radius: ${SPACE.size4};
  width: 108px;
  background: white;

  transition-timing-function: ease-in-out;
  transition-duration: 0.15s;
  transition-property: border-color, background-color;

  ${_ref2 => {
  let {
    active
  } = _ref2;
  return active && css`
      background-color: ${getTokenValue(THEME_TOKENS.textInput.color.backgroundHover)};
      box-shadow: ${getTokenValue(THEME_TOKENS.textInput.shadow.focus)};
    `;
}};

  ${_ref3 => {
  let {
    active
  } = _ref3;
  return !active && css`
      @media (hover: hover) {
        &:hover {
          background-color: ${getTokenValue(THEME_TOKENS.textInput.color.backgroundHover)};
          cursor: pointer;
        }
      }
    `;
}};
`;
const FareAmount = styled(Box)`
  font-weight: 600;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const AirlineNameRegion = styled(Flex)`
  /* height: 14px; */
`;
const AirlineCard = _ref4 => {
  let {
    name,
    airlineCode,
    active,
    fare,
    preferred,
    onClick
  } = _ref4;
  return /*#__PURE__*/React.createElement(AlCard, {
    active: active,
    p: 2,
    onClick: onClick
  }, /*#__PURE__*/React.createElement(AirlineNameRegion, null, /*#__PURE__*/React.createElement(AirlineName, null, name), preferred && /*#__PURE__*/React.createElement(Box, {
    pl: 1
  }, /*#__PURE__*/React.createElement(PreferredMark, null))), /*#__PURE__*/React.createElement(Flex, {
    pt: 1,
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(FareAmount, null, formatFare(fare)), /*#__PURE__*/React.createElement(AirlineLogo, {
    size: "16px",
    alCode: airlineCode
  })));
};
AirlineCard.propTypes = {
  name: PropTypes.string.isRequired,
  airlineCode: PropTypes.string.isRequired,
  active: PropTypes.bool,
  fare: PropTypes.number.isRequired,
  preferred: PropTypes.bool,
  onClick: PropTypes.func.isRequired
};
AirlineCard.defaultProps = {
  active: false,
  preferred: false
};

const HorizontalScrollContainer = styled(Box)`
  display: grid;
`;
const AirlineCarousel = _ref => {
  let {
    airlineList
  } = _ref;
  return /*#__PURE__*/React.createElement(HorizontalScrollContainer, null, /*#__PURE__*/React.createElement(HorizontalScroll, null, airlineList.map(_ref2 => {
    let {
      name,
      airlineCode,
      active,
      fare,
      preferred,
      onClick
    } = _ref2;
    return /*#__PURE__*/ /* the padding 1px is to account for the active state which adds a 1px border */React.createElement(Box, {
      key: airlineCode,
      pl: "1px",
      pb: "1px",
      pt: "1px"
    }, /*#__PURE__*/React.createElement(AirlineCard, {
      onClick: onClick,
      name: name,
      airlineCode: airlineCode,
      active: active,
      fare: fare,
      preferred: preferred
    }));
  })));
};
AirlineCarousel.propTypes = {
  airlineList: PropTypes.arrayOf(PropTypes.shape({
    /**
     * Name of airline.
     */
    name: PropTypes.string.isRequired,
    airlineCode: PropTypes.string.isRequired,
    active: PropTypes.bool,
    fare: PropTypes.number.isRequired,
    preferred: PropTypes.bool,
    onClick: PropTypes.func.isRequired
  })).isRequired
};

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

const EditSerachRegion = styled(animated.div)`
  height: 0;
`;
const InlineSearchRegion = styled(Box)`
  border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusMedium)};
  background: #fff;
`;
const EditSearchButton = styled(Button)`
  box-shadow: none;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;

  ${ButtonTextStyled} {
    padding-top: 7px;
    padding-bottom: 7px;
  }
`;
const EditSearchNewWindowButton = styled(Button)`
  box-shadow: none;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  ${ButtonTextStyled} {
    padding-top: 7px;
    padding-bottom: 7px;
    padding-right: 11px;
  }
`;
const numpaxToPassengerData = numpax => {
  const [adults, seniors, children,, infants] = numpax.split('').map(val => Number(val));
  return {
    adults,
    seniors,
    children,
    infants
  };
};
const deriveLocationObject = struct => ({
  Display: struct === null || struct === void 0 ? void 0 : struct.shortDisplay,
  KeyType: '',
  LocID: struct === null || struct === void 0 ? void 0 : struct.locId
});
const CitySearch = _ref => {
  let {
    customLocations = [],
    onSelect,
    ...rest
  } = _ref;
  const [locations, setLocations] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const normalizedCustomLocs = useMemo(() => customLocations.map(reshapeCustomLocation), [customLocations]);
  useEffect(() => {
    const {
      findLocations,
      cancelFindLocations
    } = cancellableFindLocations();
    if (inputValue) {
      const setFetchingTrue = new Promise(res => res(setIsFetching(true)));
      setFetchingTrue.then(() => {
        findLocations(inputValue, {
          mode: 'Flights'
        }).then(newLocations => {
          const filteredItems = normalizedCustomLocs.filter(item => {
            return !inputValue || item.Display.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase());
          }).concat(newLocations);
          setLocations(filteredItems);
          setIsFetching(false);
        }).catch(err => {
          if (err.message.indexOf('CANCELLED_REQUEST') === 0) {
            // intentional passthrough for cancelled findLocations call
            return;
          }
          console.error(err);
        });
      });
    } else {
      setLocations([]);
    }
    return () => cancelFindLocations(`cancelling call for ${inputValue}`);
  }, [inputValue]);
  return /*#__PURE__*/React.createElement(CityAutocomplete, _extends({}, rest, {
    showLocIcon: true,
    inputValue: inputValue,
    items: locations,
    customLocations: customLocations,
    onInputChange: setInputValue,
    onSelectionChange: onSelect,
    suggestionsCount: 8,
    isFetching: isFetching
  }));
};
CitySearch.propTypes = {
  findLocations: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  customLocations: PropTypes.arrayOf(PropTypes.shape({}))
};
const getDateSummaryRoundTrip = (from, to) => {
  if (from && to) {
    return `${monthShortFormatter(from)} ${dayOfMonthFormatter(from)} — ${monthShortFormatter(to)} ${dayOfMonthFormatter(to)}`;
  }
  return 'Select a Date Range';
};
const getDateSummaryOneWay = date => {
  if (date) {
    return `${monthShortFormatter(date)} ${dayOfMonthFormatter(date)}`;
  }
  return 'Select a Date';
};
const PASSENGER_ERROR_MSG_MAX = 'Requests for more than 9 passengers are not supported.';
const PASSENGER_ERROR_MSG_MIN = 'You must specify at least one passenger.';
const DATE_RANGE_ERROR = 'You must provide travel dates.';
const EditSearch = _ref2 => {
  let {
    shown = false,
    locations,
    numpax = '00000',
    cabinRequested = 'Y',
    findLocations,
    customLocations = [],
    fromDate,
    toDate,
    tripType,
    onApply,
    getSearchUrl
  } = _ref2;
  // Most of these fields are self validating. Probably only need `passengers`
  const [errors, setErrors] = useState({
    fromLoc: '',
    toLoc: '',
    dateRange: '',
    passengers: ''
  });
  const [fromLoc, setFromLoc] = useState(deriveLocationObject(locations[0]));
  const [toLoc, setToLoc] = useState(deriveLocationObject(locations === null || locations === void 0 ? void 0 : locations[(locations === null || locations === void 0 ? void 0 : locations.length) - 1]));
  const [passengerData, setPassengerData] = useState({
    ...numpaxToPassengerData(numpax),
    cabin: cabinRequested
  });
  const [from, setFrom] = useState(fromDate);
  const [to, setTo] = useState(toDate);
  const [tripTypeSelection, setTripTypeSelection] = useState(tripType);
  const [isOpenAfterAnimation, setIsOpenAfterAnimation] = useState(false);
  const tripTypes = [{
    label: 'Round Trip',
    value: TRIP_TYPE.roundTrip
  }, {
    label: 'One Way',
    value: TRIP_TYPE.oneWay
  }];
  const onSetTripType = e => setTripTypeSelection(e.target.value);
  const reshapedCustomLocations = customLocations.map(cl => reshapeCustomLocation(cl));
  const getFirstError = () => {
    const flatErrors = Object.keys(errors).map(r => errors[r]).filter(el => {
      return el !== '';
    });
    if (flatErrors.length) return flatErrors === null || flatErrors === void 0 ? void 0 : flatErrors[0];
    return null;
  };
  const dateDisplayRoundTrip = getDateSummaryRoundTrip(from, to);
  const dateDisplayOneWay = getDateSummaryOneWay(from);
  const generateLocChangeHandler = setter => newLoc => {
    if (newLoc) {
      return setter({
        Display: newLoc.Display,
        KeyType: newLoc.KeyType,
        LocID: newLoc.LocID
      });
    }
    return null;
  };
  const travelersChangeHandler = incomingData => {
    const totalPassengers = Object.values(incomingData).reduce((acc, x) => typeof x === 'number' ? acc + x : acc, 0);
    setPassengerData(incomingData);
    if (totalPassengers > 9) {
      setErrors({
        ...errors,
        passengers: PASSENGER_ERROR_MSG_MAX
      });
    } else if (totalPassengers < 1) {
      setErrors({
        ...errors,
        passengers: PASSENGER_ERROR_MSG_MIN
      });
    } else {
      setErrors({
        ...errors,
        passengers: ''
      });
    }
  };
  const validateDates = () => {
    if (to === null) {
      setErrors({
        ...errors,
        dateRange: DATE_RANGE_ERROR
      });
    } else {
      setErrors({
        ...errors,
        dateRange: ''
      });
    }
  };
  const openNewWindow = () => {
    if (getFirstError() === null) {
      const newUrl = getSearchUrl({
        fromLoc,
        toLoc,
        fromDate: from,
        toDate: to,
        cabinRequested: passengerData.cabin,
        numpax: `${passengerData.adults}${passengerData.seniors}${passengerData.children}0${passengerData.infants}`,
        tripType: tripTypeSelection
      });
      window.open(newUrl);
    }
  };
  const onApplyHandler = () => {
    if (getFirstError() === null) {
      onApply({
        fromLoc,
        toLoc,
        fromDate: from,
        toDate: to,
        cabinRequested: passengerData.cabin,
        numpax: `${passengerData.adults}${passengerData.seniors}${passengerData.children}0${passengerData.infants}`,
        tripType: tripTypeSelection
      });
    }
  };
  useEffect(() => {
    setFromLoc(deriveLocationObject(locations[0]));
    setToLoc(deriveLocationObject(locations === null || locations === void 0 ? void 0 : locations[(locations === null || locations === void 0 ? void 0 : locations.length) - 1]));
  }, [locations]);
  useEffect(() => {
    validateDates();
  }, [from, to]);
  const compareProps = useSpring({
    opacity: shown ? '100%' : '0%',
    height: shown ? '108px' : '0px',
    overflow: shown && isOpenAfterAnimation ? 'visible' : 'hidden',
    onRest: () => {
      setIsOpenAfterAnimation(shown);
    }
  });
  return /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, /*#__PURE__*/React.createElement(EditSerachRegion, {
    style: compareProps
  }, (shown || isOpenAfterAnimation) && /*#__PURE__*/React.createElement(Box, {
    pt: 3
  }, /*#__PURE__*/React.createElement(InlineSearchRegion, {
    p: 3
  }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Box, {
    pr: 2,
    flex: "1 0 128px"
  }, /*#__PURE__*/React.createElement(DropdownSelect, {
    value: tripTypeSelection,
    onChange: onSetTripType,
    options: tripTypes
  })), /*#__PURE__*/React.createElement(Box, {
    pr: 2
  }, /*#__PURE__*/React.createElement(CitySearch, {
    placeholder: "From",
    selectedItem: fromLoc,
    onSelect: generateLocChangeHandler(setFromLoc),
    findLocations: findLocations,
    customLocations: reshapedCustomLocations
  })), /*#__PURE__*/React.createElement(Box, {
    pr: 2
  }, /*#__PURE__*/React.createElement(CitySearch, {
    placeholder: "To",
    selectedItem: toLoc,
    onSelect: generateLocChangeHandler(setToLoc),
    findLocations: findLocations,
    customLocations: reshapedCustomLocations
  })), /*#__PURE__*/React.createElement(Box, {
    pr: 2,
    width: "160px"
  }, tripTypeSelection === TRIP_TYPE.oneWay ? /*#__PURE__*/React.createElement(DatePicker, {
    date: from,
    onSelectDate: setFrom,
    disableBeforeDate: new Date(Date.now() + 1000 * 60 * 60 * 24),
    disableAfterDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 364)
  }, /*#__PURE__*/React.createElement(TextInput, {
    placeholder: "Dates",
    value: dateDisplayOneWay,
    onChange: () => {}
  })) : /*#__PURE__*/React.createElement(DateRangePicker, {
    fromDate: from,
    toDate: to,
    onSelectFromDate: setFrom,
    onSelectToDate: setTo,
    disableBeforeDate: new Date(Date.now() + 1000 * 60 * 60 * 24),
    disableAfterDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 364)
  }, /*#__PURE__*/React.createElement(TextInput, {
    placeholder: "Dates",
    value: dateDisplayRoundTrip,
    kind: errors.dateRange ? 'danger' : null,
    onChange: () => {}
  }))), /*#__PURE__*/React.createElement(Box, {
    flex: "1 0 auto",
    pr: 2
  }, /*#__PURE__*/React.createElement(PassengersFilterChip, {
    data: passengerData,
    onChange: travelersChangeHandler
  })), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "flex-start"
  }, /*#__PURE__*/React.createElement(EditSearchButton, {
    size: "inlineForm",
    disabled: getFirstError() !== null,
    text: "Search",
    onClick: onApplyHandler
  }), /*#__PURE__*/React.createElement(EditSearchNewWindowButton, {
    size: "inlineForm",
    disabled: getFirstError() !== null,
    title: "Open in New Window",
    onClick: openNewWindow,
    bordered: true,
    icon: /*#__PURE__*/React.createElement(IconOpenNew, null)
  }))), getFirstError() && /*#__PURE__*/React.createElement(ErrorMessage, {
    mb: 0,
    message: getFirstError()
  })))));
};
EditSearch.propTypes = {
  shown: PropTypes.bool.isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  customLocations: PropTypes.arrayOf(PropTypes.shape({})),
  numpax: PropTypes.string.isRequired,
  cabinRequested: PropTypes.string.isRequired,
  findLocations: PropTypes.func.isRequired,
  fromDate: PropTypes.instanceOf(Date).isRequired,
  toDate: PropTypes.instanceOf(Date).isRequired,
  onApply: PropTypes.func.isRequired,
  getSearchUrl: PropTypes.func.isRequired,
  tripType: PropTypes.string.isRequired
};

/* eslint-disable react/require-default-props */
const FareChangeNotice = _ref => {
  let {
    expectedFare,
    flights = []
  } = _ref;
  const lowestFare = getLowestFare(flights);
  const priceDifference = (Math === null || Math === void 0 ? void 0 : Math.ceil(lowestFare)) - expectedFare;
  if (priceDifference >= 10) {
    return /*#__PURE__*/React.createElement(Notice, {
      kind: "warning",
      mx: [2, 2, 2, 4],
      mb: 3,
      compact: true,
      closable: true
    }, "Some fares in this market have changed. See below for current pricing.");
  }
  if (priceDifference >= 1) {
    return /*#__PURE__*/React.createElement(Notice, {
      mx: [2, 2, 2, 4],
      mb: 3,
      compact: true,
      closable: true
    }, "The lowest fare in this market has changed slightly. See below for current pricing.");
  }
  if (priceDifference <= -10) {
    return /*#__PURE__*/React.createElement(Notice, {
      kind: "success",
      mx: [2, 2, 2, 4],
      mb: 3,
      compact: true,
      closable: true
    }, "Good News! Fares have gone down. See below for current pricing.");
  }
  if (priceDifference <= -1) {
    return /*#__PURE__*/React.createElement(Notice, {
      kind: "success",
      mx: [2, 2, 2, 4],
      mb: 3,
      compact: true,
      closable: true
    }, "Fares have gone down slightly. See below for current pricing.");
  }
  return null;
};
FareChangeNotice.propTypes = {
  flights: PropTypes.arrayOf(PropTypes.shape({})),
  expectedFare: PropTypes.string.isRequired
};

const MessageBody = styled.div`
  max-width: 490px;
  font-size: 16px;
  line-height: 1.3;

  img {
    display: block;
    width: 100%;
    margin: 0 auto 24px;
  }

  p {
    padding-top: 16px;
  }
`;
const JSXModal = _ref => {
  let {
    onContinue = () => {},
    onClose = () => {}
  } = _ref;
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Flight operated by JSX"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(MessageBody, null, /*#__PURE__*/React.createElement("img", {
    src: "https://cdn.amtrav.com/airline-exterior/XE-1.jpg",
    alt: "Exterior of JSX aircraft."
  }), /*#__PURE__*/React.createElement(Box, {
    my: "12px"
  }, /*#__PURE__*/React.createElement("b", null, "JSX flights arrive and depart from different terminals than other airlines."), /*#__PURE__*/React.createElement(Box, {
    mt: "12px"
  }, /*#__PURE__*/React.createElement("a", {
    href: "https://www.jsx.com/destinations-wherewefly",
    target: "_blank",
    rel: "noreferrer"
  }, "Check to see where JSX flights depart and arrive"), ".")), "JSX is a unique airline that offers fast and convenient scheduled flights out of private terminals at U.S. airports. JSX flies 30-seat jets with comfortable seats and free drinks, snacks, bags, and WiFi. But as cool as it is to avoid the crowds and hassles of public airports, there is important info you should be aware of when booking JSX, like the location of their terminals (they\u2019re usually not attached to commercial terminals) and the availability of parking, rental cars, and ground transportation.")), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "center",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: onContinue,
    text: "Continue with JSX"
  }))));
};
JSXModal.propTypes = {
  onContinue: PropTypes.func,
  onClose: PropTypes.func
};

export { AirlineCarousel, Breadcrumb, BreadcrumbFilterHeader, CmsContentModal, CompareShelfStyles, EditSearch, EditSearchNewWindowButton as EditSearchNewWindowSButtonStyles, FareChangeNotice, FlightDetails, FlightOptions, InteractiveResponse, JSXModal, PageHeader, TRIP_TYPE, TravelAlertNotice, UpgradeFare, generateFareKey, generateResultId, getBrandColumnMap, getLowestFare, getTripDateSummary, getTripDateSummaryObj, getTripRouteSummary, getTripType, processFlightsData };
//# sourceMappingURL=index.esm.js.map
